import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { api, complianceAPI } from '@/lib/api';
import { toast } from 'sonner';
import CryptoPayment from './CryptoPayment';
import MintSourcePicker from '../components/MintSourcePicker';
import ProviderStatusPanel from '../components/ProviderStatusPanel';
import SettlementSummaryCard from '../components/SettlementSummaryCard';
import CyclosLayer from '../components/CyclosLayer';
import ItauLayer from '../components/ItauLayer';
import PaystackLayer from '../components/PaystackLayer';
import RelmLayer from '../components/RelmLayer';
import EquityLayer from '../components/EquityLayer';
import DoronPayLayer from '../components/DoronPayLayer';
import LayerTransfer from '../components/LayerTransfer';
import TapPayouts from '../components/TapPayouts';
import EmailHealthBadge from '../components/EmailHealthBadge';
import ExternalStpSettlement from '../components/ExternalStpSettlement';
import { useAdminNotifications } from '@/hooks/useWebSocket';
import { Users, DollarSign, TrendingUp, Activity, UserCheck, UserX, Loader2, Bell, ArrowDownLeft, CheckCheck, Trash2, ArrowUpRight, Bitcoin, Building2, Shield, FileText, Eye, Ban, Unlock, Clock, AlertTriangle, Plus, UserCog, Pause, Play, Edit2, X, Image, Wifi, WifiOff, Mail, ExternalLink, RefreshCw, CheckCircle, AlertCircle, Server, Smartphone, Phone, ArrowRight, ArrowRightLeft } from 'lucide-react';

const AuthDocPreview = ({ path, label, contentType, size = 'large', testId }) => {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let objUrl = null;
    let cancelled = false;
    api.get(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objUrl = URL.createObjectURL(res.data);
        setUrl(objUrl);
        setErr(null);
      })
      .catch((e) => {
        if (!cancelled) setErr(e.response?.status === 404 ? 'File not available — request re-upload' : 'Failed to load document');
      });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);
  const isImage = contentType?.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(label || '');
  const isPdf = contentType === 'application/pdf' || /\.pdf$/i.test(label || '');
  const boxCls = size === 'thumb' ? 'w-16 h-16 shrink-0' : 'w-full h-48';
  if (err) {
    return (
      <div className={`${boxCls} bg-zinc-900 rounded-lg flex flex-col items-center justify-center text-center p-2`} data-testid={testId ? `${testId}-missing` : undefined} title={err}>
        <AlertCircle className="w-5 h-5 text-amber-400" />
        {size !== 'thumb' && <span className="text-xs text-amber-300 mt-1">{err}</span>}
      </div>
    );
  }
  if (!url) return <div className={`${boxCls} bg-zinc-900 rounded-lg animate-pulse`} />;
  if (isImage) {
    return (
      <>
        <img src={url} alt={label} className={`${boxCls} object-contain bg-zinc-900 rounded-lg cursor-zoom-in`} onClick={() => setOpen(true)} data-testid={testId} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
            <img src={url} alt={label} className="w-full max-h-[75vh] object-contain bg-zinc-900 rounded-lg" data-testid={testId ? `${testId}-zoom` : undefined} />
          </DialogContent>
        </Dialog>
      </>
    );
  }
  return (
    <div className={`${boxCls} bg-zinc-900 rounded-lg flex ${size === 'thumb' ? '' : 'flex-col gap-2'} items-center justify-center ${size === 'thumb' ? 'cursor-pointer' : ''}`} onClick={size === 'thumb' ? () => window.open(url, '_blank') : undefined} title={size === 'thumb' ? `Open ${label}` : undefined}>
      <FileText className="w-6 h-6 text-muted-foreground" />
      {size !== 'thumb' && (
        <Button size="sm" variant="outline" onClick={() => window.open(url, '_blank')} data-testid={testId ? `${testId}-open` : undefined}>
          {isPdf ? 'Open PDF' : 'Open file'}
        </Button>
      )}
    </div>
  );
};

const ADMIN_ROLES = [
  { value: 'super_admin', label: 'Super Admin', description: 'Full system access' },
  { value: 'ceo', label: 'CEO', description: 'Full access with reports' },
  { value: 'cco', label: 'Chief Compliance Officer', description: 'KYC review and approval' },
  { value: 'cfo', label: 'Chief Financial Officer', description: 'Financial oversight' },
  { value: 'compliance_officer', label: 'Compliance Officer', description: 'First-level KYC review' },
  { value: 'controller', label: 'Controller', description: 'Financial transactions' },
  { value: 'analyst', label: 'Analyst', description: 'View only access' },
];

const PERMISSIONS = [
  { value: 'see_only', label: 'View Only' },
  { value: 'manage', label: 'Manage' },
  { value: 'approve', label: 'Approve' },
  { value: 'report_to_ceo', label: 'Report to CEO' },
];

export default function Admin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [itauStats, setItauStats] = useState(null);

  useEffect(() => {
    api.get('/itau/stats').then((r) => setItauStats(r.data)).catch(() => {});
  }, []);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [recentActivity, setRecentActivity] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [incomingTransactions, setIncomingTransactions] = useState([]);
  const [totalsByMethod, setTotalsByMethod] = useState({});
  const [withdrawals, setWithdrawals] = useState([]);
  const [kycApplications, setKycApplications] = useState([]);
  const [kycStats, setKycStats] = useState(null);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMemberCred, setNewMemberCred] = useState(null);
  const [kycDocuments, setKycDocuments] = useState([]);
  const [selectedKycDoc, setSelectedKycDoc] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'users');

  // ALT1 state
  const [alt1Health, setAlt1Health] = useState(null);
  const [alt1Payments, setAlt1Payments] = useState([]);
  const [alt1Webhooks, setAlt1Webhooks] = useState([]);
  const [alt1Loading, setAlt1Loading] = useState(false);
  const [alt1Settling, setAlt1Settling] = useState(false);
  const [alt1Settlements, setAlt1Settlements] = useState([]);
  const [alt1TrialForm, setAlt1TrialForm] = useState({
    mint_account_id: '',
    amount: 49000000,
    recipient_name: 'ROBERTO DANIEL BRUSCO',
    recipient_account: '/0150506111000117999240',
    memo: '',
  });

  // Mandiri state
  const [mandiriHealth, setMandiriHealth] = useState(null);
  const [mandiriLoading, setMandiriLoading] = useState(false);
  const [mandiriTxns, setMandiriTxns] = useState([]);
  const [mandiriTestResults, setMandiriTestResults] = useState(null);
  const [mandiriTesting, setMandiriTesting] = useState(false);
  const [mandiriQuoteForm, setMandiriQuoteForm] = useState({
    source_account: '2600002626591',
    destination_amount: '10000055',
    destination_currency: 'SAR',
    beneficiary_bank_code: 'BMRIIDJAXXX',
    beneficiary_account: '2600002390503',
  });
  const [mandiriTransferForm, setMandiriTransferForm] = useState({
    mint_account_id: '',
    quote_id: '',
    amount_value: '',
    amount_currency: 'USD',
    source_account: '2600002626591',
    beneficiary_account: '',
    beneficiary_name: '',
    beneficiary_country: 'MY',
    beneficiary_email: '',
    remark: '',
  });

  // SOVEIN state
  const [soveinHealth, setSoveinHealth] = useState(null);
  const [soveinLoading, setSoveinLoading] = useState(false);
  const [soveinTxns, setSoveinTxns] = useState([]);
  const [soveinTestResults, setSoveinTestResults] = useState(null);
  const [soveinTesting, setSoveinTesting] = useState(false);
  const [soveinBiometricForm, setSoveinBiometricForm] = useState({
    unit_id: 'L360-GH-300',
    heart_rate: 165,
    oxygen_sat: 98,
    thermal_output: '37.2C',
    sovereign_id: 'SPRING-SG-7-INDUSTRY',
    security_level: 'BLOCK-FUND-TIER-1',
  });
  const [soveinDeliveryForm, setSoveinDeliveryForm] = useState({
    unit_id: 'L360-GH-300',
    activation_status: 'PENDING',
    delivery_address: '',
    client_device_id: '',
  });

  // M-Pesa state
  const [mpesaHealth, setMpesaHealth] = useState(null);
  const [mpesaLoading, setMpesaLoading] = useState(false);
  const [mpesaTxns, setMpesaTxns] = useState([]);
  const [mpesaTestResults, setMpesaTestResults] = useState(null);
  const [mpesaTesting, setMpesaTesting] = useState(false);
  const [mpesaSending, setMpesaSending] = useState(false);
  const [mpesaReconData, setMpesaReconData] = useState(null);
  const [mpesaReconLoading, setMpesaReconLoading] = useState(false);
  const [mpesaSendForm, setMpesaSendForm] = useState({
    phone_number: '',
    amount: '',
    direction: 'send',
    remarks: '',
  });
  const [mpesaValidatePhone, setMpesaValidatePhone] = useState('');
  const [mpesaStatusId, setMpesaStatusId] = useState('');
  const [mpesaReversalForm, setMpesaReversalForm] = useState({
    transaction_id: '',
    amount: '',
    remarks: 'Reversal',
  });

  // Receipt delivery state
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryTx, setDeliveryTx] = useState(null);
  const [deliveryChannel, setDeliveryChannel] = useState('email');
  const [deliveryTo, setDeliveryTo] = useState('');
  const [deliverySending, setDeliverySending] = useState(false);

  // Settlement Overview state
  const [settlementOverview, setSettlementOverview] = useState(null);
  const [settlementLoading, setSettlementLoading] = useState(false);

  // TAP BANK state
  const [tapTransactions, setTapTransactions] = useState([]);
  const [tapHealth, setTapHealth] = useState(null);
  const [tapLoading, setTapLoading] = useState(false);
  const [tapSettling, setTapSettling] = useState(false);
  const [tapSettleForm, setTapSettleForm] = useState({
    amount: '', currency: 'USD', recipient_name: '', recipient_account: '', description: '', email: '',
    phone_country: '966', phone_number: '500000000', record_only: false,
  });
  const [tapTraceResult, setTapTraceResult] = useState(null);
  const [tapTracing, setTapTracing] = useState(false);
  const [tapHeaderTraceId, setTapHeaderTraceId] = useState('');
  const [tapQuickTesting, setTapQuickTesting] = useState(false);

  // FIN.COM state
  const [fincomTransactions, setFincomTransactions] = useState([]);
  const [fincomHealth, setFincomHealth] = useState(null);
  const [fincomLoading, setFincomLoading] = useState(false);
  const [fincomSettling, setFincomSettling] = useState(false);
  const [fincomSettleForm, setFincomSettleForm] = useState({
    mint_account_id: '',
    amount: '', currency: 'USD', recipient_name: '', recipient_account: '', beneficiary_id: '', description: '', record_only: false,
  });
  const [fincomTraceResult, setFincomTraceResult] = useState(null);
  const [fincomTracing, setFincomTracing] = useState(false);

  // TKM state
  const [tkmTransactions, setTkmTransactions] = useState([]);
  const [tkmHealth, setTkmHealth] = useState(null);
  const [tkmAccounts, setTkmAccounts] = useState([]);
  const [tkmOutboundIp, setTkmOutboundIp] = useState(null);
  const [tkmWebhookConfig, setTkmWebhookConfig] = useState(null);
  const [tkmLoading, setTkmLoading] = useState(false);
  const [tkmSettling, setTkmSettling] = useState(false);
  const [tkmSettleForm, setTkmSettleForm] = useState({
    mint_account_id: '',                       // ← REQUIRED — picks one of 3 MINT accounts
    amount: '', currency: 'USD',
    beneficiary_name: '', beneficiary_iban: '',
    description: '',
    purpose: 'TKM partner settlement',
    mode: 'debit_only',                        // debit_only | debit_and_notify | tkm_internal | payout | credit-inbound | record
    inbound_path: 'M2',
    source_channel: 'partner_api_m2_live',
    tkm_from_account_id: '', tkm_to_account_id: '',
  });
  const [tkmTraceResult, setTkmTraceResult] = useState(null);

  // DB Admin state (super_admin only)
  const [dbCollections, setDbCollections] = useState([]);
  const [dbActiveCol, setDbActiveCol] = useState(null);
  const [dbDocs, setDbDocs] = useState({ docs: [], total: 0 });
  const [dbSearch, setDbSearch] = useState('');
  const [dbLoading, setDbLoading] = useState(false);
  const [dbRoles, setDbRoles] = useState([]);
  const [dbAuditLog, setDbAuditLog] = useState([]);
  const [dbForbidden, setDbForbidden] = useState(false);

  // SWIFT state
  const [swiftBeneficiaries, setSwiftBeneficiaries] = useState([]);
  const [swiftTransfers, setSwiftTransfers] = useState([]);
  const [swiftSettings, setSwiftSettings] = useState({
    auto_wire_tap_captures: false,
    ordering_customer_name: 'EzyTransfer Ltd.',
    ordering_customer_address: '',
    ordering_customer_account: '',
    sender_bic: '',
    charges_bearer: 'OUR',
    provider: 'manual',
    fincom_customer_id: '',
  });
  const [swiftLoading, setSwiftLoading] = useState(false);
  const [swiftBenForm, setSwiftBenForm] = useState({
    label: '', beneficiary_name: '', beneficiary_address: '',
    beneficiary_bic: '', beneficiary_iban: '', beneficiary_account: '',
    intermediary_bic: '', currency: 'USD', country_code: '', is_default: false,
  });
  const [swiftTransferForm, setSwiftTransferForm] = useState({
    mint_account_id: '',
    amount: '', currency: 'USD', remittance_info: '',
    beneficiary_id: '', use_default: true,
    beneficiary_name: '', beneficiary_address: '',
    beneficiary_bic: '', beneficiary_iban: '', beneficiary_account: '',
    intermediary_bic: '', charges_bearer: '',
  });
  const [swiftSendModal, setSwiftSendModal] = useState({ open: false, source: null });

  // SOVEIN settle state
  const [soveinSettling, setSoveinSettling] = useState(false);
  const [soveinTraceRef, setSoveinTraceRef] = useState('');
  const [soveinTracing, setSoveinTracing] = useState(false);
  const [soveinTraceResult, setSoveinTraceResult] = useState(null);
  // USDMint Fund Locator & Transfer state
  const [usdmintLocateTx, setUsdmintLocateTx] = useState('');
  const [usdmintLocating, setUsdmintLocating] = useState(false);
  const [usdmintFlowResult, setUsdmintFlowResult] = useState(null);
  const [usdmintPaymentTypes, setUsdmintPaymentTypes] = useState([]);
  const [usdmintTransferForm, setUsdmintTransferForm] = useState({
    payment_type_id: '',
    amount: '',
    description: '',
    subject: 'self',
  });
  const [usdmintTransferring, setUsdmintTransferring] = useState(false);
  // MINT 3-account statement state
  const [mintAccounts, setMintAccounts] = useState([]);
  const [mintStatement, setMintStatement] = useState(null);
  const [mintStatementLoading, setMintStatementLoading] = useState(false);
  const [mintActiveAccountId, setMintActiveAccountId] = useState('');
  // Unified Settlement Trace state
  const [settlementTrace, setSettlementTrace] = useState(null);
  const [settlementTraceLoading, setSettlementTraceLoading] = useState(false);
  const [settlementTraceFilter, setSettlementTraceFilter] = useState('ALL');
  const [settlementTraceIncludeTest, setSettlementTraceIncludeTest] = useState(false);
  const [transactionsView, setTransactionsView] = useState('trace'); // 'trace' | 'bridge'
  const [disabledProviders, setDisabledProviders] = useState([]); // e.g. ['alt1','sovein']
  const [soveinSettleForm, setSoveinSettleForm] = useState({
    mint_account_id: '',
    amount: '',
    currency: 'USD',
    recipient_name: '',
    recipient_account: '',
    unit_id: 'L360-GH-300',
    memo: '',
  });
  // ALT1 settle form (was implicit before — now requires MINT source for provenance)
  const [alt1SettleForm, setAlt1SettleForm] = useState({
    mint_account_id: '',
    amount: '',
    recipient_name: '',
    recipient_account: '',
    memo: '',
  });
  
  // Real-time WebSocket notifications
  const { 
    isConnected: wsConnected, 
    notifications: wsNotifications,
    unreadCount: wsUnreadCount 
  } = useAdminNotifications();

  // Reload data when new KYC notification comes in
  useEffect(() => {
    if (wsNotifications.length > 0 && wsNotifications[0].type === 'kyc') {
      // Refresh KYC data when new application arrives
      loadKYCData();
    }
  }, [wsNotifications]);

  const loadKYCData = async () => {
    try {
      const [kycRes, kycStatsRes] = await Promise.all([
        complianceAPI.getPendingKYC({}).catch(() => ({ data: [] })),
        complianceAPI.getKYCStats().catch(() => ({ data: null }))
      ]);
      setKycApplications(kycRes.data || []);
      setKycStats(kycStatsRes.data);
    } catch (error) {
      console.error('Failed to load KYC data:', error);
    }
  };

  useEffect(() => {
    if (mintActiveAccountId) {
      loadMintStatement(mintActiveAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mintActiveAccountId]);

  useEffect(() => {
    loadAdminData();
    // Eagerly load Settlement Trace + MINT accounts so the Transactions and Bridge tabs are ready
    loadSettlementTrace();
    loadMintAccounts();
    // Load provider feature flags (which settlement layers are temporarily disconnected)
    api.get('/provider-flags')
      .then(res => setDisabledProviders((res.data?.disabled_providers || []).map(p => String(p).toLowerCase())))
      .catch(() => setDisabledProviders([]));
  }, []);

  const loadAdminData = async () => {
    try {
      const [statsRes, usersRes, transactionsRes, activityRes, notificationsRes, incomingRes, withdrawalsRes, kycRes, kycStatsRes, teamRes, meRes, kycDocsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/transactions'),
        api.get('/admin/recent-activity'),
        api.get('/admin/notifications'),
        api.get('/admin/incoming/transactions'),
        api.get('/admin/withdrawals'),
        complianceAPI.getPendingKYC({}).catch(() => ({ data: [] })),
        complianceAPI.getKYCStats().catch(() => ({ data: null })),
        complianceAPI.getTeam().catch(() => ({ data: [] })),
        api.get('/auth/me').catch(() => ({ data: null })),
        complianceAPI.getKycDocuments().catch(() => ({ data: [] })),
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTransactions(transactionsRes.data);
      setRecentActivity(activityRes.data);
      setNotifications(notificationsRes.data.notifications || []);
      setUnreadCount(notificationsRes.data.unread_count || 0);
      setIncomingTransactions(incomingRes.data.transactions || []);
      setTotalsByMethod(incomingRes.data.totals_by_method || {});
      setWithdrawals(withdrawalsRes.data || []);
      setKycApplications(kycRes.data || []);
      setKycStats(kycStatsRes.data);
      setTeamMembers(teamRes.data || []);
      setKycDocuments(kycDocsRes.data || []);
      
      // Get current user's role
      if (meRes.data) {
        const myTeamMember = (teamRes.data || []).find(t => t.user_id === meRes.data.id);
        setCurrentUserRole(myTeamMember?.role || (meRes.data.is_admin ? 'admin' : null));
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  // ALT1 Settlement Functions
  const loadAlt1Data = async () => {
    setAlt1Loading(true);
    try {
      const [healthRes, paymentsRes, webhooksRes] = await Promise.allSettled([
        api.get('/alt1/health'),
        api.get('/alt1/payments'),
        api.get('/alt1/webhooks'),
      ]);
      if (healthRes.status === 'fulfilled') setAlt1Health(healthRes.value.data);
      if (paymentsRes.status === 'fulfilled') {
        const pData = paymentsRes.value.data;
        setAlt1Payments(Array.isArray(pData) ? pData : []);
      }
      if (webhooksRes.status === 'fulfilled') {
        const wData = webhooksRes.value.data;
        setAlt1Webhooks(Array.isArray(wData) ? wData : []);
      }
    } catch (e) {
      console.error('ALT1 data load error:', e);
    } finally {
      setAlt1Loading(false);
    }
  };

  const loadAlt1Settlements = async () => {
    try {
      const res = await api.get('/alt1/settlements');
      setAlt1Settlements(Array.isArray(res.data) ? res.data : []);
    } catch {
      // Endpoint may not exist yet, silently ignore
    }
  };

  const handleAlt1Settle = async () => {
    if (!alt1TrialForm.mint_account_id) {
      toast.error('Pick a MINT source account first — settlements cannot be initiated without provenance.');
      return;
    }
    if (!alt1TrialForm.amount || alt1TrialForm.amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setAlt1Settling(true);
    try {
      const res = await api.post('/alt1/settle', alt1TrialForm);
      if (res.data.status === 'success') {
        const txId = res.data.transaction_id;
        toast.success(`ALT1 settlement sent: $${alt1TrialForm.amount.toLocaleString()}`);
        window.open(`/receipts/alt1-settlement-receipt.html?tx=${txId}`, '_blank');
      } else {
        toast.error(`Settlement failed: ${res.data.error || res.data.status}`);
      }
      loadAlt1Data();
      loadAlt1Settlements();
      loadSettlementTrace();
      if (alt1TrialForm.mint_account_id) loadMintStatement(alt1TrialForm.mint_account_id);
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || error.response?.data?.detail || 'Settlement request failed');
    } finally {
      setAlt1Settling(false);
    }
  };

  // Load ALT1 data when tab switches to alt1
  useEffect(() => {
    if (activeTab === 'alt1') {
      loadAlt1Data();
      loadAlt1Settlements();
    }
    if (activeTab === 'mandiri') {
      loadMandiriData();
    }
    if (activeTab === 'sovein') {
      loadSoveinData();
    }
    if (activeTab === 'tap') {
      loadTapData();
    }
    if (activeTab === 'fincom') {
      loadFincomData();
    }
    if (activeTab === 'tkm') {
      loadTkmData();
    }
    if (activeTab === 'db-admin') {
      loadDbAdmin();
    }
    if (activeTab === 'swift') {
      loadSwiftData();
    }
    if (activeTab === 'mpesa') {
      loadMpesaData();
    }
    if (activeTab === 'mpesa-recon') {
      loadMpesaRecon();
    }
    if (activeTab === 'settlement-overview') {
      loadSettlementOverview();
    }
  }, [activeTab]);

  const loadMandiriData = async () => {
    setMandiriLoading(true);
    try {
      const [healthRes, txnRes] = await Promise.allSettled([
        api.get('/mandiri/health'),
        api.get('/mandiri/transactions'),
      ]);
      if (healthRes.status === 'fulfilled') setMandiriHealth(healthRes.value.data);
      if (txnRes.status === 'fulfilled') setMandiriTxns(Array.isArray(txnRes.value.data) ? txnRes.value.data : []);
    } catch (e) {
      console.error('Mandiri load error:', e);
    } finally {
      setMandiriLoading(false);
    }
  };

  const runMandiriTests = async () => {
    setMandiriTesting(true);
    try {
      const res = await api.post('/mandiri/test-scenarios');
      setMandiriTestResults(res.data);
      toast.success(`Mandiri tests: ${res.data.passed}/${res.data.total_tests} passed`);
      loadMandiriData();
    } catch (error) {
      toast.error('Test run failed');
    } finally {
      setMandiriTesting(false);
    }
  };

  const handleMandiriQuote = async () => {
    try {
      const res = await api.post('/mandiri/quote', mandiriQuoteForm);
      if (res.data.success) {
        const quoteId = res.data.response?.quoteId;
        toast.success(`Quote created: ${quoteId}`);
        setMandiriTransferForm(p => ({ ...p, quote_id: quoteId || '' }));
      } else {
        toast.error(`Quote failed: ${res.data.response?.responseMessage || 'Error'}`);
      }
      loadMandiriData();
    } catch (error) {
      toast.error('Quote request failed');
    }
  };

  const handleMandiriTransfer = async () => {
    if (!mandiriTransferForm.mint_account_id) {
      toast.error('Pick a MINT source account first — settlements cannot be initiated without provenance.');
      return;
    }
    if (!mandiriTransferForm.quote_id) {
      toast.error('Create a quote first');
      return;
    }
    try {
      const res = await api.post('/mandiri/transfer', mandiriTransferForm);
      if (res.data.success) {
        toast.success('Transfer submitted successfully');
      } else {
        toast.error(`Transfer failed: ${res.data.response?.responseMessage || 'Error'}`);
      }
      loadMandiriData();
      loadSettlementTrace();
      if (mandiriTransferForm.mint_account_id) loadMintStatement(mandiriTransferForm.mint_account_id);
    } catch (error) {
      toast.error('Transfer request failed');
    }
  };

  // SOVEIN functions
  const loadSoveinData = async () => {
    setSoveinLoading(true);
    api.get('/sovein/health').then(res => {
      setSoveinHealth(res.data);
    }).catch(e => console.error('SOVEIN health error:', e));
    try {
      const res = await api.get('/sovein/transactions');
      setSoveinTxns(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('SOVEIN txn load error:', e);
    } finally {
      setSoveinLoading(false);
    }
  };

  const loadTapData = async () => {
    setTapLoading(true);
    api.get('/tap/health').then(res => setTapHealth(res.data)).catch(() => {});
    try {
      const res = await api.get('/tap/transactions');
      setTapTransactions(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Tap load error:', e);
    } finally {
      setTapLoading(false);
    }
  };

  const handleTapSettle = async () => {
    if (!tapSettleForm.amount || parseFloat(tapSettleForm.amount) <= 0 || !tapSettleForm.recipient_name) {
      toast.error('Enter amount and recipient name');
      return;
    }
    setTapSettling(true);
    try {
      const res = await api.post('/tap/settle', {
        ...tapSettleForm,
        amount: parseFloat(tapSettleForm.amount),
      });
      if (res.data.status === 'success') {
        toast.success(`TAP settlement initiated: ${res.data.reference} - $${parseFloat(tapSettleForm.amount).toLocaleString()}`);
        if (res.data.payment_url) window.open(res.data.payment_url, '_blank');
      } else {
        toast.error(`Settlement failed: ${JSON.stringify(res.data.errors || res.data)}`);
      }
      loadTapData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Settlement failed');
    } finally {
      setTapSettling(false);
    }
  };

  const handleTapStatusUpdate = async (txnId) => {
    try {
      const res = await api.post(`/tap/status-update/${txnId}`);
      toast.info(`Status: ${res.data.current_status || 'checked'}`);
      loadTapData();
    } catch (error) {
      toast.error('Status check failed');
    }
  };

  const handleTapTrace = async (txnId) => {
    setTapTracing(true);
    setTapTraceResult(null);
    try {
      const res = await api.get(`/tap/trace/${txnId}`);
      setTapTraceResult(res.data);
      toast.success('Trace complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trace failed');
    } finally {
      setTapTracing(false);
    }
  };

  const handleTapQuickTest = async () => {
    setTapQuickTesting(true);
    try {
      const res = await api.post('/tap/settle', {
        amount: 1,
        currency: 'USD',
        recipient_name: 'Quick Test $1',
        recipient_account: 'QUICK-TEST',
        description: '$1 smoke test from Admin',
        email: 'settlements@ezytransfer.com',
      });
      if (res.data.payment_url) {
        toast.success(`Charge ${res.data.charge_id?.slice(-8)} created — opening hosted page…`);
        window.open(res.data.payment_url, '_blank');
      } else {
        toast.error('Charge created but no payment URL returned');
      }
      loadTapData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Quick test failed');
    } finally {
      setTapQuickTesting(false);
    }
  };

  const handleTapHeaderTrace = async () => {
    const id = (tapHeaderTraceId || '').trim();
    if (!id) {
      toast.error('Paste a Charge ID or Reference first');
      return;
    }
    await handleTapTrace(id);
  };

  // ─── FIN.COM handlers ───
  const loadFincomData = async () => {
    setFincomLoading(true);
    api.get('/fincom/health').then(res => setFincomHealth(res.data)).catch(() => {});
    try {
      const res = await api.get('/fincom/transactions');
      setFincomTransactions(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('FIN.COM load error:', e);
    } finally {
      setFincomLoading(false);
    }
  };

  const handleFincomSettle = async () => {
    if (!fincomSettleForm.mint_account_id) {
      toast.error('Pick a MINT source account first — settlements cannot be initiated without provenance.');
      return;
    }
    if (!fincomSettleForm.amount || parseFloat(fincomSettleForm.amount) <= 0 || !fincomSettleForm.recipient_name) {
      toast.error('Amount and Recipient Name are required');
      return;
    }
    setFincomSettling(true);
    try {
      const res = await api.post('/fincom/settle', {
        ...fincomSettleForm,
        amount: parseFloat(fincomSettleForm.amount),
      });
      if (res.data.status === 'success' || res.data.status === 'recorded') {
        toast.success(`FIN.COM settlement ${res.data.status}: ${res.data.reference} - $${parseFloat(fincomSettleForm.amount).toLocaleString()}`);
      } else {
        toast.error(`FIN.COM settlement ${res.data.status}`);
      }
      setFincomSettleForm(p => ({ ...p, amount: '', recipient_name: '', recipient_account: '', beneficiary_id: '', description: '', record_only: false }));
      loadFincomData();
      loadSettlementTrace();
      if (fincomSettleForm.mint_account_id) loadMintStatement(fincomSettleForm.mint_account_id);
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || error.response?.data?.detail || 'FIN.COM settlement failed');
    } finally {
      setFincomSettling(false);
    }
  };

  const handleFincomStatusUpdate = async (txnId) => {
    try {
      const res = await api.post(`/fincom/status-update/${txnId}`);
      toast.success(`Status: ${res.data.current_status || res.data.previous_status || 'updated'}`);
      loadFincomData();
    } catch (error) {
      toast.error('Status update failed');
    }
  };

  const handleFincomTrace = async (txnId) => {
    setFincomTracing(true);
    setFincomTraceResult(null);
    try {
      const res = await api.get(`/fincom/trace/${txnId}`);
      setFincomTraceResult(res.data);
      toast.success('Trace complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trace failed');
    } finally {
      setFincomTracing(false);
    }
  };

  // ─── TKM handlers ───
  const loadTkmData = async () => {
    setTkmLoading(true);
    api.get('/tkm/health').then(res => setTkmHealth(res.data)).catch(() => {});
    api.get('/tkm/outbound-ip').then(res => setTkmOutboundIp(res.data)).catch(() => {});
    api.get('/tkm/webhook-config').then(res => setTkmWebhookConfig(res.data)).catch(() => setTkmWebhookConfig(null));
    api.get('/tkm/accounts').then(res => {
      const data = res.data;
      if (Array.isArray(data)) setTkmAccounts(data);
      else if (data?.body && Array.isArray(data.body)) setTkmAccounts(data.body);
      else if (data?.body?.data && Array.isArray(data.body.data)) setTkmAccounts(data.body.data);
      else setTkmAccounts([]);
    }).catch(() => setTkmAccounts([]));
    try {
      const res = await api.get('/tkm/transactions');
      setTkmTransactions(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('TKM load error:', e);
    } finally {
      setTkmLoading(false);
    }
  };

  const handleTkmSettle = async () => {
    // GUARDRAIL: ALWAYS require a MINT source account before any settlement
    if (!tkmSettleForm.mint_account_id) {
      toast.error('Pick a MINT source account first — settlements cannot be initiated without provenance.');
      return;
    }
    if (!tkmSettleForm.amount || parseFloat(tkmSettleForm.amount) <= 0) {
      toast.error('Amount is required');
      return;
    }
    const newMintModes = ['debit_only', 'debit_and_notify', 'tkm_internal'];
    if (newMintModes.includes(tkmSettleForm.mode)) {
      // mode-specific validations
      if (tkmSettleForm.mode !== 'tkm_internal' && !tkmSettleForm.beneficiary_name && !tkmSettleForm.beneficiary_iban) {
        toast.error('Beneficiary name or IBAN is required for the SEPA wire');
        return;
      }
      if (tkmSettleForm.mode === 'tkm_internal' && (!tkmSettleForm.tkm_from_account_id || !tkmSettleForm.tkm_to_account_id)) {
        toast.error('TKM internal mode requires both source and destination TKM account ids');
        return;
      }
      setTkmSettling(true);
      try {
        const res = await api.post('/tkm/mint-settle', {
          ...tkmSettleForm,
          amount: parseFloat(tkmSettleForm.amount),
        });
        const debitTx = res.data?.mint_debit?.transaction_number;
        toast.success(`TKM settlement ${res.data.status}: ${res.data.reference} — ${res.data.mint_account?.key} debited ${tkmSettleForm.currency} ${parseFloat(tkmSettleForm.amount).toLocaleString()}${debitTx ? ` (MINT tx #${debitTx})` : ''}`);
        setTkmSettleForm(p => ({ ...p, amount: '', beneficiary_name: '', beneficiary_iban: '', description: '' }));
        loadTkmData();
        loadSettlementTrace();
        if (tkmSettleForm.mint_account_id) loadMintStatement(tkmSettleForm.mint_account_id);
      } catch (error) {
        toast.error(error.response?.data?.detail?.error || error.response?.data?.detail || 'TKM settlement failed');
      } finally {
        setTkmSettling(false);
      }
      return;
    }
    // Legacy modes (payout, credit-inbound, record) — still go through /tkm/settle
    if (tkmSettleForm.mode === 'payout' && !tkmSettleForm.beneficiary_iban) {
      toast.error('Recipient IBAN required for payout mode');
      return;
    }
    setTkmSettling(true);
    try {
      const res = await api.post('/tkm/settle', {
        amount: parseFloat(tkmSettleForm.amount),
        currency: tkmSettleForm.currency,
        recipient_name: tkmSettleForm.beneficiary_name,
        recipient_iban: tkmSettleForm.beneficiary_iban,
        source_account_id: tkmSettleForm.mint_account_id,
        description: tkmSettleForm.description,
        purpose: tkmSettleForm.purpose,
        mode: tkmSettleForm.mode,
      });
      toast.success(`TKM settlement ${res.data.status}: ${res.data.reference} - ${res.data.currency} ${parseFloat(tkmSettleForm.amount).toLocaleString()}`);
      setTkmSettleForm(p => ({ ...p, amount: '', beneficiary_name: '', beneficiary_iban: '', description: '' }));
      loadTkmData();
      loadSettlementTrace();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'TKM settlement failed');
    } finally {
      setTkmSettling(false);
    }
  };

  const handleTkmStatusUpdate = async (txnId) => {
    try {
      const res = await api.post(`/tkm/status-update/${txnId}`);
      toast.success(`Status: ${res.data.current_status || 'unchanged'}`);
      loadTkmData();
    } catch (error) {
      toast.error('Status update failed');
    }
  };

  const handleTkmTrace = async (txnId) => {
    setTkmTraceResult(null);
    try {
      const res = await api.get(`/tkm/trace/${txnId}`);
      setTkmTraceResult(res.data);
      toast.success('Trace complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trace failed');
    }
  };

  const handleTkmReplay = async (txn) => {
    if (!window.confirm(`Replay TKM transaction?\n\nOriginal: ${txn.reference}\nAmount: ${txn.currency} ${txn.amount}\nMode: ${txn.mode}\n\nThis will re-fire the same payload to TEAM KM with a fresh reference.`)) return;
    try {
      const res = await api.post(`/tkm/replay/${txn.id}`);
      if (res.data.ok) {
        toast.success(`Replay OK: ${res.data.new_external_reference} (TKM ${res.data.new_transaction?.wire_id || 'received'})`);
      } else {
        toast.error(`Replay returned status: ${res.data.tkm_response?.status}`);
      }
      loadTkmData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Replay failed');
    }
  };

  // ─── DB Admin handlers ───
  const loadDbAdmin = async () => {
    setDbLoading(true);
    setDbForbidden(false);
    try {
      const [cols, roles] = await Promise.all([
        api.get('/db-admin/collections'),
        api.get('/db-admin/roles'),
      ]);
      setDbCollections(cols.data.collections || []);
      setDbRoles(roles.data.roles || []);
    } catch (e) {
      if (e.response?.status === 403) {
        setDbForbidden(true);
      } else {
        toast.error('DB Admin load failed');
      }
    } finally {
      setDbLoading(false);
    }
  };

  const loadDbCollection = async (name, q = '') => {
    setDbLoading(true);
    setDbActiveCol(name);
    try {
      const res = await api.get(`/db-admin/collection/${name}`, { params: { limit: 50, q: q || undefined } });
      setDbDocs(res.data || { docs: [], total: 0 });
    } catch (e) {
      toast.error('Could not load collection');
    } finally {
      setDbLoading(false);
    }
  };

  const handleDbDelete = async (name, id) => {
    if (!window.confirm(`Delete document "${id}" from ${name}?`)) return;
    try {
      await api.delete(`/db-admin/collection/${name}/${id}`);
      toast.success('Deleted');
      loadDbCollection(name, dbSearch);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDbBackup = async () => {
    try {
      const res = await api.get('/db-admin/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ezytransfer_db_backup_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e) {
      toast.error('Backup failed');
    }
  };

  const handleDbSeed = async () => {
    if (!window.confirm('Seed Production DB?\n\nThis is idempotent and will:\n• Create the canonical $49M ALT1 settlement (if missing)\n• Create the canonical $25M SOVEIN settlement (if missing)\n• Mark every Bridge transfer as reversed')) return;
    try {
      const res = await api.post('/db-admin/seed-production');
      const a = res.data.actions || {};
      alert(`Seed OK\n\nALT1 created: ${a.alt1_seed_created}\nSOVEIN created: ${a.sovein_seed_created}\nBridge marked reversed: ${a.bridge_marked_reversed}`);
      loadDbAdmin();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seed failed');
    }
  };

  const handleDbAuditLog = async () => {
    try {
      const res = await api.get('/db-admin/audit-log', { params: { limit: 100 } });
      setDbAuditLog(res.data.entries || []);
    } catch (e) {
      toast.error('Could not load audit log');
    }
  };

  // ─── SWIFT handlers ───
  const loadSwiftData = async () => {
    setSwiftLoading(true);
    try {
      const [bens, txs, st] = await Promise.all([
        api.get('/swift/beneficiaries'),
        api.get('/swift/transfers'),
        api.get('/swift/settings'),
      ]);
      setSwiftBeneficiaries(bens.data || []);
      setSwiftTransfers(txs.data || []);
      setSwiftSettings({ ...swiftSettings, ...st.data });
    } catch (e) {
      toast.error('SWIFT load failed');
    } finally {
      setSwiftLoading(false);
    }
  };

  const handleSwiftAddBeneficiary = async () => {
    if (!swiftBenForm.label || !swiftBenForm.beneficiary_name || !swiftBenForm.beneficiary_bic) {
      toast.error('Label, name, and BIC are required');
      return;
    }
    if (!swiftBenForm.beneficiary_iban && !swiftBenForm.beneficiary_account) {
      toast.error('Provide either IBAN or account number');
      return;
    }
    try {
      await api.post('/swift/beneficiaries', swiftBenForm);
      toast.success('Beneficiary added');
      setSwiftBenForm({
        label: '', beneficiary_name: '', beneficiary_address: '',
        beneficiary_bic: '', beneficiary_iban: '', beneficiary_account: '',
        intermediary_bic: '', currency: 'USD', country_code: '', is_default: false,
      });
      loadSwiftData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    }
  };

  const handleSwiftDeleteBeneficiary = async (id) => {
    if (!window.confirm('Delete this beneficiary?')) return;
    try {
      await api.delete(`/swift/beneficiaries/${id}`);
      toast.success('Deleted');
      loadSwiftData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleSwiftSetDefault = async (id) => {
    try {
      await api.post(`/swift/beneficiaries/${id}/set-default`);
      toast.success('Default beneficiary set');
      loadSwiftData();
    } catch (e) {
      toast.error('Set default failed');
    }
  };

  const handleSwiftSyncFincom = async (id) => {
    try {
      const res = await api.post(`/swift/beneficiaries/${id}/sync-fincom`);
      toast.success(`Synced to Fin.com: ${res.data.fincom_beneficiary_id}`);
      loadSwiftData();
    } catch (e) {
      toast.error(e.response?.data?.detail?.fincom?.body?.message || e.response?.data?.detail || 'Sync failed');
    }
  };

  const handleSwiftSaveSettings = async () => {
    try {
      await api.put('/swift/settings', swiftSettings);
      toast.success('Settings saved');
      loadSwiftData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleSwiftCreateTransfer = async (sourceLayer = 'manual', sourceId = '') => {
    if (!swiftTransferForm.mint_account_id) {
      toast.error('Pick a MINT source account first — settlements cannot be initiated without provenance.');
      return;
    }
    if (!swiftTransferForm.amount || parseFloat(swiftTransferForm.amount) <= 0) {
      toast.error('Amount required');
      return;
    }
    try {
      const res = await api.post('/swift/transfers', {
        ...swiftTransferForm,
        amount: parseFloat(swiftTransferForm.amount),
        source_layer: sourceLayer,
        source_id: sourceId,
      });
      toast.success(`SWIFT transfer ${res.data.reference} created (${res.data.status})`);
      setSwiftTransferForm(p => ({
        ...p, amount: '', remittance_info: '',
        beneficiary_id: '', use_default: true,
        beneficiary_name: '', beneficiary_address: '',
        beneficiary_bic: '', beneficiary_iban: '', beneficiary_account: '',
        intermediary_bic: '', charges_bearer: '',
      }));
      loadSwiftData();
      loadSettlementTrace();
      if (swiftTransferForm.mint_account_id) loadMintStatement(swiftTransferForm.mint_account_id);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.response?.data?.detail || 'SWIFT transfer failed');
    }
  };

  const handleSwiftMarkSent = async (id) => {
    const ref = window.prompt('Bank confirmation reference (optional):') || '';
    try {
      await api.post(`/swift/transfers/${id}/mark-sent`, { confirmation_ref: ref });
      toast.success('Marked as sent');
      loadSwiftData();
    } catch (e) {
      toast.error('Mark sent failed');
    }
  };

  const handleSwiftFromTap = async (tapTxn) => {
    if (!window.confirm(`Push TAP ${tapTxn.reference} ($${tapTxn.amount} ${tapTxn.currency}) to SWIFT default beneficiary?`)) return;
    try {
      const res = await api.post(`/swift/from-tap/${tapTxn.charge_id || tapTxn.id}`, { use_default: true });
      toast.success(`SWIFT ${res.data.reference} created`);
      loadTapData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send to SWIFT failed');
    }
  };


  const runSoveinTests = async () => {
    setSoveinTesting(true);
    try {
      const res = await api.post('/sovein/test-scenarios');
      setSoveinTestResults(res.data);
      toast.success(`SOVEIN tests: ${res.data.passed}/${res.data.total_tests} passed`);
      loadSoveinData();
    } catch (error) {
      toast.error('SOVEIN test run failed');
    } finally {
      setSoveinTesting(false);
    }
  };

  const handleSoveinBiometric = async () => {
    try {
      const res = await api.post('/sovein/sync/biometric', soveinBiometricForm);
      if (res.data.success) {
        toast.success('Biometric data synced');
      } else {
        toast.error(`Biometric sync failed: ${res.data.response?.responseMessage || 'Error'}`);
      }
      loadSoveinData();
    } catch (error) {
      toast.error('Biometric sync request failed');
    }
  };

  const handleSoveinOracle = async () => {
    try {
      const res = await api.get('/sovein/oracle/alpha');
      if (res.data.success) {
        toast.success('Oracle alpha data retrieved');
      } else {
        toast.error(`Oracle retrieval failed: ${res.data.response?.responseMessage || 'Error'}`);
      }
      loadSoveinData();
    } catch (error) {
      toast.error('Oracle alpha request failed');
    }
  };

  const handleSoveinDelivery = async () => {
    try {
      const res = await api.post('/sovein/express/delivery', soveinDeliveryForm);
      if (res.data.success) {
        toast.success('Express delivery linked');
      } else {
        toast.error(`Delivery link failed: ${res.data.response?.responseMessage || 'Error'}`);
      }
      loadSoveinData();
    } catch (error) {
      toast.error('Express delivery request failed');
    }
  };

  const handleSoveinStatusUpdate = async (txnId) => {
    try {
      const res = await api.post(`/sovein/status-update/${txnId}`);
      const d = res.data;
      if (d.sovein_reachable) {
        toast.success(`Status updated: ${d.status} — ${d.status_detail}`);
      } else {
        toast.error(`SOVEIN unreachable — status unchanged`);
      }
      loadSoveinData();
    } catch (error) {
      toast.error('Status update failed');
    }
  };

  // Settlement Overview functions
  const loadSettlementOverview = async () => {
    setSettlementLoading(true);
    try {
      const res = await api.get('/settlement/overview');
      setSettlementOverview(res.data);
    } catch (e) {
      console.error('Settlement overview load error:', e);
    } finally {
      setSettlementLoading(false);
    }
  };

  // M-Pesa functions
  const loadMpesaData = async () => {
    setMpesaLoading(true);
    try {
      const [healthRes, txnRes] = await Promise.allSettled([
        api.get('/mpesa/health'),
        api.get('/mpesa/transactions'),
      ]);
      if (healthRes.status === 'fulfilled') setMpesaHealth(healthRes.value.data);
      if (txnRes.status === 'fulfilled') setMpesaTxns(Array.isArray(txnRes.value.data) ? txnRes.value.data : []);
    } catch (e) {
      console.error('M-Pesa load error:', e);
    } finally {
      setMpesaLoading(false);
    }
  };

  const loadMpesaRecon = async () => {
    setMpesaReconLoading(true);
    try {
      const res = await api.get('/mpesa/reconciliation');
      setMpesaReconData(res.data);
    } catch (e) {
      console.error('M-Pesa recon error:', e);
    } finally {
      setMpesaReconLoading(false);
    }
  };

  const runMpesaTests = async () => {
    setMpesaTesting(true);
    try {
      const res = await api.get('/mpesa/test-scenarios');
      setMpesaTestResults(res.data);
      toast.success(`M-Pesa tests: ${res.data.passed}/${res.data.total_tests} passed`);
      loadMpesaData();
    } catch (error) {
      toast.error('M-Pesa test run failed');
    } finally {
      setMpesaTesting(false);
    }
  };

  const handleMpesaSend = async () => {
    if (!mpesaSendForm.phone_number || !mpesaSendForm.amount || parseFloat(mpesaSendForm.amount) <= 0) {
      toast.error('Enter phone number and valid amount');
      return;
    }
    setMpesaSending(true);
    try {
      const res = await api.post('/mpesa/payment', {
        ...mpesaSendForm,
        amount: parseFloat(mpesaSendForm.amount),
      });
      if (res.data.status === 'success') {
        toast.success(`M-Pesa ${mpesaSendForm.direction === 'send' ? 'B2C' : 'C2B'}: ETB ${mpesaSendForm.amount} — ID: ${res.data.transaction_id}`);
      } else {
        toast.error(`Payment failed: ${res.data.response?.responseMessage || res.data.response?.error || 'Error'}`);
      }
      loadMpesaData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'M-Pesa payment failed');
    } finally {
      setMpesaSending(false);
    }
  };

  const handleMpesaValidate = async () => {
    if (!mpesaValidatePhone) { toast.error('Enter phone number'); return; }
    try {
      const res = await api.post('/mpesa/validate', { phone_number: mpesaValidatePhone });
      if (res.data.success) {
        toast.success('Customer validated');
      } else {
        toast.error(`Validation: ${res.data.response?.responseMessage || 'Failed'}`);
      }
      loadMpesaData();
    } catch (error) {
      toast.error('Validation request failed');
    }
  };

  const handleMpesaBalance = async () => {
    try {
      const res = await api.get('/mpesa/balance');
      if (res.data.success) {
        toast.success('Balance retrieved');
      } else {
        toast.error(`Balance: ${res.data.response?.responseMessage || res.data.response?.error || 'Failed'}`);
      }
      loadMpesaData();
    } catch (error) {
      toast.error('Balance request failed');
    }
  };

  const handleMpesaStatus = async () => {
    if (!mpesaStatusId) { toast.error('Enter conversation ID'); return; }
    try {
      const res = await api.post('/mpesa/status', { conversation_id: mpesaStatusId });
      if (res.data.success) {
        toast.success('Status retrieved');
      } else {
        toast.error(`Status: ${res.data.response?.responseMessage || 'Failed'}`);
      }
      loadMpesaData();
    } catch (error) {
      toast.error('Status query failed');
    }
  };

  const handleMpesaReversal = async () => {
    if (!mpesaReversalForm.transaction_id || !mpesaReversalForm.amount) {
      toast.error('Enter transaction ID and amount');
      return;
    }
    try {
      const res = await api.post('/mpesa/reversal', {
        ...mpesaReversalForm,
        amount: parseFloat(mpesaReversalForm.amount),
      });
      if (res.data.status === 'success') {
        toast.success('Reversal submitted');
      } else {
        toast.error(`Reversal: ${res.data.response?.responseMessage || 'Failed'}`);
      }
      loadMpesaData();
    } catch (error) {
      toast.error('Reversal request failed');
    }
  };

  const handleDeliverReceipt = async () => {
    if (!deliveryTx || !deliveryTo) {
      toast.error(`Enter ${deliveryChannel === 'email' ? 'email address' : 'phone number'}`);
      return;
    }
    setDeliverySending(true);
    try {
      const res = await api.post('/receipts/deliver', {
        provider: deliveryTx.provider || 'mpesa',
        transaction_id: deliveryTx.id,
        channel: deliveryChannel,
        to_email: deliveryChannel === 'email' ? deliveryTo : undefined,
        to_phone: deliveryChannel !== 'email' ? deliveryTo : undefined,
      });
      if (res.data.result?.success) {
        toast.success(`Receipt sent via ${deliveryChannel}`);
        setDeliveryOpen(false);
        setDeliveryTo('');
      } else {
        toast.error(`Delivery failed: ${res.data.result?.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delivery failed');
    } finally {
      setDeliverySending(false);
    }
  };

  const openWhatsAppShare = async (provider, txId) => {
    try {
      const res = await api.get(`/receipts/share-link/${provider}/${txId}`);
      window.open(res.data.whatsapp_link, '_blank');
    } catch {
      toast.error('Could not generate share link');
    }
  };

  const handleSoveinSettle = async () => {
    if (!soveinSettleForm.mint_account_id) {
      toast.error('Pick a MINT source account first — settlements cannot be initiated without provenance.');
      return;
    }
    if (!soveinSettleForm.amount || parseFloat(soveinSettleForm.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSoveinSettling(true);
    try {
      const res = await api.post('/sovein/settle', {
        ...soveinSettleForm,
        amount: parseFloat(soveinSettleForm.amount),
      });
      if (res.data.status === 'success') {
        toast.success(`SOVEIN settlement sent: $${parseFloat(soveinSettleForm.amount).toLocaleString()} — Ref: ${res.data.reference}`);
      } else {
        toast.error(`Settlement failed: ${res.data.response?.responseMessage || res.data.response?.error || 'Error'}`);
      }
      loadSoveinData();
      loadSettlementTrace();
      if (soveinSettleForm.mint_account_id) loadMintStatement(soveinSettleForm.mint_account_id);
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || error.response?.data?.detail || 'Settlement request failed');
    } finally {
      setSoveinSettling(false);
    }
  };

  // ALT1 settle handler is defined above (handleAlt1Settle) — uses alt1TrialForm and now requires MINT source.

  const handleSoveinTrace = async (ref) => {
    const traceRef = ref || soveinTraceRef;
    if (!traceRef) {
      toast.error('Enter a settlement reference to trace');
      return;
    }
    setSoveinTracing(true);
    setSoveinTraceResult(null);
    try {
      const res = await api.get(`/sovein/trace/${traceRef}`);
      setSoveinTraceResult(res.data);
      if (res.data.settlement_found) {
        toast.success(`Settlement found on SOVEIN network`);
      } else if (res.data.api_status?.reachable === false && res.data.endpoints_tried?.length === 0) {
        toast.error('SOVEIN API unreachable — connection timed out');
      } else {
        toast.info(`Trace complete — ${res.data.endpoints_tried?.length || 0} endpoints checked`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trace request failed');
    } finally {
      setSoveinTracing(false);
    }
  };

  const handleUsdmintLocate = async (txNum) => {
    const tx = txNum || usdmintLocateTx;
    if (!tx) { toast.error('Enter a transaction number'); return; }
    setUsdmintLocating(true);
    setUsdmintFlowResult(null);
    try {
      const res = await api.get(`/funds/trace-flow/${tx}`);
      setUsdmintFlowResult(res.data);
      if (res.data.status === 'traced') {
        toast.success('Fund flow traced successfully');
      } else {
        toast.error(res.data.error || 'Transaction not found');
      }
      // Also load payment types if not loaded
      if (usdmintPaymentTypes.length === 0) {
        const ptRes = await api.get('/funds/payment-types');
        if (ptRes.data.payment_types) setUsdmintPaymentTypes(ptRes.data.payment_types);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Locate failed');
    } finally {
      setUsdmintLocating(false);
    }
  };

  const handleUsdmintTransfer = async () => {
    if (!usdmintTransferForm.payment_type_id || !usdmintTransferForm.amount || parseFloat(usdmintTransferForm.amount) <= 0) {
      toast.error('Select a payment type and enter a valid amount');
      return;
    }
    setUsdmintTransferring(true);
    try {
      const res = await api.post('/funds/transfer', {
        ...usdmintTransferForm,
        amount: parseFloat(usdmintTransferForm.amount),
      });
      if (res.data.status === 'success') {
        toast.success(`Transfer complete: TX# ${res.data.transaction_number} — $${parseFloat(usdmintTransferForm.amount).toLocaleString()}`);
        setUsdmintFlowResult(null);
      } else {
        toast.error(`Transfer failed: ${res.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setUsdmintTransferring(false);
    }
  };

  // ---------- Unified Settlement Trace ----------
  const loadSettlementTrace = async (includeTest = settlementTraceIncludeTest) => {
    setSettlementTraceLoading(true);
    try {
      const res = await api.get(`/admin/settlement-trace?limit=200&include_test=${includeTest ? 'true' : 'false'}`);
      setSettlementTrace(res.data);
    } catch (error) {
      toast.error('Failed to load settlement trace');
    } finally {
      setSettlementTraceLoading(false);
    }
  };

  // ---------- MINT 3-Account Statement ----------
  const loadMintAccounts = async () => {
    try {
      const res = await api.get('/usdmint/accounts');
      setMintAccounts(res.data.accounts || []);
      if (!mintActiveAccountId && (res.data.accounts || []).length > 0) {
        setMintActiveAccountId(res.data.accounts[0].id);
      }
    } catch (error) {
      // silent — MINT may not be configured
    }
  };

  const loadMintStatement = async (accountId) => {
    if (!accountId) return;
    setMintStatementLoading(true);
    try {
      const res = await api.get(`/usdmint/account/${accountId}/statement?page_size=100`);
      setMintStatement(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load MINT statement');
      setMintStatement(null);
    } finally {
      setMintStatementLoading(false);
    }
  };

  const downloadMintTxConfirmation = async (accountId, accountKey, txNumber) => {
    try {
      const res = await api.get(
        `/usdmint/account/${accountId}/transaction/${txNumber}/confirmation.pdf`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `MINT_Confirmation_${accountKey || accountId}_${txNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Confirmation downloaded for tx #${txNumber}`);
    } catch (error) {
      toast.error('Failed to download confirmation');
    }
  };

  const downloadMintStatementPdf = async (accountId, accountKey) => {
    try {
      const res = await api.get(`/usdmint/account/${accountId}/statement.pdf?page_size=100`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `MINT_Statement_${accountKey || accountId}_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Statement PDF downloaded');
    } catch (error) {
      toast.error('Failed to download statement PDF');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.put(`/admin/notifications/${notificationId}/read`);
      loadAdminData();
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.put('/admin/notifications/mark-all-read');
      toast.success('All notifications marked as read');
      loadAdminData();
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/admin/notifications/${notificationId}`);
      toast.success('Notification deleted');
      loadAdminData();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const approveWithdrawal = async (withdrawalId) => {
    try {
      await api.put(`/admin/withdrawals/${withdrawalId}/approve`);
      toast.success('Withdrawal approved');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve withdrawal');
    }
  };

  const completeWithdrawal = async (withdrawalId) => {
    const txHash = prompt('Enter transaction hash (optional):');
    try {
      await api.put(`/admin/withdrawals/${withdrawalId}/complete${txHash ? `?transaction_hash=${txHash}` : ''}`);
      toast.success('Withdrawal marked as completed');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete withdrawal');
    }
  };

  const blockWithdrawal = async (withdrawalId) => {
    if (!window.confirm('Block this withdrawal as FRAUD? The record is kept, funds stay frozen (no refund), and the account is blocked from logging in or withdrawing.')) return;
    try {
      await api.put(`/admin/withdrawals/${withdrawalId}/block`);
      toast.success('Withdrawal blocked and account frozen');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block withdrawal');
    }
  };

  const rejectWithdrawal = async (withdrawalId) => {
    const reason = prompt('Enter rejection reason:', 'Rejected by admin');
    if (!reason) return;
    try {
      await api.put(`/admin/withdrawals/${withdrawalId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Withdrawal rejected and refunded');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject withdrawal');
    }
  };

  // KYC Management Functions
  const handleFirstReview = async (kycId, decision) => {
    const notes = prompt('Enter review notes:');
    if (!notes) return;
    try {
      await complianceAPI.firstReview(kycId, { decision, notes });
      toast.success(`KYC ${decision === 'approve' ? 'approved for second review' : decision}`);
      loadAdminData();
      setSelectedKYC(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review KYC');
    }
  };

  const handleSecondReview = async (kycId, decision) => {
    const notes = prompt('Enter final review notes:');
    if (!notes) return;
    try {
      await complianceAPI.secondReview(kycId, { decision, notes });
      toast.success(`KYC application ${decision}`);
      loadAdminData();
      setSelectedKYC(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete review');
    }
  };

  const handleBlockCustomer = async (kycId) => {
    const reason = prompt('Enter reason for blocking:');
    if (!reason) return;
    try {
      await complianceAPI.blockCustomer(kycId, reason);
      toast.success('Customer blocked');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block customer');
    }
  };

  const handleUnblockCustomer = async (kycId) => {
    const notes = prompt('Enter notes for unblocking:');
    if (!notes) return;
    try {
      await complianceAPI.unblockCustomer(kycId, notes);
      toast.success('Customer unblocked');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock customer');
    }
  };

  // Team Management Functions
  const canManageTeam = currentUserRole === 'super_admin' || currentUserRole === 'ceo';

  const handleAddTeamMember = async (data) => {
    try {
      const res = await complianceAPI.addTeamMember(data);
      toast.success(res.data?.message || 'Team member added successfully');
      if (res.data?.temp_password) {
        setNewMemberCred({ email: res.data.email, password: res.data.temp_password });
      }
      loadAdminData();
      setShowAddTeamMember(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    }
  };

  const handleUpdateTeamMember = async (memberId, data) => {
    try {
      await complianceAPI.updateTeamMember(memberId, data);
      toast.success('Team member updated');
      loadAdminData();
      setEditingMember(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;
    try {
      await complianceAPI.removeTeamMember(memberId);
      toast.success('Team member removed');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const handleSuspendTeamMember = async (memberId, isSuspended) => {
    try {
      await complianceAPI.updateTeamMember(memberId, { is_suspended: isSuspended });
      toast.success(isSuspended ? 'Team member suspended' : 'Team member reactivated');
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    }
  };

  // Document verification helper
  const handleVerifyDocument = async (kycId, docId, verified) => {
    const notes = verified ? '' : prompt('Enter reason for rejection:');
    try {
      await complianceAPI.verifyDocument(kycId, docId, verified, notes || '');
      toast.success(verified ? 'Document verified' : 'Document rejected');
      // Refresh KYC data
      const kycRes = await complianceAPI.getKYC(kycId);
      setSelectedKYC(kycRes.data);
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify document');
    }
  };

  const getKYCStatusBadge = (status) => {
    const statusMap = {
      'draft': 'secondary',
      'submitted': 'default',
      'under_review': 'default',
      'pending_documents': 'secondary',
      'pending_approval': 'default',
      'approved': 'default',
      'rejected': 'destructive',
      'blocked': 'destructive',
      'cancelled': 'secondary'
    };
    return statusMap[status] || 'outline';
  };

  const getRiskBadge = (risk) => {
    const riskMap = {
      'low': 'default',
      'medium': 'secondary',
      'high': 'destructive'
    };
    return riskMap[risk] || 'outline';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': 'secondary',
      'pending_approval': 'secondary',
      'pending_manual': 'secondary',
      'processing': 'default',
      'approved': 'default',
      'completed': 'default',
      'failed': 'destructive',
      'rejected': 'destructive'
    };
    return statusMap[status] || 'outline';
  };

  const handleUpdateUserStatus = async (userId, status) => {
    try {
      await api.put(`/admin/users/${userId}/status?status=${status}`);
      toast.success(`User ${status === 'active' ? 'activated' : 'suspended'} successfully`);
      loadAdminData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleInternalReview = async (userId) => {
    // Prefer the actual uploaded document submission - that's what gets reviewed
    try {
      const res = await complianceAPI.getUserKycDocuments(userId);
      setSelectedKycDoc(res.data);
      setActiveTab('kyc-docs');
      return;
    } catch (e) { /* no kyc_documents submission */ }
    try {
      const res = await complianceAPI.getUserKYC(userId);
      if (res.data?.id && res.data.status !== 'draft' && (res.data.documents || []).length > 0) {
        setSelectedKYC(res.data);
        setActiveTab('kyc');
        return;
      }
    } catch (e) { /* no compliance application */ }
    toast.info('No KYC submission from this user yet — use "Send KYC Doc Link" to request documents');
  };

  const handleViewComplianceDoc = async (kycId, doc) => {
    try {
      const res = await api.get(`/compliance/kyc/${kycId}/document/${doc.id}/download`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch (error) {
      toast.error(error.response?.status === 404 ? 'Document file not found in storage' : 'Failed to load document');
    }
  };

  const handleSendBridgeKyc = async (userId, email) => {
    try {
      const res = await complianceAPI.sendBridgeKyc(userId);
      toast.success(res.data.message || `Bridge KYC link sent to ${email}`);
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Bridge KYC email');
    }
  };

  const handleSendKycDocLink = async (userId, email) => {
    try {
      const res = await complianceAPI.sendKycDocLink(userId);
      const d = res.data;
      const parts = [`Email ${d.email_sent ? 'sent' : 'FAILED'} (${d.email})`];
      if (d.whatsapp_sent) parts.push(`WhatsApp sent (${d.phone})`);
      else if (d.whatsapp_error) parts.push(`WhatsApp: ${d.whatsapp_error}`);
      if (d.email_sent || d.whatsapp_sent) toast.success(parts.join(' · '));
      else toast.error(parts.join(' · '));
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send KYC link');
    }
  };

  const handleKycReview = async (userId, decision, notes = '') => {
    try {
      const res = await complianceAPI.reviewKycDocuments(userId, decision, notes);
      toast.success(res.data.message || `KYC ${decision}`);
      setSelectedKycDoc(null);
      loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review failed');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'default';
      case 'pending': return 'secondary';
      case 'failed': return 'destructive';
      case 'active': return 'default';
      case 'suspended': return 'destructive';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Manage users, transactions, and monitor system statistics</p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card data-testid="admin-stat-users" className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-accent" />
              <span className="text-sm text-gray-600 uppercase tracking-[0.2em]">Total Users</span>
            </div>
            <p className="text-3xl font-bold font-mono tabular-nums">{stats?.total_users || 0}</p>
          </Card>

          <Card data-testid="admin-stat-transactions" className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="w-5 h-5 text-accent" />
              <span className="text-sm text-gray-600 uppercase tracking-[0.2em]">Transactions</span>
            </div>
            <p className="text-3xl font-bold font-mono tabular-nums">{stats?.total_transactions || 0}</p>
          </Card>

          <Card data-testid="admin-stat-volume" className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-accent" />
              <span className="text-sm text-gray-600 uppercase tracking-[0.2em]">Total Volume</span>
            </div>
            <p className="text-3xl font-bold font-mono tabular-nums">${stats?.total_volume?.toFixed(2) || '0.00'}</p>
          </Card>

          <Card data-testid="admin-stat-completed" className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              <span className="text-sm text-gray-600 uppercase tracking-[0.2em]">Completed</span>
            </div>
            <p className="text-3xl font-bold font-mono tabular-nums">{stats?.completed_transactions || 0}</p>
          </Card>

          <Card data-testid="admin-stat-itau" className="p-6 cursor-pointer hover:border-orange-300 transition-colors" onClick={() => setActiveTab('itau')}>
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600 uppercase tracking-[0.2em]">Itaú (BRL)</span>
            </div>
            <p className="text-3xl font-bold font-mono tabular-nums">R$ {(itauStats?.total_volume_brl || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-xs mt-1 font-mono" data-testid="admin-stat-itau-breakdown">
              <span className="text-emerald-600">{itauStats?.charges?.paid || 0} paid</span>
              <span className="text-gray-400"> · </span>
              <span className="text-amber-600">{itauStats?.charges?.expired || 0} expired</span>
              <span className="text-gray-400"> · </span>
              <span className="text-sky-600">{itauStats?.charges?.open || 0} open</span>
            </p>
          </Card>
        </div>

        {/* WebSocket Connection Status */}
        <div className="flex items-center gap-2 mb-4">
          {wsConnected ? (
            <Badge variant="default" className="gap-1 bg-green-600">
              <Wifi className="w-3 h-3" /> Live Updates
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <WifiOff className="w-3 h-3" /> Offline
            </Badge>
          )}
          <EmailHealthBadge />
          {wsUnreadCount > 0 && (
            <Badge variant="destructive" className="animate-pulse">
              {wsUnreadCount} new notification{wsUnreadCount > 1 ? 's' : ''}
            </Badge>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => loadAdminData()}
            className="ml-2"
          >
            Refresh Data
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="admin-reconcile-btn"
            onClick={async () => {
              if (!window.confirm('Reconcile ledger?\n\nThis will:\n• Reverse all Bridge transfers\n• Delete TAP test charges ≥ $1M\n• Delete SOVEIN $0/test_report rows\n• Ensure the $49M ALT1 retroactive record exists\n\nSafe to re-run (idempotent).')) return;
              try {
                const token = localStorage.getItem('token');
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reconcile`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` },
                });
                const data = await r.json();
                if (!r.ok) throw new Error(data.detail || 'Reconcile failed');
                const a = data.actions || {};
                const after = data.after || {};
                alert(`Reconcile OK\n\nBridge reversed: ${a.bridge_reversed}\nTAP test deleted: ${a.tap_test_deleted}\nSOVEIN garbage deleted: ${a.sovein_garbage_deleted}\nALT1 seed created: ${a.alt1_seed_created}\n\nTotal transactions: ${after.total_transactions}\nActive volume: $${(after.active_volume_usd||0).toLocaleString()}`);
                loadAdminData();
              } catch (e) {
                alert('Reconcile failed: ' + e.message);
              }
            }}
            className="ml-2 border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            Reconcile Ledger
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v);
          if (v === 'transactions' && !settlementTrace) loadSettlementTrace();
          if (v === 'bridge-dashboard' && mintAccounts.length === 0) loadMintAccounts();
        }} className="space-y-6">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="users" data-testid="admin-tab-users">Users Management</TabsTrigger>
            <TabsTrigger value="kyc" data-testid="admin-tab-kyc" className="gap-2">
              <Shield className="w-4 h-4" /> KYC Review
              {kycApplications.length > 0 && (
                <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs">
                  {kycApplications.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="admin-tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="incoming" data-testid="admin-tab-incoming" className="gap-2">
              <ArrowDownLeft className="w-4 h-4" /> Incoming
            </TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="admin-tab-withdrawals" className="gap-2">
              <ArrowUpRight className="w-4 h-4" /> Withdrawals
              {withdrawals.filter(w => w.status === 'pending_approval' || w.status === 'pending_manual').length > 0 && (
                <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs">
                  {withdrawals.filter(w => w.status === 'pending_approval' || w.status === 'pending_manual').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="team" data-testid="admin-tab-team" className="gap-2">
              <UserCog className="w-4 h-4" /> Team
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="admin-tab-notifications" className="gap-2">
              <Bell className="w-4 h-4" /> Notifications
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-1 px-1.5 py-0.5 text-xs">{unreadCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="admin-tab-activity">Recent Activity</TabsTrigger>
            <TabsTrigger value="kyc-docs" data-testid="admin-tab-kyc-docs" className="gap-2">
              KYC Documents
              {kycDocuments.filter(d => d.status === 'under_review').length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">{kycDocuments.filter(d => d.status === 'under_review').length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="bridge-dashboard" data-testid="admin-tab-bridge" className="gap-2">
              <Bitcoin className="w-4 h-4" /> Bridge
            </TabsTrigger>
            {!disabledProviders.includes('alt1') && (
              <TabsTrigger value="alt1" data-testid="admin-tab-alt1" className="gap-2">
                <Server className="w-4 h-4" /> ALT1
              </TabsTrigger>
            )}
            <TabsTrigger value="mandiri" data-testid="admin-tab-mandiri" className="gap-2">
              <Building2 className="w-4 h-4" /> Mandiri
            </TabsTrigger>
            {!disabledProviders.includes('sovein') && (
              <TabsTrigger value="sovein" data-testid="admin-tab-sovein" className="gap-2">
                <Shield className="w-4 h-4" /> SOVEIN
              </TabsTrigger>
            )}
            <TabsTrigger value="mpesa" data-testid="admin-tab-mpesa" className="gap-2">
              <Smartphone className="w-4 h-4" /> M-pesa
            </TabsTrigger>
            <TabsTrigger value="mpesa-recon" data-testid="admin-tab-mpesa-recon" className="gap-2">
              <Activity className="w-4 h-4" /> M-pesa Recon
            </TabsTrigger>
            <TabsTrigger value="settlement-overview" data-testid="admin-tab-settlement-overview" className="gap-2">
              <TrendingUp className="w-4 h-4" /> Settlement Overview
            </TabsTrigger>
            <TabsTrigger value="tap" data-testid="admin-tab-tap" className="gap-2">
              <Server className="w-4 h-4" /> TAP
            </TabsTrigger>
            <TabsTrigger value="fincom" data-testid="admin-tab-fincom" className="gap-2 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-700">
              <Server className="w-4 h-4" /> FIN.COM
            </TabsTrigger>
            <TabsTrigger value="tkm" data-testid="admin-tab-tkm" className="gap-2 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Server className="w-4 h-4" /> TKM
            </TabsTrigger>
            <TabsTrigger value="swift" data-testid="admin-tab-swift" className="gap-2 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-800">
              <Server className="w-4 h-4" /> SWIFT
            </TabsTrigger>
            <TabsTrigger value="cyclos" data-testid="admin-tab-cyclos" className="gap-2 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Server className="w-4 h-4" /> Cyclos
            </TabsTrigger>
            <TabsTrigger value="itau" data-testid="admin-tab-itau" className="gap-2 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-700">
              <Server className="w-4 h-4" /> ITAU
            </TabsTrigger>
            <TabsTrigger value="paystack" data-testid="admin-tab-paystack" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <Server className="w-4 h-4" /> Paystack
            </TabsTrigger>
            <TabsTrigger value="relm" data-testid="admin-tab-relm" className="gap-2 data-[state=active]:bg-cyan-50 data-[state=active]:text-cyan-700">
              <Server className="w-4 h-4" /> Relm
            </TabsTrigger>
            <TabsTrigger value="equity" data-testid="admin-tab-equity" className="gap-2 data-[state=active]:bg-red-50 data-[state=active]:text-red-700">
              <Server className="w-4 h-4" /> Equity
            </TabsTrigger>
            <TabsTrigger value="doronpay" data-testid="admin-tab-doronpay" className="gap-2 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Server className="w-4 h-4" /> DoronPay
            </TabsTrigger>
            <TabsTrigger value="layer-transfer" data-testid="admin-tab-layer-transfer" className="gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
              <ArrowRightLeft className="w-4 h-4" /> Layer Transfer
            </TabsTrigger>
            <TabsTrigger value="db-admin" data-testid="admin-tab-db-admin" className="gap-2 data-[state=active]:bg-rose-50 data-[state=active]:text-rose-700">
              <Shield className="w-4 h-4" /> DB Admin
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-6">User Management</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Name</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Internal KYC</th>
                      <th className="text-left py-3 px-4">Bridge KYC</th>
                      <th className="text-left py-3 px-4">Joined</th>
                      <th className="text-left py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} data-testid={`admin-user-${user.id}`} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{user.name}</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{user.email}</td>
                        <td className="py-3 px-4">
                          <Badge variant={getStatusColor(user.status || 'active')} className="capitalize">
                            {user.status || 'active'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={getStatusColor(user.internal_kyc_status || user.kyc_status)} className="capitalize">
                            {user.internal_kyc_status || user.kyc_status || 'pending'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={getStatusColor(user.bridge_kyc_status === 'approved' ? 'approved' : user.bridge_kyc_status === 'not_started' ? 'pending' : user.bridge_kyc_status)} className="capitalize">
                            {user.bridge_kyc_status || 'not started'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(user.created_at)}</td>
                        <td className="py-3 px-4">
                          {user.status === 'suspended' ? (
                            <Button
                              data-testid={`activate-user-${user.id}`}
                              size="sm"
                              variant="outline"
                              className="gap-2"
                              onClick={() => handleUpdateUserStatus(user.id, 'active')}
                            >
                              <UserCheck className="w-4 h-4" /> Activate
                            </Button>
                          ) : (
                            <Button
                              data-testid={`suspend-user-${user.id}`}
                              size="sm"
                              variant="destructive"
                              className="gap-2"
                              onClick={() => handleUpdateUserStatus(user.id, 'suspended')}
                            >
                              <UserX className="w-4 h-4" /> Suspend
                            </Button>
                          )}
                          {user.internal_kyc_status !== 'approved' && user.role !== 'super_admin' && (
                            <Button
                              data-testid={`internal-review-${user.id}`}
                              size="sm"
                              variant="outline"
                              className="gap-1.5 text-emerald-400 border-emerald-400/30 hover:bg-emerald-400/10"
                              onClick={() => handleInternalReview(user.id)}
                            >
                              <Eye className="w-3.5 h-3.5" /> Internal Review
                            </Button>
                          )}
                          {user.internal_kyc_status === 'approved' && user.bridge_kyc_status !== 'approved' && user.role !== 'super_admin' && (
                            <Button
                              data-testid={`send-bridge-kyc-${user.id}`}
                              size="sm"
                              variant="outline"
                              className="gap-1.5 text-sky-400 border-sky-400/30 hover:bg-sky-400/10"
                              onClick={() => handleSendBridgeKyc(user.id, user.email)}
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> Bridge KYC
                            </Button>
                          )}
                          <Button
                            data-testid={`send-kyc-doc-${user.id}`}
                            size="sm"
                            variant="outline"
                            className="gap-1.5 text-amber-400 border-amber-400/30 hover:bg-amber-400/10"
                            onClick={() => handleSendKycDocLink(user.id, user.email)}
                          >
                            <FileText className="w-3.5 h-3.5" /> Send KYC Doc Link
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* KYC Review Tab */}
          <TabsContent value="kyc">
            <div className="space-y-6">
              {/* KYC Stats */}
              {kycStats && (
                <div className="grid md:grid-cols-4 gap-4">
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-xs text-muted-foreground uppercase">Total Applications</span>
                    </div>
                    <p className="text-2xl font-bold">{kycStats.total_applications || 0}</p>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-yellow-500" />
                      <span className="text-xs text-muted-foreground uppercase">Pending Review</span>
                    </div>
                    <p className="text-2xl font-bold">{kycStats.pending_reviews || 0}</p>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCheck className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-muted-foreground uppercase">Approved</span>
                    </div>
                    <p className="text-2xl font-bold">{kycStats.status_distribution?.approved || 0}</p>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-xs text-muted-foreground uppercase">Reviews Due</span>
                    </div>
                    <p className="text-2xl font-bold">{kycStats.reviews_due || 0}</p>
                  </Card>
                </div>
              )}

              {/* KYC Applications List */}
              <Card className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Pending KYC Applications</h2>
                {kycApplications.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No pending KYC applications</p>
                ) : (
                  <div className="space-y-4">
                    {kycApplications.map((kyc) => (
                      <div key={kyc.id} data-testid={`kyc-application-${kyc.id}`} 
                           className="p-4 border rounded-lg hover:bg-muted/50">
                        <div className="flex justify-between items-start">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              {kyc.customer_type === 'individual' ? (
                                <Users className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <Building2 className="w-4 h-4 text-blue-500" />
                              )}
                              <span className="font-medium capitalize">{kyc.customer_type}</span>
                              <Badge variant={getKYCStatusBadge(kyc.status)} className="capitalize">
                                {kyc.status?.replace(/_/g, ' ')}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {kyc.customer_type === 'individual' 
                                ? `${kyc.individual_data?.first_name || ''} ${kyc.individual_data?.last_name || ''}`
                                : kyc.corporate_data?.legal_name || 'N/A'
                              }
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Submitted: {kyc.submitted_at ? formatDate(kyc.submitted_at) : 'Draft'}
                            </p>
                            {kyc.bridge_kyc_status && (
                              <div className="flex items-center gap-1.5 mt-1">
                                <span className="text-xs text-muted-foreground">Bridge KYC:</span>
                                <Badge variant={kyc.bridge_kyc_status === 'approved' ? 'default' : 'secondary'} className="capitalize text-xs">
                                  {kyc.bridge_kyc_status}
                                </Badge>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {kyc.risk_assessment && (
                              <Badge variant={getRiskBadge(kyc.risk_assessment.overall_risk)} className="uppercase">
                                {kyc.risk_assessment.overall_risk} Risk ({kyc.risk_assessment.risk_score}/100)
                              </Badge>
                            )}
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setSelectedKYC(kyc)}
                                data-testid={`kyc-review-btn-${kyc.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" /> Review
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* KYC Detail Modal */}
              {selectedKYC && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h2 className="text-2xl font-semibold">KYC Application Review</h2>
                        <p className="text-muted-foreground">ID: {selectedKYC.id.substring(0, 8)}...</p>
                      </div>
                      <Button variant="ghost" onClick={() => setSelectedKYC(null)}>×</Button>
                    </div>

                    <div className="space-y-6">
                      {/* Basic Info */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h3 className="font-medium mb-2">Customer Type</h3>
                          <p className="capitalize">{selectedKYC.customer_type}</p>
                        </div>
                        <div>
                          <h3 className="font-medium mb-2">Status</h3>
                          <Badge variant={getKYCStatusBadge(selectedKYC.status)} className="capitalize">
                            {selectedKYC.status?.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      </div>

                      {/* Customer Details */}
                      {selectedKYC.customer_type === 'individual' && selectedKYC.individual_data && (
                        <div className="p-4 bg-muted rounded-lg">
                          <h3 className="font-medium mb-3">Personal Information</h3>
                          <div className="grid md:grid-cols-2 gap-2 text-sm">
                            <div><span className="text-muted-foreground">Name:</span> {selectedKYC.individual_data.first_name} {selectedKYC.individual_data.last_name}</div>
                            <div><span className="text-muted-foreground">DOB:</span> {selectedKYC.individual_data.date_of_birth}</div>
                            <div><span className="text-muted-foreground">Nationality:</span> {selectedKYC.individual_data.nationality}</div>
                            <div><span className="text-muted-foreground">Country:</span> {selectedKYC.individual_data.country_of_residence}</div>
                            <div><span className="text-muted-foreground">Phone:</span> {selectedKYC.individual_data.phone}</div>
                            <div><span className="text-muted-foreground">Email:</span> {selectedKYC.individual_data.email}</div>
                            <div><span className="text-muted-foreground">Occupation:</span> {selectedKYC.individual_data.occupation}</div>
                            <div><span className="text-muted-foreground">Source of Funds:</span> {selectedKYC.individual_data.source_of_funds}</div>
                            <div><span className="text-muted-foreground">Monthly Volume:</span> ${selectedKYC.individual_data.expected_monthly_volume?.toLocaleString()}</div>
                            <div><span className="text-muted-foreground">PEP:</span> {selectedKYC.individual_data.is_pep ? 'Yes' : 'No'}</div>
                          </div>
                        </div>
                      )}

                      {selectedKYC.customer_type === 'corporate' && selectedKYC.corporate_data && (
                        <div className="p-4 bg-muted rounded-lg">
                          <h3 className="font-medium mb-3">Company Information</h3>
                          <div className="grid md:grid-cols-2 gap-2 text-sm">
                            <div><span className="text-muted-foreground">Legal Name:</span> {selectedKYC.corporate_data.legal_name}</div>
                            <div><span className="text-muted-foreground">Reg. Number:</span> {selectedKYC.corporate_data.registration_number}</div>
                            <div><span className="text-muted-foreground">Legal Form:</span> {selectedKYC.corporate_data.legal_form}</div>
                            <div><span className="text-muted-foreground">Country:</span> {selectedKYC.corporate_data.country_of_incorporation}</div>
                            <div><span className="text-muted-foreground">Industry:</span> {selectedKYC.corporate_data.industry_sector}</div>
                            <div><span className="text-muted-foreground">Monthly Volume:</span> ${selectedKYC.corporate_data.expected_monthly_volume?.toLocaleString()}</div>
                          </div>
                        </div>
                      )}

                      {/* UBOs (Corporate) */}
                      {selectedKYC.ubos?.length > 0 && (
                        <div className="p-4 bg-muted rounded-lg">
                          <h3 className="font-medium mb-3">Ultimate Beneficial Owners ({selectedKYC.ubos.length})</h3>
                          <div className="space-y-2">
                            {selectedKYC.ubos.map((ubo, idx) => (
                              <div key={idx} className="flex justify-between items-center text-sm">
                                <span>{ubo.full_name}</span>
                                <span className="flex items-center gap-2">
                                  {ubo.ownership_percentage}% ({ubo.ownership_type})
                                  {ubo.is_pep && <Badge variant="secondary" className="text-xs">PEP</Badge>}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Risk Assessment */}
                      {selectedKYC.risk_assessment && (
                        <div className="p-4 bg-muted rounded-lg">
                          <h3 className="font-medium mb-3">Risk Assessment</h3>
                          <div className="grid md:grid-cols-3 gap-2 text-sm mb-3">
                            <div>Country Risk: <Badge variant={getRiskBadge(selectedKYC.risk_assessment.country_risk)} className="ml-1">{selectedKYC.risk_assessment.country_risk}</Badge></div>
                            <div>Sector Risk: <Badge variant={getRiskBadge(selectedKYC.risk_assessment.sector_risk)} className="ml-1">{selectedKYC.risk_assessment.sector_risk}</Badge></div>
                            <div>PEP Risk: <Badge variant={getRiskBadge(selectedKYC.risk_assessment.pep_risk)} className="ml-1">{selectedKYC.risk_assessment.pep_risk}</Badge></div>
                          </div>
                          {selectedKYC.risk_assessment.risk_factors?.length > 0 && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Risk Factors:</p>
                              <ul className="text-xs list-disc list-inside">
                                {selectedKYC.risk_assessment.risk_factors.map((f, i) => (
                                  <li key={i}>{f}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Documents */}
                      {selectedKYC.documents?.length > 0 && (
                        <div className="p-4 bg-muted rounded-lg">
                          <h3 className="font-medium mb-3">Documents ({selectedKYC.documents.length})</h3>
                          <div className="space-y-3">
                            {selectedKYC.documents.map((doc, idx) => (
                              <div key={idx} className="flex justify-between items-center p-3 bg-background rounded border">
                                <div className="flex items-center gap-3">
                                  <AuthDocPreview
                                    path={`/compliance/kyc/${selectedKYC.id}/document/${doc.id}/download`}
                                    label={doc.file_name}
                                    contentType={doc.mime_type}
                                    size="thumb"
                                    testId={`compliance-doc-thumb-${idx}`}
                                  />
                                  <div>
                                    <p className="font-medium capitalize">{doc.document_type?.replace(/_/g, ' ')}</p>
                                    <p className="text-xs text-muted-foreground">{doc.file_name}</p>
                                    {doc.verified && doc.verified_at && (
                                      <p className="text-[10px] text-emerald-600" data-testid={`compliance-doc-audit-${idx}`}>
                                        Verified by {doc.verified_by_email || doc.verified_by || 'admin'} · {new Date(doc.verified_at).toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="gap-1"
                                    onClick={() => handleViewComplianceDoc(selectedKYC.id, doc)}
                                    data-testid={`compliance-view-doc-${idx}`}
                                  >
                                    <Eye className="w-3 h-3" /> View
                                  </Button>
                                  {doc.verified ? (
                                    <Badge variant="default" className="gap-1">
                                      <CheckCheck className="w-3 h-3" /> Verified
                                    </Badge>
                                  ) : doc.rejected ? (
                                    <Badge variant="destructive" className="gap-1">
                                      <X className="w-3 h-3" /> Rejected
                                    </Badge>
                                  ) : (
                                    <>
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="gap-1"
                                        onClick={() => { if (window.confirm(`Confirm you have VIEWED "${doc.file_name}" and verify it as authentic?`)) handleVerifyDocument(selectedKYC.id, doc.id, true); }}
                                        data-testid={`compliance-verify-doc-${idx}`}
                                      >
                                        <CheckCheck className="w-3 h-3" /> Verify
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="destructive" 
                                        className="gap-1"
                                        onClick={() => { if (window.confirm(`Reject document "${doc.file_name}"?`)) handleVerifyDocument(selectedKYC.id, doc.id, false); }}
                                        data-testid={`compliance-reject-doc-${idx}`}
                                      >
                                        <X className="w-3 h-3" /> Reject
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Audit Trail */}
                      {(selectedKYC.submitted_at || selectedKYC.first_reviewer || selectedKYC.second_reviewer) && (
                        <div className="p-3 bg-muted/40 rounded border text-xs space-y-1" data-testid="compliance-audit-trail">
                          <p className="font-semibold text-sm mb-1">Audit Trail</p>
                          {selectedKYC.submitted_at && <p>Submitted: {new Date(selectedKYC.submitted_at).toLocaleString()}</p>}
                          {selectedKYC.first_reviewer && (
                            <p>1st review: {selectedKYC.first_review_decision || 'completed'} by {selectedKYC.first_reviewer} · {selectedKYC.first_review_at ? new Date(selectedKYC.first_review_at).toLocaleString() : ''}{selectedKYC.first_review_notes ? ` — "${selectedKYC.first_review_notes}"` : ''}</p>
                          )}
                          {selectedKYC.second_reviewer && (
                            <p>2nd review by {selectedKYC.second_reviewer} · {selectedKYC.second_review_at ? new Date(selectedKYC.second_review_at).toLocaleString() : ''}{selectedKYC.second_review_notes ? ` — "${selectedKYC.second_review_notes}"` : ''}</p>
                          )}
                          {selectedKYC.approved_at && <p className="text-emerald-600">Approved: {new Date(selectedKYC.approved_at).toLocaleString()}</p>}
                        </div>
                      )}

                      {/* Review Actions */}
                      <div className="flex flex-wrap gap-2 pt-4 border-t">
                        {!selectedKYC.first_reviewer && selectedKYC.status === 'under_review' && (
                          <>
                            <Button onClick={() => handleFirstReview(selectedKYC.id, 'approve')} className="bg-emerald-600 hover:bg-emerald-700">
                              <CheckCheck className="w-4 h-4 mr-1" /> Approve (1st Review)
                            </Button>
                            <Button onClick={() => handleFirstReview(selectedKYC.id, 'request_documents')} variant="secondary">
                              Request Documents
                            </Button>
                            <Button onClick={() => handleFirstReview(selectedKYC.id, 'escalate')} variant="outline">
                              Escalate
                            </Button>
                            <Button onClick={() => handleFirstReview(selectedKYC.id, 'reject')} variant="destructive">
                              Reject
                            </Button>
                          </>
                        )}
                        {selectedKYC.first_reviewer && !selectedKYC.second_reviewer && selectedKYC.status === 'pending_approval' && (
                          <>
                            <Button onClick={() => handleSecondReview(selectedKYC.id, 'approve')} className="bg-emerald-600 hover:bg-emerald-700">
                              <CheckCheck className="w-4 h-4 mr-1" /> Final Approve
                            </Button>
                            <Button onClick={() => handleSecondReview(selectedKYC.id, 'request_changes')} variant="secondary">
                              Request Changes
                            </Button>
                            <Button onClick={() => handleSecondReview(selectedKYC.id, 'reject')} variant="destructive">
                              Reject
                            </Button>
                          </>
                        )}
                        {selectedKYC.status === 'approved' && (
                          <Button onClick={() => handleBlockCustomer(selectedKYC.id)} variant="destructive">
                            <Ban className="w-4 h-4 mr-1" /> Block Customer
                          </Button>
                        )}
                        {selectedKYC.status === 'blocked' && (
                          <Button onClick={() => handleUnblockCustomer(selectedKYC.id)} variant="outline">
                            <Unlock className="w-4 h-4 mr-1" /> Unblock Customer
                          </Button>
                        )}
                        <Button variant="ghost" onClick={() => setSelectedKYC(null)}>Close</Button>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="transactions">
            <Card className="p-6">
              {/* View toggle: Settlement Trace (default) vs. Bridge Transactions (legacy) */}
              {!selectedTransaction && (
                <div className="flex items-center gap-2 mb-5 border-b pb-3" data-testid="transactions-view-toggle">
                  <button
                    onClick={() => { setTransactionsView('trace'); if (!settlementTrace) loadSettlementTrace(); }}
                    data-testid="view-trace-btn"
                    className={`px-3 py-1.5 text-sm font-medium rounded ${transactionsView === 'trace' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    Settlement Trace (all providers)
                  </button>
                  <button
                    onClick={() => setTransactionsView('bridge')}
                    data-testid="view-bridge-btn"
                    className={`px-3 py-1.5 text-sm font-medium rounded ${transactionsView === 'bridge' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    Bridge Transactions
                  </button>
                  {transactionsView === 'trace' && (
                    <Button
                      size="sm" variant="outline" className="ml-auto"
                      data-testid="trace-refresh-btn"
                      onClick={loadSettlementTrace}
                      disabled={settlementTraceLoading}
                    >
                      {settlementTraceLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  )}
                </div>
              )}

              {/* Settlement Trace view */}
              {!selectedTransaction && transactionsView === 'trace' && (
                <div className="space-y-4" data-testid="settlement-trace-view">
                  {!settlementTrace && !settlementTraceLoading && (
                    <div className="text-center py-12">
                      <p className="text-sm text-slate-500 mb-3">Unified view of every settlement across all providers — MINT, TKM, TAP, Bridge, SOVEIN, ALT1, Mandiri, SWIFT, M-Pesa.</p>
                      <Button onClick={loadSettlementTrace} data-testid="trace-load-btn">Load Settlement Trace</Button>
                    </div>
                  )}
                  {settlementTraceLoading && <p className="text-center text-sm text-slate-500 py-8">Loading trace across all providers...</p>}
                  {settlementTrace && (
                    <>
                      {/* Provider chips */}
                      <div className="flex flex-wrap items-center gap-2" data-testid="trace-provider-chips">
                        <button
                          onClick={() => setSettlementTraceFilter('ALL')}
                          data-testid="trace-filter-ALL"
                          className={`px-3 py-1 text-xs rounded-full border ${settlementTraceFilter === 'ALL' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                        >
                          All ({settlementTrace.count})
                        </button>
                        {(settlementTrace.providers || []).map(p => (
                          <button
                            key={p}
                            onClick={() => setSettlementTraceFilter(p)}
                            data-testid={`trace-filter-${p}`}
                            className={`px-3 py-1 text-xs rounded-full border ${settlementTraceFilter === p ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                          >
                            {p} ({settlementTrace.by_provider?.[p] || 0})
                          </button>
                        ))}
                        <span className="ml-auto flex items-center gap-3">
                          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" data-testid="trace-include-test-toggle">
                            <input
                              type="checkbox"
                              checked={settlementTraceIncludeTest}
                              onChange={(e) => { setSettlementTraceIncludeTest(e.target.checked); loadSettlementTrace(e.target.checked); }}
                              className="rounded"
                            />
                            Show test entries
                          </label>
                          <span className="text-[10px] text-slate-400">Generated {new Date(settlementTrace.generated_at).toLocaleString()}</span>
                        </span>
                      </div>

                      {/* Trace table */}
                      <div className="overflow-x-auto border border-slate-200 rounded-lg">
                        <table className="w-full text-xs" data-testid="settlement-trace-table">
                          <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
                            <tr>
                              <th className="text-left px-3 py-2">When (UTC)</th>
                              <th className="text-left px-3 py-2">Provider</th>
                              <th className="text-left px-3 py-2">Dir</th>
                              <th className="text-right px-3 py-2">Amount</th>
                              <th className="text-left px-3 py-2">Reference</th>
                              <th className="text-left px-3 py-2">Source</th>
                              <th className="text-left px-3 py-2">Destination</th>
                              <th className="text-left px-3 py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(settlementTrace.rows || [])
                              .filter(r => settlementTraceFilter === 'ALL' || r.provider === settlementTraceFilter)
                              .map((r, idx) => (
                                <tr key={`${r.provider}-${r.id}-${idx}`} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`trace-row-${r.reference || r.id}`}>
                                  <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                                  <td className="px-3 py-2">
                                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">{r.provider}</span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${r.direction === 'inbound' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : r.direction === 'outbound' ? 'bg-rose-50 text-rose-700 border border-rose-100' : 'bg-slate-50 text-slate-700 border border-slate-100'}`}>
                                      {r.direction || '—'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">{r.currency} {Number(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td className="px-3 py-2 font-mono text-[10px] text-slate-600 break-all max-w-[160px]">{r.reference || r.id}{r.wire_id && r.wire_id !== r.reference ? <span className="block text-[9px] text-slate-400">wire: {r.wire_id}</span> : null}</td>
                                  <td className="px-3 py-2 text-slate-600 break-all max-w-[180px]">{r.source_account || '—'}</td>
                                  <td className="px-3 py-2 text-slate-600 break-all max-w-[180px]">{r.dest_account || r.counterparty || '—'}</td>
                                  <td className="px-3 py-2"><span className="capitalize text-slate-700">{(r.status || '').replace(/_/g, ' ')}</span></td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                        {(settlementTrace.rows || []).filter(r => settlementTraceFilter === 'ALL' || r.provider === settlementTraceFilter).length === 0 && (
                          <p className="text-center text-sm text-slate-400 py-8">No transactions for this filter.</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Legacy Bridge view OR detail view */}
              {selectedTransaction ? (
                /* Transaction Detail View */
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-semibold">Transaction Details</h2>
                    <Button variant="outline" size="sm" onClick={() => setSelectedTransaction(null)}>
                      <X className="w-4 h-4 mr-1" /> Back to List
                    </Button>
                  </div>

                  {/* Status & Amount Header */}
                  <div className="flex items-center justify-between bg-muted/50 rounded-lg p-5">
                    <div>
                      <p className="text-sm text-muted-foreground">Amount</p>
                      <p className="text-3xl font-mono font-bold">${selectedTransaction.amount?.toFixed(2)}</p>
                    </div>
                    <div className="text-right flex items-center gap-3">
                      {selectedTransaction.provider === 'bridge' && <Badge variant="outline">Bridge</Badge>}
                      {selectedTransaction.usdmint_settled && <Badge className="bg-sky-600 text-white">Settled</Badge>}
                      <Badge variant={getStatusColor(selectedTransaction.state || selectedTransaction.status)} className="capitalize text-sm py-1 px-3">
                        {(selectedTransaction.state || selectedTransaction.status || '').replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>

                  {/* Core Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3 border rounded-lg p-4">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Transaction Info</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Internal ID</span><span className="font-mono text-xs select-all">{selectedTransaction.id}</span></div>
                        {selectedTransaction.bridge_transfer_id && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Bridge Transfer ID</span><span className="font-mono text-xs select-all">{selectedTransaction.bridge_transfer_id}</span></div>
                        )}
                        <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="capitalize">{selectedTransaction.transfer_type || selectedTransaction.type || 'N/A'}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">From</span><span>{(selectedTransaction.from_currency || selectedTransaction.source_currency || 'USD').toUpperCase()}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">To</span><span>{(selectedTransaction.to_currency || selectedTransaction.destination_currency || 'USDC').toUpperCase()}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Rail</span><span className="capitalize">{(selectedTransaction.source_rail || '').replace(/_/g, ' ') || 'N/A'}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{formatDate(selectedTransaction.created_at)}</span></div>
                        {selectedTransaction.updated_at && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Updated</span><span>{formatDate(selectedTransaction.updated_at)}</span></div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3 border rounded-lg p-4">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">User Info</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{selectedTransaction.user_email || 'N/A'}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">User ID</span><span className="font-mono text-xs select-all">{selectedTransaction.user_id || 'N/A'}</span></div>
                        {selectedTransaction.payment_route_id && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Payment Route</span><span className="font-mono text-xs select-all">{selectedTransaction.payment_route_id}</span></div>
                        )}
                        {selectedTransaction.destination_wallet && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Dest. Wallet</span><span className="font-mono text-xs select-all truncate max-w-[200px]">{selectedTransaction.destination_wallet}</span></div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Deposit Instructions */}
                  {selectedTransaction.source_deposit_instructions && (
                    <div className="border rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Deposit Instructions</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {Object.entries(selectedTransaction.source_deposit_instructions).map(([key, val]) => (
                          <div key={key} className="flex justify-between gap-2">
                            <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-mono text-xs select-all text-right truncate max-w-[250px]">{String(val)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* USDMint Settlement */}
                  {selectedTransaction.usdmint_settlement && (
                    <div className="border rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">USDMint Settlement</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="default" className="capitalize">{selectedTransaction.usdmint_settlement.status}</Badge></div>
                        {selectedTransaction.usdmint_settlement.transaction_id && (
                          <div className="flex justify-between"><span className="text-muted-foreground">USDMint Txn ID</span><span className="font-mono text-xs select-all">{selectedTransaction.usdmint_settlement.transaction_id}</span></div>
                        )}
                        {selectedTransaction.usdmint_settlement.amount && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="font-mono">${selectedTransaction.usdmint_settlement.amount}</span></div>
                        )}
                        {selectedTransaction.usdmint_settled_at && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Settled At</span><span>{formatDate(selectedTransaction.usdmint_settled_at)}</span></div>
                        )}
                        {selectedTransaction.usdmint_settlement.idempotency_key && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Idempotency Key</span><span className="font-mono text-xs select-all">{selectedTransaction.usdmint_settlement.idempotency_key}</span></div>
                        )}
                        {selectedTransaction.usdmint_settlement.reconciled_by && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Reconciled By</span><span>{selectedTransaction.usdmint_settlement.reconciled_by}</span></div>
                        )}
                        {selectedTransaction.usdmint_settlement.deposit_message && (
                          <div className="flex justify-between"><span className="text-muted-foreground">Deposit Message</span><span className="font-mono text-xs select-all">{selectedTransaction.usdmint_settlement.deposit_message}</span></div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bridge Response */}
                  {selectedTransaction.bridge_response && (
                    <div className="border rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Bridge API Response</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Bridge ID</span><span className="font-mono text-xs select-all">{selectedTransaction.bridge_response.id}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Bridge State</span><span className="capitalize">{selectedTransaction.bridge_response.state}</span></div>
                        {selectedTransaction.bridge_response.on_behalf_of && (
                          <div className="flex justify-between"><span className="text-muted-foreground">On Behalf Of</span><span className="font-mono text-xs select-all">{selectedTransaction.bridge_response.on_behalf_of}</span></div>
                        )}
                        {selectedTransaction.bridge_response.receipt && (
                          <>
                            <div className="flex justify-between"><span className="text-muted-foreground">Initial Amount</span><span>${selectedTransaction.bridge_response.receipt.initial_amount}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Developer Fee</span><span>${selectedTransaction.bridge_response.receipt.developer_fee}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Final Amount</span><span>${selectedTransaction.bridge_response.receipt.final_amount}</span></div>
                          </>
                        )}
                        <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{formatDate(selectedTransaction.bridge_response.created_at)}</span></div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {selectedTransaction.provider === 'bridge' && !selectedTransaction.usdmint_settled && (
                    <div className="flex gap-3 pt-2">
                      <Button
                        variant="outline"
                        onClick={async () => {
                          try {
                            await complianceAPI.markSettled(selectedTransaction.id);
                            toast.success('Transfer marked as settled');
                            setSelectedTransaction(null);
                            loadAdminData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to mark settled');
                          }
                        }}
                      >
                        <CheckCheck className="w-4 h-4 mr-2" /> Mark as Settled
                      </Button>
                      <Button
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={async () => {
                          if (!window.confirm('This will send real money from USDMint. Continue?')) return;
                          try {
                            await complianceAPI.settleTransfer(selectedTransaction.id);
                            toast.success('Settlement initiated via USDMint');
                            setSelectedTransaction(null);
                            loadAdminData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Settlement failed');
                          }
                        }}
                      >
                        <DollarSign className="w-4 h-4 mr-2" /> Settle via USDMint
                      </Button>
                    </div>
                  )}
                </div>
              ) : transactionsView === 'bridge' ? (
                /* Transactions List */
                <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">All Transactions</h2>
                <div className="flex gap-2">
                  <Button
                    data-testid="sync-bridge-btn"
                    variant="default"
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={async () => {
                      try {
                        const res = await complianceAPI.syncBridgeTransfers();
                        toast.success(`Synced: ${res.data.imported} imported, ${res.data.settled} settled, ${res.data.already_exists} already existed`);
                        loadAdminData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Sync failed');
                      }
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Sync Bridge Transfers
                  </Button>
                  <Button
                    data-testid="reconcile-settlements-btn"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await complianceAPI.reconcileSettlements();
                        if (res.data.reconciled > 0) {
                          toast.success(`Reconciled ${res.data.reconciled} transfers`);
                        } else {
                          toast.info(`All ${transactions.filter(t => t.provider === 'bridge').length} Bridge transfers are already settled — nothing to reconcile`);
                        }
                        loadAdminData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Reconciliation failed');
                      }
                    }}
                  >
                    <CheckCheck className="w-4 h-4 mr-1" /> Reconcile Settlements
                  </Button>
                  <Button
                    data-testid="settle-all-btn"
                    variant="default"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm('This will send real money from USDMint for ALL pending transfers. Continue?')) return;
                      try {
                        const res = await complianceAPI.settleAllPending();
                        toast.success(`Settled ${res.data.settled} of ${res.data.total_pending} transfers`);
                        loadAdminData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Settlement failed');
                      }
                    }}
                  >
                    <DollarSign className="w-4 h-4 mr-1" /> Settle All Pending
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                {transactions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No transactions yet</p>
                ) : transactions.map((tx) => {
                  const isBridge = tx.provider === 'bridge';
                  const displayStatus = tx.state || tx.status;
                  return (
                  <div
                    key={tx.id}
                    data-testid={`admin-transaction-${tx.id}`}
                    className="flex justify-between items-center p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedTransaction(tx)}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {isBridge ? `${(tx.from_currency || 'USD').toUpperCase()} → ${(tx.to_currency || 'USDC').toUpperCase()}` : `${tx.from_currency} → ${tx.to_currency}`}
                        </p>
                        {isBridge && <Badge variant="outline" className="text-xs">Bridge</Badge>}
                        {tx.usdmint_settled && <Badge variant="default" className="text-xs bg-sky-600">Settled</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {tx.user_email ? `${tx.user_email} • ` : ''}ID: {tx.id.substring(0, 8)}...
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <p className="font-mono font-bold text-lg">${tx.amount?.toFixed(2)}</p>
                        <Badge variant={getStatusColor(displayStatus)} className="capitalize mt-1">
                          {displayStatus === 'funds_sent' ? 'Funds Sent' : displayStatus === 'awaiting_funds' ? 'Awaiting Funds' : displayStatus === 'payment_processed' ? 'Completed' : displayStatus}
                        </Badge>
                      </div>
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  );
                })}
              </div>
                </>
              ) : null}
            </Card>
          </TabsContent>

          <TabsContent value="incoming">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Incoming Transactions</h2>
                <div className="flex gap-4">
                  {Object.entries(totalsByMethod).map(([method, data]) => (
                    <Badge key={method} variant="outline" className="text-sm py-1 px-3">
                      {method}: ${data.amount?.toFixed(2) || '0.00'} ({data.count || 0})
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                {incomingTransactions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No incoming transactions yet</p>
                ) : (
                  incomingTransactions.map((tx) => (
                    <div key={tx.id} data-testid={`incoming-tx-${tx.id}`} className="flex justify-between items-center p-4 border rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                          <ArrowDownLeft className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="font-medium capitalize">{tx.payment_method?.replace('_', ' ')}</p>
                          <p className="text-sm text-muted-foreground">Provider: {tx.payment_provider}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-lg text-green-600">+${tx.amount?.toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground">{tx.currency || 'USD'}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="withdrawals">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Withdrawal Requests</h2>
                <div className="flex gap-2">
                  <Badge variant="secondary">
                    Pending: {withdrawals.filter(w => w.status === 'pending_approval' || w.status === 'pending_manual').length}
                  </Badge>
                  <Badge variant="default">
                    Completed: {withdrawals.filter(w => w.status === 'completed').length}
                  </Badge>
                </div>
              </div>
              <div className="space-y-4">
                {withdrawals.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No withdrawal requests yet</p>
                ) : (
                  withdrawals.map((withdrawal) => (
                    <div key={withdrawal.id} data-testid={`admin-withdrawal-${withdrawal.id}`} className="flex justify-between items-center p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${withdrawal.wallet_type === 'crypto' ? 'bg-orange-500/10' : 'bg-blue-500/10'}`}>
                          {withdrawal.wallet_type === 'crypto' ? (
                            <Bitcoin className="w-5 h-5 text-orange-500" />
                          ) : (
                            <Building2 className="w-5 h-5 text-blue-500" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{withdrawal.user_email || 'Unknown User'}</p>
                            <Badge variant={getStatusBadge(withdrawal.status)}>{withdrawal.status?.replace('_', ' ')}</Badge>
                            {withdrawal.hold_reason && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`withdrawal-hold-badge-${withdrawal.id}`}>
                                AUTO-HELD: {withdrawal.hold_reason}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {withdrawal.wallet_type === 'crypto' 
                              ? `${withdrawal.crypto_currency?.toUpperCase()} - ${withdrawal.wallet_address?.substring(0, 20)}...`
                              : `${withdrawal.bank_name} - ****${withdrawal.account_number}`}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatDate(withdrawal.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="font-mono font-bold text-lg">${withdrawal.amount?.toFixed(2)}</p>
                        {!['completed', 'blocked', 'rejected'].includes(withdrawal.status) && (
                          <div className="flex gap-2">
                            {(withdrawal.status === 'pending_approval' || withdrawal.status === 'pending_manual') && (
                              <Button size="sm" onClick={() => approveWithdrawal(withdrawal.id)}>
                                Approve
                              </Button>
                            )}
                            {withdrawal.status === 'approved' && (
                              <Button size="sm" onClick={() => completeWithdrawal(withdrawal.id)}>
                                Mark Complete
                              </Button>
                            )}
                            <Button size="sm" variant="destructive" onClick={() => rejectWithdrawal(withdrawal.id)}>
                              Reject
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-600 text-red-700 hover:bg-red-50" data-testid={`block-withdrawal-${withdrawal.id}`} onClick={() => blockWithdrawal(withdrawal.id)}>
                              Block (fraud)
                            </Button>
                          </div>
                        )}
                        {withdrawal.status === 'blocked' && (
                          <Badge variant="destructive" data-testid={`withdrawal-blocked-badge-${withdrawal.id}`}>BLOCKED — FRAUD</Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Team Management Tab */}
          <TabsContent value="team">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-semibold">Team Management</h2>
                  <p className="text-sm text-muted-foreground">Manage admin team members and their permissions</p>
                </div>
                {canManageTeam && (
                  <Button onClick={() => setShowAddTeamMember(true)} data-testid="add-team-member-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Team Member
                  </Button>
                )}
              </div>

              {!canManageTeam && (
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-200 text-sm">
                  Only Super Admin and CEO can add, remove, or modify team members.
                </div>
              )}

              <div className="space-y-4">
                {teamMembers.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No team members configured</p>
                ) : (
                  teamMembers.map((member) => (
                    <div key={member.id} data-testid={`team-member-${member.id}`} 
                         className={`p-4 border rounded-lg ${member.is_suspended ? 'opacity-60 bg-red-500/5' : ''}`}>
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{member.name}</p>
                            <Badge variant={member.is_suspended ? 'destructive' : 'default'}>
                              {ADMIN_ROLES.find(r => r.value === member.role)?.label || member.role}
                            </Badge>
                            {member.is_suspended && <Badge variant="destructive">Suspended</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">{member.email}</p>
                          <div className="flex gap-1 flex-wrap mt-2">
                            {member.permissions?.map((p) => (
                              <Badge key={p} variant="outline" className="text-xs">{p.replace(/_/g, ' ')}</Badge>
                            ))}
                          </div>
                          {member.department && (
                            <p className="text-xs text-muted-foreground">Department: {member.department}</p>
                          )}
                        </div>
                        {canManageTeam && (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setEditingMember(member)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {member.is_suspended ? (
                              <Button size="sm" variant="outline" onClick={() => handleSuspendTeamMember(member.id, false)}>
                                <Play className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="secondary" onClick={() => handleSuspendTeamMember(member.id, true)}>
                                <Pause className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="destructive" onClick={() => handleRemoveTeamMember(member.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Add Team Member Modal */}
            {showAddTeamMember && (
              <AddTeamMemberModal 
                onAdd={handleAddTeamMember}
                onClose={() => setShowAddTeamMember(false)}
              />
            )}

            {/* One-time temp password display */}
            {newMemberCred && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="max-w-md w-full p-6" data-testid="team-member-cred-dialog">
                  <h2 className="text-xl font-semibold mb-2">Account Created</h2>
                  <p className="text-sm text-muted-foreground mb-4">Share these login details securely with the new team member. This password is shown only once — it is not emailed.</p>
                  <div className="bg-muted rounded-lg p-4 space-y-2 font-mono text-sm">
                    <div><span className="text-muted-foreground">Email: </span><strong data-testid="team-member-cred-email">{newMemberCred.email}</strong></div>
                    <div><span className="text-muted-foreground">Temp password: </span><strong data-testid="team-member-cred-password">{newMemberCred.password}</strong></div>
                  </div>
                  <div className="flex gap-2 justify-end pt-4">
                    <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(`Email: ${newMemberCred.email}\nTemporary password: ${newMemberCred.password}`); toast.success('Copied to clipboard'); }}>Copy</Button>
                    <Button data-testid="team-member-cred-close" onClick={() => setNewMemberCred(null)}>Done</Button>
                  </div>
                </Card>
              </div>
            )}

            {/* Edit Team Member Modal */}
            {editingMember && (
              <EditTeamMemberModal
                member={editingMember}
                onUpdate={(data) => handleUpdateTeamMember(editingMember.id, data)}
                onClose={() => setEditingMember(null)}
              />
            )}
          </TabsContent>

          <TabsContent value="notifications">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Notifications</h2>
                {notifications.length > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllNotificationsRead} className="gap-2">
                    <CheckCheck className="w-4 h-4" /> Mark All Read
                  </Button>
                )}
              </div>
              <div className="space-y-4">
                {notifications.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No notifications yet</p>
                ) : (
                  notifications.map((notification) => (
                    <div 
                      key={notification.id} 
                      data-testid={`notification-${notification.id}`}
                      className={`flex justify-between items-start p-4 border rounded-lg ${notification.status === 'unread' ? 'bg-accent/5 border-accent/20' : ''}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${notification.status === 'unread' ? 'bg-accent/10' : 'bg-muted'}`}>
                          <Bell className={`w-5 h-5 ${notification.status === 'unread' ? 'text-accent' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{notification.title}</p>
                            {notification.status === 'unread' && (
                              <Badge variant="default" className="text-xs">New</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                          {notification.amount && (
                            <p className="text-sm font-mono text-green-600 mt-1">
                              +${notification.amount?.toFixed(2)} {notification.currency || 'USD'}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">{formatDate(notification.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {notification.status === 'unread' && (
                          <Button variant="ghost" size="sm" onClick={() => markNotificationRead(notification.id)}>
                            <CheckCheck className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => deleteNotification(notification.id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Recent Users</h2>
                <div className="space-y-4">
                  {recentActivity?.recent_users?.map((user) => (
                    <div key={user.id} className="flex justify-between items-center pb-4 border-b last:border-0">
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{formatDate(user.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Recent Transactions</h2>
                <div className="space-y-4">
                  {recentActivity?.recent_transactions?.map((tx) => (
                    <div key={tx.id} className="flex justify-between items-center pb-4 border-b last:border-0">
                      <div>
                        <p className="font-medium">${Number(tx.amount || 0).toFixed(2)} <span className="text-xs font-normal text-muted-foreground">{tx.from_currency} → {tx.to_currency}</span></p>
                        <p className="text-sm text-muted-foreground">{tx.sender_name || tx.sender_email || 'Unknown sender'}{tx.recipient_name ? ` → ${tx.recipient_name}` : ''}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                      </div>
                      <Badge variant={getStatusColor(tx.status)} className="capitalize">
                        {tx.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* KYC Documents Review Tab */}
          <TabsContent value="kyc-docs">
            <div className="space-y-6">
              {selectedKycDoc ? (
                /* Document Detail Review */
                <Card className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold">KYC Document Review</h2>
                    <Button variant="outline" size="sm" onClick={() => setSelectedKycDoc(null)}>
                      <X className="w-4 h-4 mr-1" /> Back to List
                    </Button>
                  </div>

                  {/* User Info */}
                  <div className="bg-muted/50 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><span className="text-muted-foreground">Name:</span> <strong>{selectedKycDoc.full_name || selectedKycDoc.user_name}</strong></div>
                      <div><span className="text-muted-foreground">Email:</span> <strong>{selectedKycDoc.user_email}</strong></div>
                      <div><span className="text-muted-foreground">DOB:</span> <strong>{selectedKycDoc.date_of_birth || 'N/A'}</strong></div>
                      <div><span className="text-muted-foreground">Country:</span> <strong>{selectedKycDoc.country || 'N/A'}</strong></div>
                    </div>
                  </div>

                  {/* Uploaded Documents */}
                  <h3 className="font-semibold mb-3">Uploaded Documents ({selectedKycDoc.files?.length || 0})</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {(selectedKycDoc.files || []).map((f, i) => (
                      <div key={i} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{f.label}</span>
                          <Badge variant="outline" className="text-xs">{f.content_type}</Badge>
                        </div>
                        <AuthDocPreview
                          path={`/admin/kyc-documents/${selectedKycDoc.user_id}/file/${f.filename}`}
                          label={f.label}
                          contentType={f.content_type}
                          testId={`kyc-doc-preview-${i}`}
                        />
                        <p className="text-xs text-muted-foreground mt-2">{f.original_name} ({(f.size / 1024).toFixed(0)} KB)</p>
                      </div>
                    ))}
                  </div>

                  {/* AI Analysis */}
                  {selectedKycDoc.ai_analysis && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold">AI Analysis</h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await complianceAPI.reanalyzeKycDocuments(selectedKycDoc.user_id);
                              setSelectedKycDoc(prev => ({ ...prev, ai_analysis: res.data }));
                              toast.success('Re-analysis complete');
                            } catch (e) {
                              toast.error('Re-analysis failed');
                            }
                          }}
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-analyze
                        </Button>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground">Overall Recommendation:</span>
                          <Badge variant={
                            selectedKycDoc.ai_analysis.overall_recommendation === 'approve' ? 'default' :
                            selectedKycDoc.ai_analysis.overall_recommendation === 'reject' ? 'destructive' : 'secondary'
                          } className="text-sm capitalize">
                            {selectedKycDoc.ai_analysis.overall_recommendation}
                          </Badge>
                        </div>
                        <p className="text-sm">{selectedKycDoc.ai_analysis.overall_reason}</p>
                        <p className="text-sm text-muted-foreground">
                          Avg Authenticity Score: <strong>{selectedKycDoc.ai_analysis.average_authenticity_score}/10</strong> |
                          Documents Analyzed: <strong>{selectedKycDoc.ai_analysis.documents_analyzed}</strong>
                        </p>
                        {selectedKycDoc.ai_analysis.total_issues?.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-amber-400 mb-1">Issues Found:</p>
                            <ul className="text-sm text-muted-foreground list-disc list-inside">
                              {selectedKycDoc.ai_analysis.total_issues.map((issue, i) => (
                                <li key={i}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* Per-document analysis */}
                        {(selectedKycDoc.ai_analysis.individual_analyses || []).map((a, i) => (
                          <div key={i} className="border-t pt-3 mt-3">
                            <p className="font-medium text-sm mb-1">{a.document_label}</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              <div>Type: <strong className="capitalize">{a.document_type}</strong></div>
                              <div>Readability: <strong className="capitalize">{a.readability}</strong></div>
                              <div>Authenticity: <strong>{a.authenticity_score}/10</strong></div>
                              <div>Recommendation: <Badge variant={a.recommendation === 'approve' ? 'default' : a.recommendation === 'reject' ? 'destructive' : 'secondary'} className="text-xs capitalize">{a.recommendation}</Badge></div>
                            </div>
                            {a.recommendation_reason && <p className="text-xs text-muted-foreground mt-1">{a.recommendation_reason}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Admin Decision Buttons */}
                  {selectedKycDoc.status === 'under_review' && (
                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        data-testid="kyc-approve-btn"
                        className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                        onClick={() => { if (window.confirm('Confirm you have reviewed the documents above and approve this KYC?\nThe user will immediately get dashboard/wallet access.')) handleKycReview(selectedKycDoc.user_id, 'approved'); }}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> APPROVE
                      </Button>
                      <Button
                        data-testid="kyc-reject-btn"
                        variant="destructive"
                        className="flex-1"
                        onClick={() => {
                          const notes = window.prompt('Rejection reason (will be sent to user):');
                          if (notes !== null) handleKycReview(selectedKycDoc.user_id, 'rejected', notes);
                        }}
                      >
                        <AlertCircle className="w-4 h-4 mr-2" /> REJECT
                      </Button>
                    </div>
                  )}
                  {selectedKycDoc.status !== 'under_review' && selectedKycDoc.admin_review && (
                    <div className="pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Decision:</span>
                        <Badge variant={selectedKycDoc.status === 'approved' ? 'default' : 'destructive'} className="capitalize">{selectedKycDoc.status}</Badge>
                      </div>
                      {selectedKycDoc.admin_review.notes && (
                        <p className="text-sm text-muted-foreground mt-2">Notes: {selectedKycDoc.admin_review.notes}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1" data-testid="kyc-doc-audit-line">
                        Reviewed by: {selectedKycDoc.admin_review.reviewed_by || 'admin'} · {formatDate(selectedKycDoc.admin_review.reviewed_at)}
                      </p>
                    </div>
                  )}
                </Card>
              ) : (
                /* Submissions List */
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-semibold">KYC Document Submissions</h2>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-sky-600 border-sky-300 hover:bg-sky-50"
                      onClick={async () => {
                        if (!window.confirm('Send a fresh KYC upload link by email to EVERY user still pending verification?')) return;
                        try {
                          const res = await api.post('/admin/kyc-doc/send-link-bulk');
                          toast.success(`KYC links: ${res.data.emails_sent}/${res.data.pending_users} emails · ${res.data.whatsapp_sent || 0} WhatsApp sent`);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Bulk send failed');
                        }
                      }}
                      data-testid="kyc-bulk-rerequest-btn"
                    >
                      <Mail className="w-3.5 h-3.5" /> Re-request All Pending
                    </Button>
                  </div>
                  {kycDocuments.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No KYC document submissions yet</p>
                  ) : (
                    <div className="space-y-3">
                      {kycDocuments.map((doc) => (
                        <div
                          key={doc.id || doc.user_id}
                          data-testid={`kyc-doc-item-${doc.user_id}`}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
                          onClick={async () => {
                            try {
                              const res = await complianceAPI.getUserKycDocuments(doc.user_id);
                              setSelectedKycDoc(res.data);
                            } catch (e) {
                              toast.error('Failed to load documents');
                            }
                          }}
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{doc.user_name || doc.full_name || 'Unknown'}</p>
                              <Badge variant={
                                doc.status === 'approved' ? 'default' :
                                doc.status === 'rejected' ? 'destructive' :
                                doc.status === 'under_review' ? 'secondary' : 'outline'
                              } className="capitalize text-xs">
                                {doc.status === 'under_review' ? 'Under Review' : doc.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{doc.user_email} | {doc.files?.length || 0} documents</p>
                            <p className="text-xs text-muted-foreground">Submitted: {formatDate(doc.uploaded_at)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc.ai_analysis && (
                              <Badge variant={
                                doc.ai_analysis.overall_recommendation === 'approve' ? 'default' :
                                doc.ai_analysis.overall_recommendation === 'reject' ? 'destructive' : 'secondary'
                              } className="text-xs capitalize">
                                AI: {doc.ai_analysis.overall_recommendation}
                              </Badge>
                            )}
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Bridge Dashboard Tab */}
          <TabsContent value="bridge-dashboard">
            <div className="space-y-6">
              <ExternalStpSettlement provider="Bridge" />
              <CryptoPayment />

              {/* MINT 3-Account Statement (per-account inflows/outflows + PDF) */}
              <Card className="p-6" data-testid="mint-accounts-statement">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-sky-500 rounded-full" />
                  <h3 className="text-xl font-semibold">USDMint Accounts — Settlement Statement</h3>
                  <Badge variant="outline" className="text-sky-700 border-sky-300">3 accounts</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Pick any of the 3 MINT accounts to see every inflow and outflow that touched it, with summary totals and a downloadable PDF for compliance.
                </p>
                {mintAccounts.length === 0 ? (
                  <p className="text-xs text-slate-500">No MINT accounts loaded yet. <button onClick={loadMintAccounts} className="underline text-sky-600">Refresh</button></p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="mint-account-cards">
                    {mintAccounts.map((a) => (
                      <button
                        key={a.id}
                        data-testid={`mint-account-${a.key}`}
                        onClick={() => setMintActiveAccountId(a.id)}
                        className={`text-left border rounded-lg p-3 hover:bg-sky-50 transition ${mintActiveAccountId === a.id ? 'border-sky-500 bg-sky-50/70' : 'border-slate-200 bg-white'}`}
                      >
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">{a.key}</p>
                        <p className="font-semibold text-slate-800 mt-0.5">{a.name}</p>
                        <p className="text-[11px] font-mono text-slate-500 mt-0.5">{a.number}</p>
                        <p className="font-mono text-lg text-slate-900 mt-1">${Number(a.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </button>
                    ))}
                  </div>
                )}
                {mintActiveAccountId && (
                  <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid="mint-statement-panel">
                    {mintStatementLoading && (
                      <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="mint-statement-loading">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-600" />
                        Fetching live statement from USDMint... (first load ~10s, cached for 30s afterwards)
                      </div>
                    )}
                    {mintStatement && !mintStatementLoading && (
                      <>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                          <div>
                            <p className="text-xs text-slate-500">Statement for</p>
                            <p className="font-semibold text-slate-800">{mintStatement.account?.name} <span className="font-mono text-xs text-slate-500">{mintStatement.account?.number}</span></p>
                            {mintStatement.account?.balance !== null && mintStatement.account?.balance !== undefined && (
                              <p className="text-xs text-slate-600 mt-0.5">
                                Current balance: <span className="font-mono font-semibold text-slate-900" data-testid="mint-statement-balance">${Number(mintStatement.account.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm" variant="outline"
                            data-testid="mint-statement-pdf-btn"
                            onClick={() => downloadMintStatementPdf(mintActiveAccountId, mintStatement.account?.key)}
                          >
                            Download Statement PDF
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <div className="border border-emerald-100 rounded p-2 bg-emerald-50/50">
                            <p className="text-[10px] uppercase tracking-wide text-emerald-700">Inflows</p>
                            <p className="font-mono text-lg text-emerald-700 mt-0.5">${Number(mintStatement.summary?.total_in || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <p className="text-[10px] text-emerald-700/70">{mintStatement.summary?.inflows_count} txns</p>
                          </div>
                          <div className="border border-rose-100 rounded p-2 bg-rose-50/50">
                            <p className="text-[10px] uppercase tracking-wide text-rose-700">Outflows</p>
                            <p className="font-mono text-lg text-rose-700 mt-0.5">${Number(mintStatement.summary?.total_out || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <p className="text-[10px] text-rose-700/70">{mintStatement.summary?.outflows_count} txns</p>
                          </div>
                          <div className="border border-indigo-100 rounded p-2 bg-indigo-50/50">
                            <p className="text-[10px] uppercase tracking-wide text-indigo-700">Net</p>
                            <p className={`font-mono text-lg mt-0.5 ${Number(mintStatement.summary?.net || 0) >= 0 ? 'text-indigo-700' : 'text-rose-700'}`}>${Number(mintStatement.summary?.net || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <p className="text-[10px] text-indigo-700/70">in − out</p>
                          </div>
                        </div>
                        <div className="overflow-x-auto border border-slate-100 rounded">
                          <table className="w-full text-xs" data-testid="mint-statement-table">
                            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
                              <tr>
                                <th className="text-left px-2 py-1.5">Date (UTC)</th>
                                <th className="text-left px-2 py-1.5">Tx #</th>
                                <th className="text-left px-2 py-1.5">Type</th>
                                <th className="text-left px-2 py-1.5">Dir</th>
                                <th className="text-right px-2 py-1.5">Amount</th>
                                <th className="text-left px-2 py-1.5">Description</th>
                                <th className="text-center px-2 py-1.5">Confirmation</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(mintStatement.rows || []).map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                                  <td className="px-2 py-1.5 font-mono text-[10px]">{(r.date || '').slice(0, 19).replace('T', ' ')}</td>
                                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{r.transaction_number}</td>
                                  <td className="px-2 py-1.5">{r.type}</td>
                                  <td className="px-2 py-1.5">
                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${r.direction === 'inflow' ? 'bg-emerald-50 text-emerald-700' : r.direction === 'outflow' ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-700'}`}>
                                      {r.direction}
                                    </span>
                                  </td>
                                  <td className={`px-2 py-1.5 text-right font-mono ${r.direction === 'inflow' ? 'text-emerald-700' : r.direction === 'outflow' ? 'text-rose-700' : 'text-slate-700'}`}>${Number(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td className="px-2 py-1.5 text-slate-600 max-w-[260px] truncate" title={r.description}>{r.description}</td>
                                  <td className="px-2 py-1.5 text-center">
                                    <button
                                      onClick={() => downloadMintTxConfirmation(mintActiveAccountId, mintStatement.account?.key, r.transaction_number)}
                                      data-testid={`mint-row-confirmation-${r.transaction_number}`}
                                      className="text-[10px] underline text-sky-700 hover:text-sky-900"
                                      title="Download per-transaction settlement confirmation PDF"
                                    >
                                      PDF
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {(mintStatement.rows || []).length === 0 && (
                            <p className="text-center text-xs text-slate-400 py-6">No transactions found for this account in the last 100 ledger entries.</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="alt1">
            <div className="space-y-6" data-testid="alt1-dashboard">
              {/* Header */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-10 bg-blue-600 rounded-full" />
                    <div>
                      <h2 className="text-2xl font-semibold">ALT1 Settlement Provider</h2>
                      <p className="text-sm text-muted-foreground">Direct API settlement layer</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadAlt1Data} disabled={alt1Loading} data-testid="alt1-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${alt1Loading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>

                {/* Status Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">API Status</p>
                    <div className="flex items-center gap-2 mt-2" data-testid="alt1-api-status">
                      {alt1Health?.status === 'connected' ? (
                        <><Wifi className="w-5 h-5 text-green-500" /><span className="font-semibold text-green-600">Connected</span></>
                      ) : alt1Health?.status === 'unavailable' ? (
                        <><WifiOff className="w-5 h-5 text-red-500" /><span className="font-semibold text-red-600">Unavailable</span></>
                      ) : (
                        <><WifiOff className="w-5 h-5 text-gray-400" /><span className="font-semibold text-gray-500">Unknown</span></>
                      )}
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Settlements</p>
                    <p className="text-2xl font-bold mt-2" data-testid="alt1-settlements-count">{alt1Settlements.length}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Payments Logged</p>
                    <p className="text-2xl font-bold mt-2" data-testid="alt1-payments-count">{alt1Payments.length}</p>
                  </div>
                </div>

                {alt1Health?.status === 'unavailable' && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <strong>Note:</strong> ALT1 API (34.60.236.109) is currently unreachable. Please check connectivity or try again.
                  </div>
                )}
              </Card>

              {/* Trial Transaction */}
              <Card className="p-6" data-testid="alt1-trial-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-green-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Send Settlement</h3>
                  <Badge className="bg-amber-50 text-amber-800 border border-amber-200">MINT source required</Badge>
                </div>

                <MintSourcePicker
                  accounts={mintAccounts}
                  selectedId={alt1TrialForm.mint_account_id}
                  onSelect={(id) => setAlt1TrialForm(p => ({ ...p, mint_account_id: id }))}
                  providerLabel="ALT1"
                  onRefresh={loadMintAccounts}
                  amount={alt1TrialForm.amount}
                  testIdPrefix="alt1-mint-source"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount (USD)</Label>
                    <Input
                      data-testid="alt1-amount-input"
                      type="number"
                      value={alt1TrialForm.amount}
                      onChange={(e) => setAlt1TrialForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                      placeholder="200.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recipient Name</Label>
                    <Input
                      data-testid="alt1-recipient-name"
                      value={alt1TrialForm.recipient_name}
                      onChange={(e) => setAlt1TrialForm(p => ({ ...p, recipient_name: e.target.value }))}
                      placeholder="Recipient name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recipient Account</Label>
                    <Input
                      data-testid="alt1-recipient-account"
                      value={alt1TrialForm.recipient_account}
                      onChange={(e) => setAlt1TrialForm(p => ({ ...p, recipient_account: e.target.value }))}
                      placeholder="Account identifier"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Memo</Label>
                    <Input
                      data-testid="alt1-memo"
                      value={alt1TrialForm.memo}
                      onChange={(e) => setAlt1TrialForm(p => ({ ...p, memo: e.target.value }))}
                      placeholder="Optional note..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-4">
                  <Button
                    data-testid="alt1-settle-btn"
                    onClick={handleAlt1Settle}
                    disabled={alt1Settling || !alt1TrialForm.amount}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {alt1Settling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><DollarSign className="w-4 h-4 mr-2" /> Send Settlement</>}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    via HMAC-SHA256 signed request to ALT1 API
                  </span>
                </div>
              </Card>

              {/* Settlement History */}
              <Card className="p-6" data-testid="alt1-history-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-purple-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Settlement History</h3>
                </div>
                {alt1Settlements.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Date</th>
                          <th className="text-left py-2 px-3">Amount</th>
                          <th className="text-left py-2 px-3">Recipient</th>
                          <th className="text-left py-2 px-3">Status</th>
                          <th className="text-left py-2 px-3">Memo</th>
                          <th className="text-left py-2 px-3">Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alt1Settlements.map((s, i) => (
                          <tr key={s.id || i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                            <td className="py-2 px-3 font-semibold">${s.amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                            <td className="py-2 px-3">{s.recipient_name || '—'}</td>
                            <td className="py-2 px-3">
                              <Badge variant={s.status === 'success' ? 'default' : s.status === 'error' ? 'destructive' : 'secondary'} className="capitalize">
                                {s.status}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-xs text-muted-foreground">{s.memo || '—'}</td>
                            <td className="py-2 px-3">
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 text-xs"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/alt1/receipt-page/${s.id}`, '_blank')}
                                data-testid={`alt1-receipt-${s.id}`}
                              >
                                <FileText className="w-3 h-3" /> Receipt
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 text-xs"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/alt1/dtr/${s.id}`, '_blank')}
                                data-testid={`alt1-dtr-${s.id}`}
                              >
                                <FileText className="w-3 h-3" /> DTR
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No settlements yet. Use the form above to initiate your first settlement.</p>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="mandiri">
            <div className="space-y-6" data-testid="mandiri-dashboard">
              {/* Header */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-10 bg-orange-600 rounded-full" />
                    <div>
                      <h2 className="text-2xl font-semibold">Mandiri Bank Remittance</h2>
                      <p className="text-sm text-muted-foreground">International remittance via Bank Mandiri Open API</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadMandiriData} disabled={mandiriLoading} data-testid="mandiri-refresh-btn">
                      <RefreshCw className={`w-4 h-4 mr-2 ${mandiriLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                    <Button size="sm" onClick={runMandiriTests} disabled={mandiriTesting} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="mandiri-run-tests-btn">
                      {mandiriTesting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running...</> : <><Activity className="w-4 h-4 mr-2" /> Run Sandbox Tests</>}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">API Status</p>
                    <div className="flex items-center gap-2 mt-2" data-testid="mandiri-api-status">
                      {mandiriHealth?.status === 'connected' ? (
                        <><Wifi className="w-5 h-5 text-green-500" /><span className="font-semibold text-green-600">Connected</span></>
                      ) : mandiriHealth?.status === 'unavailable' ? (
                        <><WifiOff className="w-5 h-5 text-red-500" /><span className="font-semibold text-red-600">Unavailable</span></>
                      ) : (
                        <><WifiOff className="w-5 h-5 text-gray-400" /><span className="font-semibold text-gray-500">Unknown</span></>
                      )}
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Transactions</p>
                    <p className="text-2xl font-bold mt-2" data-testid="mandiri-txn-count">{mandiriTxns.filter(t => t.type === 'transfer').length}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Sandbox URL</p>
                    <p className="text-xs font-mono mt-2 text-muted-foreground break-all">{mandiriHealth?.base_url || 'sandbox.bankmandiri.co.id'}</p>
                  </div>
                </div>
              </Card>

              {/* Test Results */}
              {mandiriTestResults && (
                <Card className="p-6" data-testid="mandiri-test-results">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-blue-600 rounded-full" />
                    <h3 className="text-xl font-semibold">Sandbox Test Results</h3>
                    <Badge variant={mandiriTestResults.passed === mandiriTestResults.total_tests ? 'default' : 'secondary'}>
                      {mandiriTestResults.passed}/{mandiriTestResults.total_tests} Passed
                    </Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">#</th>
                          <th className="text-left py-2 px-3">Test Scenario</th>
                          <th className="text-left py-2 px-3">Expected</th>
                          <th className="text-left py-2 px-3">Response</th>
                          <th className="text-left py-2 px-3">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mandiriTestResults.results?.map((r, i) => (
                          <tr key={i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3">{i + 1}</td>
                            <td className="py-2 px-3 font-medium">{r.test}</td>
                            <td className="py-2 px-3 text-xs font-mono">{r.expected}</td>
                            <td className="py-2 px-3 text-xs font-mono">{r.response_code} - {r.response_message}</td>
                            <td className="py-2 px-3">
                              {r.passed ? (
                                <Badge variant="default" className="bg-green-600">PASS</Badge>
                              ) : (
                                <Badge variant="destructive">FAIL</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Create Quote */}
              <Card className="p-6" data-testid="mandiri-quote-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-green-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Step 1: Create Quote</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Source Account</Label>
                    <Input data-testid="mandiri-source-account" value={mandiriQuoteForm.source_account}
                      onChange={(e) => setMandiriQuoteForm(p => ({...p, source_account: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Destination Amount</Label>
                    <Input data-testid="mandiri-dest-amount" value={mandiriQuoteForm.destination_amount}
                      onChange={(e) => setMandiriQuoteForm(p => ({...p, destination_amount: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Destination Currency</Label>
                    <Input data-testid="mandiri-dest-currency" value={mandiriQuoteForm.destination_currency}
                      onChange={(e) => setMandiriQuoteForm(p => ({...p, destination_currency: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Beneficiary Bank Code</Label>
                    <Input data-testid="mandiri-bank-code" value={mandiriQuoteForm.beneficiary_bank_code}
                      onChange={(e) => setMandiriQuoteForm(p => ({...p, beneficiary_bank_code: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Beneficiary Account</Label>
                    <Input data-testid="mandiri-benef-account" value={mandiriQuoteForm.beneficiary_account}
                      onChange={(e) => setMandiriQuoteForm(p => ({...p, beneficiary_account: e.target.value}))} />
                  </div>
                </div>
                <Button className="mt-4 bg-green-600 hover:bg-green-700 text-white" onClick={handleMandiriQuote} data-testid="mandiri-create-quote-btn">
                  <DollarSign className="w-4 h-4 mr-2" /> Create Quote
                </Button>
              </Card>

              {/* Transfer */}
              <Card className="p-6" data-testid="mandiri-transfer-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-purple-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Step 2: Execute Transfer</h3>
                  <Badge className="bg-amber-50 text-amber-800 border border-amber-200">MINT source required</Badge>
                </div>

                <MintSourcePicker
                  accounts={mintAccounts}
                  selectedId={mandiriTransferForm.mint_account_id}
                  onSelect={(id) => setMandiriTransferForm(p => ({ ...p, mint_account_id: id }))}
                  providerLabel="Mandiri"
                  onRefresh={loadMintAccounts}
                  amount={mandiriTransferForm.amount_value}
                  testIdPrefix="mandiri-mint-source"
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Quote ID</Label>
                    <Input data-testid="mandiri-quote-id" value={mandiriTransferForm.quote_id} placeholder="From Step 1"
                      onChange={(e) => setMandiriTransferForm(p => ({...p, quote_id: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input data-testid="mandiri-transfer-amount" value={mandiriTransferForm.amount_value}
                      onChange={(e) => setMandiriTransferForm(p => ({...p, amount_value: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Input data-testid="mandiri-transfer-currency" value={mandiriTransferForm.amount_currency}
                      onChange={(e) => setMandiriTransferForm(p => ({...p, amount_currency: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Beneficiary Name</Label>
                    <Input data-testid="mandiri-benef-name" value={mandiriTransferForm.beneficiary_name}
                      onChange={(e) => setMandiriTransferForm(p => ({...p, beneficiary_name: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Beneficiary Account</Label>
                    <Input data-testid="mandiri-benef-account-transfer" value={mandiriTransferForm.beneficiary_account}
                      onChange={(e) => setMandiriTransferForm(p => ({...p, beneficiary_account: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input data-testid="mandiri-country" value={mandiriTransferForm.beneficiary_country}
                      onChange={(e) => setMandiriTransferForm(p => ({...p, beneficiary_country: e.target.value}))} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Remark</Label>
                    <Input data-testid="mandiri-remark" value={mandiriTransferForm.remark} placeholder="Payment description"
                      onChange={(e) => setMandiriTransferForm(p => ({...p, remark: e.target.value}))} />
                  </div>
                </div>
                <Button className="mt-4 bg-purple-600 hover:bg-purple-700 text-white" onClick={handleMandiriTransfer} data-testid="mandiri-transfer-btn">
                  <ArrowUpRight className="w-4 h-4 mr-2" /> Execute Transfer
                </Button>
              </Card>

              {/* Transaction History */}
              <Card className="p-6" data-testid="mandiri-history-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-orange-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Transaction History</h3>
                </div>
                {mandiriTxns.filter(t => t.type !== 'test_report').length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Date</th>
                          <th className="text-left py-2 px-3">Type</th>
                          <th className="text-left py-2 px-3">Amount</th>
                          <th className="text-left py-2 px-3">Beneficiary</th>
                          <th className="text-left py-2 px-3">Response</th>
                          <th className="text-left py-2 px-3">Status</th>
                          <th className="text-left py-2 px-3">Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mandiriTxns.filter(t => t.type !== 'test_report').map((t, i) => (
                          <tr key={t.id || i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                            <td className="py-2 px-3">
                              <Badge variant="outline" className="capitalize">{t.type}</Badge>
                            </td>
                            <td className="py-2 px-3 font-semibold">{t.amount ? `$${Number(t.amount).toLocaleString()}` : '—'}</td>
                            <td className="py-2 px-3">{t.beneficiary_name || '—'}</td>
                            <td className="py-2 px-3 text-xs font-mono">{t.response_code} {t.response_message}</td>
                            <td className="py-2 px-3">
                              <Badge variant={t.status === 'COMPLETED' ? 'default' : t.status === 'PENDING' ? 'secondary' : 'destructive'} className="capitalize">
                                {t.status || '—'}
                              </Badge>
                            </td>
                            <td className="py-2 px-3">
                              {t.type === 'transfer' && (
                                <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid={`mandiri-receipt-${t.id}`}
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/mandiri/receipt-page/${t.id}`, '_blank')}>
                                  <FileText className="w-3 h-3 mr-1" /> PDF
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No transactions yet. Create a quote and execute a transfer above.</p>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="sovein">
            <div className="space-y-6" data-testid="sovein-dashboard">
              {/* Header */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-10 bg-emerald-600 rounded-full" />
                    <div>
                      <h2 className="text-2xl font-semibold">SOVEIN Settlement Layer</h2>
                      <p className="text-sm text-muted-foreground">Biometric-linked settlement via SOVEIN Data Gates</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadSoveinData} disabled={soveinLoading} data-testid="sovein-refresh-btn">
                      <RefreshCw className={`w-4 h-4 mr-2 ${soveinLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                    <Button size="sm" onClick={runSoveinTests} disabled={soveinTesting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sovein-run-tests-btn">
                      {soveinTesting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running...</> : <><Activity className="w-4 h-4 mr-2" /> Run Sandbox Tests</>}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">API Status</p>
                    <div className="flex items-center gap-2 mt-2" data-testid="sovein-api-status">
                      {soveinHealth?.status === 'connected' ? (
                        <><Wifi className="w-5 h-5 text-green-500" /><span className="font-semibold text-green-600">Connected</span></>
                      ) : soveinHealth?.status === 'unavailable' ? (
                        <><WifiOff className="w-5 h-5 text-red-500" /><span className="font-semibold text-red-600">Unavailable</span></>
                      ) : (
                        <><WifiOff className="w-5 h-5 text-gray-400" /><span className="font-semibold text-gray-500">Unknown</span></>
                      )}
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Transactions</p>
                    <p className="text-2xl font-bold mt-2" data-testid="sovein-txn-count">{soveinTxns.filter(t => t.type !== 'test_report').length}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Security Level</p>
                    <Badge variant="outline" className="mt-2">BLOCK-FUND-TIER-1</Badge>
                  </div>
                </div>
              </Card>

              {/* Send Settlement */}
              <Card className="p-6" data-testid="sovein-settle-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-green-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Send Settlement</h3>
                  <Badge className="bg-amber-50 text-amber-800 border border-amber-200">MINT source required</Badge>
                </div>

                <MintSourcePicker
                  accounts={mintAccounts}
                  selectedId={soveinSettleForm.mint_account_id}
                  onSelect={(id) => setSoveinSettleForm(p => ({ ...p, mint_account_id: id }))}
                  providerLabel="SOVEIN"
                  onRefresh={loadMintAccounts}
                  amount={soveinSettleForm.amount}
                  testIdPrefix="sovein-mint-source"
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Amount (USD)</Label>
                    <Input
                      data-testid="sovein-settle-amount"
                      type="number"
                      value={soveinSettleForm.amount}
                      onChange={(e) => setSoveinSettleForm(p => ({ ...p, amount: e.target.value }))}
                      placeholder="10000.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recipient Name</Label>
                    <Input
                      data-testid="sovein-settle-recipient-name"
                      value={soveinSettleForm.recipient_name}
                      onChange={(e) => setSoveinSettleForm(p => ({ ...p, recipient_name: e.target.value }))}
                      placeholder="Recipient name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recipient Account</Label>
                    <Input
                      data-testid="sovein-settle-recipient-account"
                      value={soveinSettleForm.recipient_account}
                      onChange={(e) => setSoveinSettleForm(p => ({ ...p, recipient_account: e.target.value }))}
                      placeholder="Account identifier"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit ID</Label>
                    <Input
                      data-testid="sovein-settle-unit-id"
                      value={soveinSettleForm.unit_id}
                      onChange={(e) => setSoveinSettleForm(p => ({ ...p, unit_id: e.target.value }))}
                      placeholder="L360-GH-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Input
                      data-testid="sovein-settle-currency"
                      value={soveinSettleForm.currency}
                      onChange={(e) => setSoveinSettleForm(p => ({ ...p, currency: e.target.value }))}
                      placeholder="USD"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Memo</Label>
                    <Input
                      data-testid="sovein-settle-memo"
                      value={soveinSettleForm.memo}
                      onChange={(e) => setSoveinSettleForm(p => ({ ...p, memo: e.target.value }))}
                      placeholder="Optional note..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-4">
                  <Button
                    data-testid="sovein-settle-btn"
                    onClick={handleSoveinSettle}
                    disabled={soveinSettling || !soveinSettleForm.amount}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {soveinSettling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><DollarSign className="w-4 h-4 mr-2" /> Send Settlement</>}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    via X-API-Key signed request to SOVEIN Data Gate
                  </span>
                </div>
              </Card>

              {/* Trace Settlement */}
              <Card className="p-6" data-testid="sovein-trace-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-amber-500 rounded-full" />
                  <h3 className="text-xl font-semibold">Trace Settlement</h3>
                  <Badge variant="outline" className="text-amber-600 border-amber-300">Destination Finder</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Query SOVEIN's network to trace where a settlement landed. Enter a reference or click Trace on any settlement below.
                </p>
                <div className="flex gap-3 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>Settlement Reference</Label>
                    <Input
                      data-testid="sovein-trace-ref-input"
                      value={soveinTraceRef}
                      onChange={(e) => setSoveinTraceRef(e.target.value)}
                      placeholder="e.g. EZY-SOV-E50ECB1E"
                    />
                  </div>
                  <Button
                    data-testid="sovein-trace-btn"
                    onClick={() => handleSoveinTrace()}
                    disabled={soveinTracing || !soveinTraceRef}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {soveinTracing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Tracing...</> : <><Eye className="w-4 h-4 mr-2" /> Trace Settlement</>}
                  </Button>
                </div>

                {soveinTraceResult && (
                  <div className="mt-4 border rounded-lg overflow-hidden" data-testid="sovein-trace-result">
                    <div className={`px-4 py-3 text-sm font-medium ${
                      soveinTraceResult.settlement_found 
                        ? 'bg-green-50 text-green-800 border-b border-green-200' 
                        : soveinTraceResult.api_status?.reachable === false
                          ? 'bg-red-50 text-red-800 border-b border-red-200'
                          : 'bg-amber-50 text-amber-800 border-b border-amber-200'
                    }`}>
                      {soveinTraceResult.settlement_found 
                        ? 'Settlement FOUND on SOVEIN network' 
                        : soveinTraceResult.api_status?.reachable === false && soveinTraceResult.endpoints_tried?.length === 0
                          ? `SOVEIN API connection timed out: ${soveinTraceResult.api_status?.error || 'Timeout'}`
                          : `Trace complete - ${soveinTraceResult.endpoints_tried?.length || 0} endpoints queried`
                      }
                    </div>
                    <div className="p-4 space-y-3 bg-slate-50">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Reference:</span>
                          <span className="ml-2 font-mono font-medium">{soveinTraceResult.reference}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">API Status:</span>
                          <span className={`ml-2 font-medium ${soveinTraceResult.api_status?.reachable ? 'text-green-600' : 'text-red-600'}`}>
                            {soveinTraceResult.api_status?.reachable ? 'Reachable' : 'Unreachable'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Traced at:</span>
                          <span className="ml-2">{new Date(soveinTraceResult.traced_at).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Endpoints tried:</span>
                          <span className="ml-2 font-medium">{soveinTraceResult.endpoints_tried?.length || 0}</span>
                        </div>
                      </div>

                      {soveinTraceResult.local_record && (
                        <div className="border-t pt-3">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Local DB Record</p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-muted-foreground">Amount:</span> <span className="font-medium">${Number(soveinTraceResult.local_record.amount || 0).toLocaleString()}</span></div>
                            <div><span className="text-muted-foreground">Status:</span> <span className="font-medium">{soveinTraceResult.local_record.status}</span></div>
                            <div><span className="text-muted-foreground">Recipient:</span> <span className="font-medium">{soveinTraceResult.local_record.recipient_name}</span></div>
                            <div><span className="text-muted-foreground">Account:</span> <span className="font-mono text-xs">{soveinTraceResult.local_record.recipient_account}</span></div>
                          </div>
                        </div>
                      )}

                      {soveinTraceResult.settlement_found && soveinTraceResult.destination && (
                        <div className="border-t pt-3">
                          <p className="text-xs uppercase tracking-wide text-green-700 mb-2">SOVEIN Destination Data</p>
                          <pre className="bg-white border rounded p-3 text-xs overflow-auto max-h-64 font-mono">
                            {JSON.stringify(soveinTraceResult.destination, null, 2)}
                          </pre>
                        </div>
                      )}

                      {soveinTraceResult.endpoints_tried?.length > 0 && (
                        <details className="border-t pt-3">
                          <summary className="text-xs uppercase tracking-wide text-muted-foreground cursor-pointer hover:text-foreground">
                            Endpoint Results ({soveinTraceResult.endpoints_tried.length})
                          </summary>
                          <div className="mt-2 space-y-1">
                            {soveinTraceResult.endpoints_tried.map((ep, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs font-mono">
                                <span className={`w-2 h-2 rounded-full ${ep.status_code === 200 ? 'bg-green-500' : ep.status_code ? 'bg-red-400' : 'bg-gray-400'}`} />
                                <span className="text-muted-foreground">{ep.method}</span>
                                <span className="truncate">{ep.url?.replace(soveinTraceResult.base_url, '')}</span>
                                <span className="text-muted-foreground ml-auto">{ep.status_code || ep.error || '—'}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                )}
              </Card>

              {/* USDMint Fund Locator & Transfer */}
              <Card className="p-6" data-testid="usdmint-fund-locator">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-blue-600 rounded-full" />
                  <h3 className="text-xl font-semibold">USDMint Fund Locator</h3>
                  <Badge variant="outline" className="text-blue-600 border-blue-300">Real-Time Ledger</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Locate funds on USDMint by transaction number. Shows exact origin, destination, fees, and current balances.
                </p>
                <div className="flex gap-3 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>USDMint Transaction Number</Label>
                    <Input
                      data-testid="usdmint-locate-input"
                      value={usdmintLocateTx}
                      onChange={(e) => setUsdmintLocateTx(e.target.value)}
                      placeholder="e.g. 0382219271"
                    />
                  </div>
                  <Button
                    data-testid="usdmint-locate-btn"
                    onClick={() => handleUsdmintLocate()}
                    disabled={usdmintLocating || !usdmintLocateTx}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {usdmintLocating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Locating...</> : <><Eye className="w-4 h-4 mr-2" /> Locate Funds</>}
                  </Button>
                </div>

                {usdmintFlowResult && usdmintFlowResult.status === 'traced' && (
                  <div className="mt-5 space-y-4" data-testid="usdmint-flow-result">
                    {/* Fund Flow Visualization */}
                    <div className="bg-slate-50 border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                        <span className="text-xs uppercase tracking-wide font-semibold text-blue-700">Fund Flow — TX# {usdmintFlowResult.transaction_number}</span>
                      </div>

                      {/* Origin → Destination */}
                      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center mb-4">
                        <div className="bg-white border rounded-lg p-3">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Origin</p>
                          <p className="font-semibold text-sm">{usdmintFlowResult.origin?.account_name}</p>
                          <p className="text-xs font-mono text-muted-foreground">{usdmintFlowResult.origin?.account_number || 'System'}</p>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-emerald-600">${Number(usdmintFlowResult.amount || 0).toLocaleString()}</div>
                          <ArrowRight className="w-5 h-5 mx-auto text-muted-foreground" />
                          <p className="text-[10px] text-muted-foreground">{usdmintFlowResult.payment_type}</p>
                        </div>
                        <div className="bg-white border-2 border-blue-300 rounded-lg p-3">
                          <p className="text-[10px] uppercase tracking-wide text-blue-600 mb-1">Destination</p>
                          <p className="font-semibold text-sm">{usdmintFlowResult.destination?.user || usdmintFlowResult.destination?.account_name}</p>
                          <p className="text-xs font-mono text-muted-foreground">{usdmintFlowResult.destination?.account_number || 'System'}</p>
                          <p className="text-xs text-muted-foreground">{usdmintFlowResult.destination?.account_name}</p>
                        </div>
                      </div>

                      {/* Status & Meta */}
                      <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                        <div><span className="text-muted-foreground">Status:</span> <span className="font-medium text-emerald-600">{usdmintFlowResult.statuses?.join(' → ')}</span></div>
                        <div><span className="text-muted-foreground">Channel:</span> <span className="font-medium">{usdmintFlowResult.initiated_by}</span></div>
                        <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{usdmintFlowResult.date ? new Date(usdmintFlowResult.date).toLocaleString() : 'N/A'}</span></div>
                      </div>

                      {/* Fee Splits */}
                      {usdmintFlowResult.fee_splits?.length > 0 && (
                        <div className="border-t pt-3">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Fee Splits</p>
                          <div className="space-y-2">
                            {usdmintFlowResult.fee_splits.map((fee, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-white border rounded px-3 py-2 text-sm">
                                <div>
                                  <span className="font-medium">{fee.fee_type}</span>
                                  <span className="text-muted-foreground ml-2">→ {fee.to_user}</span>
                                  <span className="text-xs font-mono text-muted-foreground ml-1">({fee.to_account})</span>
                                </div>
                                <span className="font-semibold">${Number(fee.amount || 0).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Current Balances */}
                      {usdmintFlowResult.current_balances?.length > 0 && (
                        <div className="border-t pt-3 mt-3">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Current Account Balances (Real-Time)</p>
                          <div className="grid grid-cols-3 gap-2">
                            {usdmintFlowResult.current_balances.map((acc, idx) => (
                              <div key={idx} className="bg-white border rounded px-3 py-2">
                                <p className="text-xs text-muted-foreground">{acc.account_name}</p>
                                <p className="font-semibold text-sm">${Number(acc.balance || 0).toLocaleString()}</p>
                                <p className="text-[10px] font-mono text-muted-foreground">{acc.account_number}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Transfer Section */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="usdmint-transfer-section">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-2 h-2 rounded-full bg-amber-500" />
                        <span className="text-xs uppercase tracking-wide font-semibold text-amber-700">Execute Transfer</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs">Payment Type</Label>
                          <select
                            data-testid="usdmint-transfer-type"
                            className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                            value={usdmintTransferForm.payment_type_id}
                            onChange={(e) => setUsdmintTransferForm(p => ({ ...p, payment_type_id: e.target.value }))}
                          >
                            <option value="">Select payment type...</option>
                            {usdmintPaymentTypes.map(pt => (
                              <option key={pt.id} value={pt.id}>{pt.name} ({pt.internal_name})</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Amount (USD)</Label>
                          <Input
                            data-testid="usdmint-transfer-amount"
                            type="number"
                            value={usdmintTransferForm.amount}
                            onChange={(e) => setUsdmintTransferForm(p => ({ ...p, amount: e.target.value }))}
                            placeholder="25000000"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Subject (user/account or 'self')</Label>
                          <Input
                            data-testid="usdmint-transfer-subject"
                            value={usdmintTransferForm.subject}
                            onChange={(e) => setUsdmintTransferForm(p => ({ ...p, subject: e.target.value }))}
                            placeholder="self or user identifier"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Description</Label>
                          <Input
                            data-testid="usdmint-transfer-desc"
                            value={usdmintTransferForm.description}
                            onChange={(e) => setUsdmintTransferForm(p => ({ ...p, description: e.target.value }))}
                            placeholder="Transfer description..."
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button
                          data-testid="usdmint-transfer-btn"
                          onClick={handleUsdmintTransfer}
                          disabled={usdmintTransferring || !usdmintTransferForm.payment_type_id || !usdmintTransferForm.amount}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          {usdmintTransferring ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transferring...</> : <><DollarSign className="w-4 h-4 mr-2" /> Execute Transfer</>}
                        </Button>
                        <span className="text-xs text-muted-foreground ml-3">Executes directly on USDMint ledger via API</span>
                      </div>
                    </div>
                  </div>
                )}
              </Card>

              {/* Test Results */}
              {soveinTestResults && (
                <Card className="p-6" data-testid="sovein-test-results">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-blue-600 rounded-full" />
                    <h3 className="text-xl font-semibold">Sandbox Test Results</h3>
                    <Badge variant={soveinTestResults.passed === soveinTestResults.total_tests ? 'default' : 'secondary'}>
                      {soveinTestResults.passed}/{soveinTestResults.total_tests} Passed
                    </Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">#</th>
                          <th className="text-left py-2 px-3">Test Scenario</th>
                          <th className="text-left py-2 px-3">Endpoint</th>
                          <th className="text-left py-2 px-3">Status Code</th>
                          <th className="text-left py-2 px-3">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {soveinTestResults.results?.map((r, i) => (
                          <tr key={i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3">{i + 1}</td>
                            <td className="py-2 px-3 font-medium">{r.test}</td>
                            <td className="py-2 px-3 text-xs font-mono">{r.endpoint}</td>
                            <td className="py-2 px-3 text-xs font-mono">{r.status_code}</td>
                            <td className="py-2 px-3">
                              {r.passed ? (
                                <Badge variant="default" className="bg-green-600">PASS</Badge>
                              ) : (
                                <Badge variant="destructive">FAIL</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Biometric Sync */}
              <Card className="p-6" data-testid="sovein-biometric-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-rose-600 rounded-full" />
                  <h3 className="text-xl font-semibold">POST /sync/biometric — Biometric Data Ingestion</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Unit ID</Label>
                    <Input data-testid="sovein-unit-id" value={soveinBiometricForm.unit_id}
                      onChange={(e) => setSoveinBiometricForm(p => ({...p, unit_id: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Heart Rate</Label>
                    <Input data-testid="sovein-heart-rate" type="number" value={soveinBiometricForm.heart_rate}
                      onChange={(e) => setSoveinBiometricForm(p => ({...p, heart_rate: parseInt(e.target.value) || 0}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Oxygen Saturation</Label>
                    <Input data-testid="sovein-oxygen" type="number" value={soveinBiometricForm.oxygen_sat}
                      onChange={(e) => setSoveinBiometricForm(p => ({...p, oxygen_sat: parseInt(e.target.value) || 0}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Thermal Output</Label>
                    <Input data-testid="sovein-thermal" value={soveinBiometricForm.thermal_output}
                      onChange={(e) => setSoveinBiometricForm(p => ({...p, thermal_output: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Sovereign ID</Label>
                    <Input data-testid="sovein-sovereign-id" value={soveinBiometricForm.sovereign_id}
                      onChange={(e) => setSoveinBiometricForm(p => ({...p, sovereign_id: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Security Level</Label>
                    <Input data-testid="sovein-security-level" value={soveinBiometricForm.security_level}
                      onChange={(e) => setSoveinBiometricForm(p => ({...p, security_level: e.target.value}))} />
                  </div>
                </div>
                <Button className="mt-4 bg-rose-600 hover:bg-rose-700 text-white" onClick={handleSoveinBiometric} data-testid="sovein-sync-biometric-btn">
                  <Activity className="w-4 h-4 mr-2" /> Sync Biometric Data
                </Button>
              </Card>

              {/* Oracle Alpha */}
              <Card className="p-6" data-testid="sovein-oracle-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-amber-600 rounded-full" />
                  <h3 className="text-xl font-semibold">GET /oracle/alpha — Predictive Performance Data</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">Retrieve the predictive performance data for the David Barclay/Private Bank Trading Desk.</p>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleSoveinOracle} data-testid="sovein-oracle-btn">
                  <TrendingUp className="w-4 h-4 mr-2" /> Retrieve Oracle Alpha
                </Button>
              </Card>

              {/* Express Delivery */}
              <Card className="p-6" data-testid="sovein-delivery-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-cyan-600 rounded-full" />
                  <h3 className="text-xl font-semibold">POST /express/delivery — Logistics Activation</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Unit ID</Label>
                    <Input data-testid="sovein-delivery-unit-id" value={soveinDeliveryForm.unit_id}
                      onChange={(e) => setSoveinDeliveryForm(p => ({...p, unit_id: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Activation Status</Label>
                    <Input data-testid="sovein-delivery-status" value={soveinDeliveryForm.activation_status}
                      onChange={(e) => setSoveinDeliveryForm(p => ({...p, activation_status: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Delivery Address</Label>
                    <Input data-testid="sovein-delivery-address" value={soveinDeliveryForm.delivery_address} placeholder="Woolworths Express Hub, Accra"
                      onChange={(e) => setSoveinDeliveryForm(p => ({...p, delivery_address: e.target.value}))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Device ID</Label>
                    <Input data-testid="sovein-delivery-device" value={soveinDeliveryForm.client_device_id} placeholder="DEV-EZY-001"
                      onChange={(e) => setSoveinDeliveryForm(p => ({...p, client_device_id: e.target.value}))} />
                  </div>
                </div>
                <Button className="mt-4 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleSoveinDelivery} data-testid="sovein-delivery-btn">
                  <ArrowUpRight className="w-4 h-4 mr-2" /> Link Express Delivery
                </Button>
              </Card>

              {/* Webhook Architecture Info */}
              <Card className="p-6" data-testid="sovein-webhook-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-violet-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Webhook Architecture</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="font-semibold text-sm">on_payment_success</p>
                    <p className="text-xs text-muted-foreground mt-1">Triggers Oracle feed decryption + auto-debits USDMint ledger for the settlement amount.</p>
                    <p className="text-xs font-mono mt-2 text-emerald-600">USDMint auto-debit: ACTIVE</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="font-semibold text-sm">on_biometric_glitch</p>
                    <p className="text-xs text-muted-foreground mt-1">Triggers Glitch Finder Bot to scan for anomalies. Flags settlement for manual review if detected.</p>
                    <p className="text-xs font-mono mt-2 text-amber-600">Auto-flag: ENABLED</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="font-semibold text-sm">on_delivery_confirmed</p>
                    <p className="text-xs text-muted-foreground mt-1">Confirms settlement delivery. Updates transaction status and generates receipt.</p>
                    <p className="text-xs font-mono mt-2 text-blue-600">Receipt generation: ACTIVE</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4 font-mono">Webhook endpoint: POST /api/sovein/webhook | Auto-debit: USDMint EZYtransfer 63995800</p>
              </Card>

              {/* Transaction History */}
              <Card className="p-6" data-testid="sovein-history-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-emerald-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Transaction History</h3>
                </div>
                {soveinTxns.filter(t => t.type !== 'test_report').length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Date</th>
                          <th className="text-left py-2 px-3">Type</th>
                          <th className="text-left py-2 px-3">Amount</th>
                          <th className="text-left py-2 px-3">Recipient</th>
                          <th className="text-left py-2 px-3">Reference</th>
                          <th className="text-left py-2 px-3">Status</th>
                          <th className="text-left py-2 px-3">USDMint</th>
                          <th className="text-left py-2 px-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {soveinTxns.filter(t => t.type !== 'test_report').map((t, i) => (
                          <tr key={t.id || i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                            <td className="py-2 px-3">
                              <Badge variant="outline" className="capitalize">{t.type?.replace(/_/g, ' ')}</Badge>
                            </td>
                            <td className="py-2 px-3 font-semibold">
                              {t.amount ? `$${Number(t.amount).toLocaleString(undefined, {minimumFractionDigits: 2})}` : '—'}
                            </td>
                            <td className="py-2 px-3 text-sm">{t.recipient_name || t.unit_id || '—'}</td>
                            <td className="py-2 px-3 font-mono text-xs">{t.reference || '—'}</td>
                            <td className="py-2 px-3">
                              <Badge variant={t.status === 'success' ? 'default' : t.status === 'failed' ? 'destructive' : 'secondary'} className="capitalize">
                                {t.status || (t.success ? 'success' : 'failed')}
                              </Badge>
                            </td>
                            <td className="py-2 px-3">
                              {t.usdmint_debit ? (
                                <Badge variant={t.usdmint_debit.status === 'success' ? 'default' : 'destructive'} className="text-xs">
                                  {t.usdmint_debit.status === 'success' ? 'Debited' : 'Not Debited'}
                                </Badge>
                              ) : t.type === 'settlement' ? (
                                <Badge variant="secondary" className="text-xs">Pending</Badge>
                              ) : '—'}
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex gap-1">
                                {t.type === 'settlement' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-1 text-xs"
                                      onClick={() => handleSoveinStatusUpdate(t.id)}
                                      data-testid={`sovein-status-update-${t.id}`}
                                    >
                                      <RefreshCw className="w-3 h-3" /> Status
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-1 text-xs"
                                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sovein/receipt-page/${t.id}`, '_blank')}
                                      data-testid={`sovein-receipt-${t.id}`}
                                    >
                                      <FileText className="w-3 h-3" /> Receipt
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-1 text-xs"
                                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sovein/dtr/${t.id}`, '_blank')}
                                      data-testid={`sovein-dtr-${t.id}`}
                                    >
                                      <FileText className="w-3 h-3" /> DTR
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="gap-1 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                                      onClick={() => { setSoveinTraceRef(t.reference || t.id); handleSoveinTrace(t.reference || t.id); }}
                                      data-testid={`sovein-trace-${t.id}`}
                                    >
                                      <Eye className="w-3 h-3" /> Trace
                                    </Button>
                                    {t.usdmint_debit?.transaction_number && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-1 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                                        onClick={() => { setUsdmintLocateTx(t.usdmint_debit.transaction_number); handleUsdmintLocate(t.usdmint_debit.transaction_number); }}
                                        data-testid={`sovein-locate-${t.id}`}
                                      >
                                        <DollarSign className="w-3 h-3" /> Locate
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No transactions yet. Use the endpoints above to interact with SOVEIN.</p>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="settlement-overview">
            <div className="space-y-6" data-testid="settlement-overview-dashboard">
              {disabledProviders.length > 0 && (
                <Card className="p-4 border-amber-200 bg-amber-50/60" data-testid="disconnected-providers-banner">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-amber-500 rounded-full" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-amber-900">
                        Temporarily disconnected: {disabledProviders.map(p => p.toUpperCase()).join(', ')}
                      </p>
                      <p className="text-xs text-amber-800 mt-0.5">
                        Settlements for these providers are blocked (HTTP 503) and their tabs are hidden. Toggle them back on from the Provider Status panel below.
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Provider Status — live toggle, no restart needed */}
              <ProviderStatusPanel
                api={api}
                onChange={() => {
                  // refresh the disabledProviders state so tabs hide/show instantly
                  api.get('/provider-flags')
                    .then(res => setDisabledProviders((res.data?.disabled_providers || []).map(p => String(p).toLowerCase())))
                    .catch(() => {});
                }}
              />

              {/* Daily Settlement Summary — preview + send now (auto 06:00 UTC) */}
              <SettlementSummaryCard api={api} />

              {/* Header */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-10 bg-indigo-600 rounded-full" />
                    <div>
                      <h2 className="text-2xl font-semibold">Settlement Overview</h2>
                      <p className="text-sm text-muted-foreground">Cross-layer settlement health across ALT1, Mandiri, and SOVEIN</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={loadSettlementOverview} disabled={settlementLoading} data-testid="settlement-refresh-btn">
                      <RefreshCw className={`w-4 h-4 mr-2 ${settlementLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="settlement-reconcile-btn"
                      onClick={async () => {
                        if (!window.confirm('Reconcile ledger?\n\nThis will:\n• Reverse all Bridge transfers\n• Delete TAP test charges ≥ $1M\n• Delete SOVEIN $0/test_report rows\n• Ensure the $49M ALT1 retroactive record exists\n\nSafe to re-run (idempotent).')) return;
                        try {
                          const r = await api.post('/admin/reconcile');
                          const a = r.data.actions || {};
                          const after = r.data.after || {};
                          alert(`Reconcile OK\n\nBridge reversed: ${a.bridge_reversed}\nTAP test deleted: ${a.tap_test_deleted}\nSOVEIN garbage deleted: ${a.sovein_garbage_deleted}\nALT1 seed created: ${a.alt1_seed_created}\n\nTotal transactions: ${after.total_transactions}\nActive volume: $${(after.active_volume_usd || 0).toLocaleString()}`);
                          loadSettlementOverview();
                          loadAdminData();
                        } catch (e) {
                          alert('Reconcile failed: ' + (e.response?.data?.detail || e.message));
                        }
                      }}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50">
                      <RefreshCw className="w-4 h-4 mr-2" /> Reconcile Ledger
                    </Button>
                    <Button variant="outline" size="sm" data-testid="download-integration-guide"
                      onClick={async () => {
                        try {
                          const res = await api.get('/admin/integration-guide-pdf', { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'EzyTransfer_Integration_Guide_v2.pdf';
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (e) { console.error('PDF download failed', e); }
                      }}>
                      <FileText className="w-4 h-4 mr-2" /> Integration Guide PDF
                    </Button>
                  </div>
                </div>

                {/* Aggregate Totals */}
                {settlementOverview && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="border rounded-lg p-5 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/20 dark:to-background">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Total Transactions (All Layers)</p>
                      <p className="text-3xl font-bold mt-1" data-testid="settlement-total-txns">{settlementOverview.totals?.total_transactions || 0}</p>
                    </div>
                    <div className="border rounded-lg p-5 bg-gradient-to-br from-green-50 to-white dark:from-green-950/20 dark:to-background">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Total Settlement Volume</p>
                      <p className="text-3xl font-bold mt-1" data-testid="settlement-total-volume">${(settlementOverview.totals?.total_volume_usd || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    </div>
                  </div>
                )}
              </Card>

              {/* Layer Cards */}
              {settlementOverview?.layers && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {settlementOverview.layers.map((layer) => {
                    const colorMap = { ALT1: 'blue', Mandiri: 'orange', SOVEIN: 'emerald' };
                    const color = colorMap[layer.provider] || 'gray';
                    const healthColor = layer.health === 'connected' ? 'text-green-600' : layer.health === 'unavailable' ? 'text-red-500' : 'text-yellow-500';
                    const healthIcon = layer.health === 'connected' ? <Wifi className="w-4 h-4 text-green-500" /> : <WifiOff className="w-4 h-4 text-red-400" />;

                    return (
                      <Card key={layer.provider} className="p-6" data-testid={`settlement-layer-${layer.provider.toLowerCase()}`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-1 h-8 bg-${color}-600 rounded-full`} />
                          <h3 className="text-lg font-bold">{layer.provider}</h3>
                          <div className="ml-auto flex items-center gap-1">
                            {healthIcon}
                            <span className={`text-xs font-medium capitalize ${healthColor}`}>{layer.health}</span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b">
                            <span className="text-sm text-muted-foreground">Total Transactions</span>
                            <span className="font-semibold">{layer.total_transactions}</span>
                          </div>

                          {layer.total_volume !== undefined && (
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-sm text-muted-foreground">Volume</span>
                              <span className="font-semibold">${Number(layer.total_volume).toLocaleString()}</span>
                            </div>
                          )}

                          {layer.successful !== undefined && (
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-sm text-muted-foreground">Successful</span>
                              <Badge variant="default" className="bg-green-600">{layer.successful}</Badge>
                            </div>
                          )}

                          {layer.failed !== undefined && (
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-sm text-muted-foreground">Failed</span>
                              <Badge variant={layer.failed > 0 ? 'destructive' : 'secondary'}>{layer.failed}</Badge>
                            </div>
                          )}

                          {layer.quotes !== undefined && (
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-sm text-muted-foreground">Quotes</span>
                              <span className="font-semibold">{layer.quotes}</span>
                            </div>
                          )}

                          {layer.transfers !== undefined && (
                            <div className="flex justify-between items-center py-2 border-b">
                              <span className="text-sm text-muted-foreground">Transfers</span>
                              <span className="font-semibold">{layer.transfers}</span>
                            </div>
                          )}

                          {layer.by_type && Object.keys(layer.by_type).length > 0 && (
                            <div className="pt-2">
                              <p className="text-xs text-muted-foreground mb-2">By Type</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(layer.by_type).map(([type, count]) => (
                                  <Badge key={type} variant="outline" className="text-xs capitalize">
                                    {type.replace(/_/g, ' ')}: {count}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Recent Activity */}
                        {layer.recent?.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Recent Activity</p>
                            {layer.recent.slice(0, 3).map((tx, i) => (
                              <div key={i} className="flex justify-between items-center py-1.5 text-xs">
                                <span className="text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</span>
                                <Badge variant="outline" className="text-xs capitalize">{tx.type?.replace(/_/g, ' ') || tx.status || '—'}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Loading / Empty state */}
              {!settlementOverview && !settlementLoading && (
                <Card className="p-8 text-center">
                  <p className="text-muted-foreground">Click "Refresh" to load settlement data across all layers.</p>
                </Card>
              )}
              {settlementLoading && (
                <Card className="p-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  <p className="text-muted-foreground">Loading settlement overview...</p>
                </Card>
              )}

              {/* Generated timestamp */}
              {settlementOverview?.generated_at && (
                <p className="text-xs text-right text-muted-foreground">
                  Last updated: {new Date(settlementOverview.generated_at).toLocaleString()}
                </p>
              )}
            </div>
          </TabsContent>

          {/* ============ M-PESA TAB ============ */}
          <TabsContent value="mpesa">
            <div className="space-y-6" data-testid="mpesa-dashboard">
              {/* Header */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-10 bg-green-500 rounded-full" />
                    <div>
                      <h2 className="text-2xl font-semibold">M-pesa (Safaricom Ethiopia)</h2>
                      <p className="text-sm text-muted-foreground">Mobile money send & receive via Safaricom Ethiopia</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadMpesaData} disabled={mpesaLoading} data-testid="mpesa-refresh-btn">
                      <RefreshCw className={`w-4 h-4 mr-2 ${mpesaLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                    <Button size="sm" onClick={runMpesaTests} disabled={mpesaTesting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="mpesa-run-tests-btn">
                      {mpesaTesting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running...</> : <><Activity className="w-4 h-4 mr-2" /> Run Sandbox Tests</>}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">API Status</p>
                    <div className="flex items-center gap-2 mt-2" data-testid="mpesa-api-status">
                      {mpesaHealth?.status === 'connected' ? (
                        <><Wifi className="w-5 h-5 text-green-500" /><span className="font-semibold text-green-600">Connected</span></>
                      ) : mpesaHealth?.status === 'error' ? (
                        <><WifiOff className="w-5 h-5 text-red-500" /><span className="font-semibold text-red-600">Error</span></>
                      ) : (
                        <><WifiOff className="w-5 h-5 text-gray-400" /><span className="font-semibold text-gray-500">Unknown</span></>
                      )}
                    </div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Shortcode</p>
                    <p className="text-xl font-bold mt-2 font-mono" data-testid="mpesa-shortcode">{mpesaHealth?.shortcode || '—'}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Transactions</p>
                    <p className="text-2xl font-bold mt-2" data-testid="mpesa-txn-count">{mpesaTxns.filter(t => t.type !== 'test_report' && t.type !== 'webhook').length}</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wider">Environment</p>
                    <Badge variant="outline" className="mt-2">{mpesaHealth?.sandbox ? 'Sandbox' : 'Production'}</Badge>
                  </div>
                </div>
              </Card>

              {/* Send / Receive Payment */}
              <Card className="p-6" data-testid="mpesa-payment-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-green-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Send / Receive Payment</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      data-testid="mpesa-phone"
                      value={mpesaSendForm.phone_number}
                      onChange={(e) => setMpesaSendForm(p => ({ ...p, phone_number: e.target.value }))}
                      placeholder="251911234567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount (ETB)</Label>
                    <Input
                      data-testid="mpesa-amount"
                      type="number"
                      value={mpesaSendForm.amount}
                      onChange={(e) => setMpesaSendForm(p => ({ ...p, amount: e.target.value }))}
                      placeholder="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Direction</Label>
                    <Select value={mpesaSendForm.direction} onValueChange={(v) => setMpesaSendForm(p => ({ ...p, direction: v }))}>
                      <SelectTrigger data-testid="mpesa-direction">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="send">Send (B2C)</SelectItem>
                        <SelectItem value="receive">Receive (C2B)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Remarks</Label>
                    <Input
                      data-testid="mpesa-remarks"
                      value={mpesaSendForm.remarks}
                      onChange={(e) => setMpesaSendForm(p => ({ ...p, remarks: e.target.value }))}
                      placeholder="Optional note..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex items-center gap-4">
                  <Button
                    data-testid="mpesa-send-btn"
                    onClick={handleMpesaSend}
                    disabled={mpesaSending || !mpesaSendForm.phone_number || !mpesaSendForm.amount}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {mpesaSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><DollarSign className="w-4 h-4 mr-2" /> {mpesaSendForm.direction === 'send' ? 'Send Money (B2C)' : 'Request Payment (C2B)'}</>}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    via Safaricom Ethiopia {mpesaSendForm.direction === 'send' ? 'B2C' : 'C2B'} API
                  </span>
                </div>
              </Card>

              {/* Test Results */}
              {mpesaTestResults && (
                <Card className="p-6" data-testid="mpesa-test-results">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-blue-600 rounded-full" />
                    <h3 className="text-xl font-semibold">Sandbox Test Results</h3>
                    <Badge variant={mpesaTestResults.passed === mpesaTestResults.total_tests ? 'default' : 'secondary'}>
                      {mpesaTestResults.passed}/{mpesaTestResults.total_tests} Passed
                    </Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">#</th>
                          <th className="text-left py-2 px-3">Test Scenario</th>
                          <th className="text-left py-2 px-3">Endpoint</th>
                          <th className="text-left py-2 px-3">Status Code</th>
                          <th className="text-left py-2 px-3">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mpesaTestResults.results?.map((r, i) => (
                          <tr key={i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3">{i + 1}</td>
                            <td className="py-2 px-3 font-medium">{r.test}</td>
                            <td className="py-2 px-3 text-xs font-mono">{r.endpoint}</td>
                            <td className="py-2 px-3 text-xs font-mono">{r.status_code}</td>
                            <td className="py-2 px-3">
                              {r.passed ? (
                                <Badge variant="default" className="bg-green-600">PASS</Badge>
                              ) : (
                                <Badge variant="destructive">FAIL</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Validate Customer */}
              <Card className="p-6" data-testid="mpesa-validate-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-cyan-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Validate Customer</h3>
                </div>
                <div className="flex gap-4 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>Phone Number</Label>
                    <Input data-testid="mpesa-validate-phone" value={mpesaValidatePhone} onChange={(e) => setMpesaValidatePhone(e.target.value)} placeholder="251911234567" />
                  </div>
                  <Button onClick={handleMpesaValidate} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="mpesa-validate-btn">
                    <UserCheck className="w-4 h-4 mr-2" /> Validate
                  </Button>
                </div>
              </Card>

              {/* Balance & Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6" data-testid="mpesa-balance-section">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-amber-600 rounded-full" />
                    <h3 className="text-xl font-semibold">Account Balance</h3>
                  </div>
                  <Button onClick={handleMpesaBalance} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="mpesa-balance-btn">
                    <DollarSign className="w-4 h-4 mr-2" /> Check Balance
                  </Button>
                </Card>

                <Card className="p-6" data-testid="mpesa-status-section">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-violet-600 rounded-full" />
                    <h3 className="text-xl font-semibold">Transaction Status</h3>
                  </div>
                  <div className="flex gap-4 items-end">
                    <div className="flex-1 space-y-2">
                      <Label>Conversation ID</Label>
                      <Input data-testid="mpesa-status-id" value={mpesaStatusId} onChange={(e) => setMpesaStatusId(e.target.value)} placeholder="Conversation ID..." />
                    </div>
                    <Button onClick={handleMpesaStatus} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="mpesa-status-btn">
                      <Clock className="w-4 h-4 mr-2" /> Query
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Reversal */}
              <Card className="p-6" data-testid="mpesa-reversal-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-red-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Transaction Reversal</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Transaction ID</Label>
                    <Input data-testid="mpesa-reversal-txid" value={mpesaReversalForm.transaction_id} onChange={(e) => setMpesaReversalForm(p => ({ ...p, transaction_id: e.target.value }))} placeholder="Transaction ID" />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input data-testid="mpesa-reversal-amount" type="number" value={mpesaReversalForm.amount} onChange={(e) => setMpesaReversalForm(p => ({ ...p, amount: e.target.value }))} placeholder="100" />
                  </div>
                  <div className="space-y-2">
                    <Label>Remarks</Label>
                    <Input data-testid="mpesa-reversal-remarks" value={mpesaReversalForm.remarks} onChange={(e) => setMpesaReversalForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Reversal" />
                  </div>
                </div>
                <Button onClick={handleMpesaReversal} className="mt-4 bg-red-600 hover:bg-red-700 text-white" disabled={!mpesaReversalForm.transaction_id || !mpesaReversalForm.amount} data-testid="mpesa-reversal-btn">
                  <AlertTriangle className="w-4 h-4 mr-2" /> Submit Reversal
                </Button>
              </Card>

              {/* Transaction History */}
              <Card className="p-6" data-testid="mpesa-txn-history">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-gray-600 rounded-full" />
                  <h3 className="text-xl font-semibold">Transaction History</h3>
                  <Badge variant="outline">{mpesaTxns.filter(t => t.type !== 'test_report').length} records</Badge>
                </div>
                {mpesaTxns.filter(t => t.type !== 'test_report').length === 0 ? (
                  <p className="text-muted-foreground text-center py-6">No M-Pesa transactions yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Time</th>
                          <th className="text-left py-2 px-3">Type</th>
                          <th className="text-left py-2 px-3">Phone</th>
                          <th className="text-left py-2 px-3">Amount</th>
                          <th className="text-left py-2 px-3">Direction</th>
                          <th className="text-left py-2 px-3">Status</th>
                          <th className="text-left py-2 px-3">User</th>
                          <th className="text-left py-2 px-3">Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mpesaTxns.filter(t => t.type !== 'test_report').slice(0, 50).map((tx, i) => (
                          <tr key={tx.id || i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3 text-xs">{tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}</td>
                            <td className="py-2 px-3"><Badge variant="outline" className="text-xs capitalize">{tx.type?.replace(/_/g, ' ')}</Badge></td>
                            <td className="py-2 px-3 font-mono text-xs">{tx.phone_number || '—'}</td>
                            <td className="py-2 px-3 font-mono font-semibold">{tx.amount ? `ETB ${tx.amount}` : '—'}</td>
                            <td className="py-2 px-3">
                              {tx.direction === 'send' ? <Badge className="bg-blue-500 text-white text-xs">Send</Badge> : tx.direction === 'receive' ? <Badge className="bg-green-500 text-white text-xs">Receive</Badge> : <span className="text-xs text-muted-foreground">{tx.type || '—'}</span>}
                            </td>
                            <td className="py-2 px-3">
                              {tx.success ? <Badge variant="default" className="bg-green-600 text-xs">Success</Badge> : <Badge variant="destructive" className="text-xs">Failed</Badge>}
                            </td>
                            <td className="py-2 px-3 text-xs">{tx.user_email || '—'}</td>
                            <td className="py-2 px-3">
                              {(tx.type === 'b2c' || tx.type === 'c2b' || tx.type === 'reversal') && (
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    data-testid={`mpesa-receipt-${tx.id}`}
                                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/mpesa/receipt-page/${tx.id}`, '_blank')}
                                  >
                                    <FileText className="w-3 h-3 mr-1" /> PDF
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs text-green-600"
                                    data-testid={`mpesa-deliver-${tx.id}`}
                                    onClick={() => { setDeliveryTx({ ...tx, provider: 'mpesa' }); setDeliveryOpen(true); }}
                                  >
                                    <Mail className="w-3 h-3 mr-1" /> Send
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs text-green-700"
                                    data-testid={`mpesa-wa-share-${tx.id}`}
                                    onClick={() => openWhatsAppShare('mpesa', tx.id)}
                                  >
                                    <Phone className="w-3 h-3 mr-1" /> WA
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ============ M-PESA RECONCILIATION TAB ============ */}
          <TabsContent value="mpesa-recon">
            <div className="space-y-6" data-testid="mpesa-recon-dashboard">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-10 bg-green-500 rounded-full" />
                    <div>
                      <h2 className="text-2xl font-semibold">M-pesa Reconciliation</h2>
                      <p className="text-sm text-muted-foreground">Transaction reconciliation and analytics</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadMpesaRecon} disabled={mpesaReconLoading} data-testid="mpesa-recon-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${mpesaReconLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>

                {mpesaReconLoading && (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    <p className="text-muted-foreground">Loading reconciliation data...</p>
                  </div>
                )}

                {mpesaReconData && (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="border rounded-lg p-4">
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Total Transactions</p>
                        <p className="text-2xl font-bold mt-1" data-testid="mpesa-recon-total">{mpesaReconData.summary?.total_transactions || 0}</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Successful</p>
                        <p className="text-2xl font-bold mt-1 text-green-600" data-testid="mpesa-recon-success">{mpesaReconData.summary?.successful || 0}</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Failed</p>
                        <p className="text-2xl font-bold mt-1 text-red-600" data-testid="mpesa-recon-failed">{mpesaReconData.summary?.failed || 0}</p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Net Volume</p>
                        <p className="text-2xl font-bold mt-1 font-mono" data-testid="mpesa-recon-volume">ETB {(mpesaReconData.summary?.net_volume || 0).toLocaleString()}</p>
                      </div>
                    </div>

                    {/* Type Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="border rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">B2C (Send)</p>
                        <p className="text-lg font-bold">{mpesaReconData.summary?.b2c_count || 0}</p>
                        <p className="text-xs font-mono text-muted-foreground">ETB {(mpesaReconData.summary?.b2c_volume || 0).toLocaleString()}</p>
                      </div>
                      <div className="border rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">C2B (Receive)</p>
                        <p className="text-lg font-bold">{mpesaReconData.summary?.c2b_count || 0}</p>
                        <p className="text-xs font-mono text-muted-foreground">ETB {(mpesaReconData.summary?.c2b_volume || 0).toLocaleString()}</p>
                      </div>
                      <div className="border rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Reversals</p>
                        <p className="text-lg font-bold">{mpesaReconData.summary?.reversal_count || 0}</p>
                        <p className="text-xs font-mono text-muted-foreground">ETB {(mpesaReconData.summary?.reversal_volume || 0).toLocaleString()}</p>
                      </div>
                      <div className="border rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Validations</p>
                        <p className="text-lg font-bold">{mpesaReconData.summary?.validation_count || 0}</p>
                      </div>
                    </div>

                    {/* Transaction Table */}
                    {mpesaReconData.transactions?.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3">Time</th>
                              <th className="text-left py-2 px-3">Type</th>
                              <th className="text-left py-2 px-3">Phone</th>
                              <th className="text-left py-2 px-3">Amount</th>
                              <th className="text-left py-2 px-3">Direction</th>
                              <th className="text-left py-2 px-3">Status</th>
                              <th className="text-left py-2 px-3">User</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mpesaReconData.transactions.map((tx, i) => (
                              <tr key={tx.id || i} className="border-b hover:bg-muted/30">
                                <td className="py-2 px-3 text-xs">{tx.created_at ? new Date(tx.created_at).toLocaleString() : '—'}</td>
                                <td className="py-2 px-3"><Badge variant="outline" className="text-xs capitalize">{tx.type?.replace(/_/g, ' ')}</Badge></td>
                                <td className="py-2 px-3 font-mono text-xs">{tx.phone_number || '—'}</td>
                                <td className="py-2 px-3 font-mono font-semibold">{tx.amount ? `ETB ${tx.amount}` : '—'}</td>
                                <td className="py-2 px-3">
                                  {tx.direction === 'send' ? <Badge className="bg-blue-500 text-white text-xs">Send</Badge> : tx.direction === 'receive' ? <Badge className="bg-green-500 text-white text-xs">Receive</Badge> : <span className="text-xs text-muted-foreground">{tx.type || '—'}</span>}
                                </td>
                                <td className="py-2 px-3">
                                  {tx.success ? <Badge variant="default" className="bg-green-600 text-xs">Success</Badge> : <Badge variant="destructive" className="text-xs">Failed</Badge>}
                                </td>
                                <td className="py-2 px-3 text-xs">{tx.user_email || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {!mpesaReconData && !mpesaReconLoading && (
                  <p className="text-muted-foreground text-center py-8">Click "Refresh" to load reconciliation data.</p>
                )}

                {mpesaReconData?.generated_at && (
                  <p className="text-xs text-right text-muted-foreground mt-4">
                    Last updated: {new Date(mpesaReconData.generated_at).toLocaleString()}
                  </p>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ═══════════════ TAP TAB ═══════════════ */}
          <TabsContent value="tap">
            <div className="space-y-6" data-testid="tap-tab">
              {/* Header + Health */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-teal-600 rounded-full" />
                    <h2 className="text-2xl font-semibold">TAP</h2>
                    <Badge variant="outline" className="text-teal-600 border-teal-300">Tap Payments</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    {tapHealth && (
                      <Badge className={tapHealth.status === 'connected' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {tapHealth.status === 'connected' ? 'Connected' : 'Disconnected'}
                      </Badge>
                    )}
                    <Button size="sm" variant="outline" onClick={loadTapData} disabled={tapLoading} data-testid="tap-refresh-btn">
                      {tapLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  TAP settlement layer powered by Tap Payments API. Create settlements, track charges, and generate receipts.
                </p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button
                    size="sm"
                    className="gap-1 text-xs bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="tap-quick-test-btn"
                    onClick={handleTapQuickTest}
                    disabled={tapQuickTesting}
                  >
                    {tapQuickTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                    Quick Test $1
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                    data-testid="tap-doc-pdf-btn"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/tap-integration.pdf`, '_blank')}
                  >
                    <FileText className="w-3 h-3" /> API Notes (PDF)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="tap-session-updates-pdf-btn"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/tap-session-updates.pdf`, '_blank')}
                  >
                    <FileText className="w-3 h-3" /> TAP Session Updates (PDF)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="tap-doc-md-btn"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/tap-integration.md`, '_blank')}
                  >
                    <FileText className="w-3 h-3" /> API Notes (Markdown)
                  </Button>
                </div>

                {/* Quick trace by Charge ID / Reference */}
                <div className="flex gap-2 mt-3 flex-wrap items-center">
                  <Input
                    placeholder="Trace by Charge ID (chg_...) or Reference (EZY-TAP-...)"
                    className="w-96 text-sm h-9"
                    data-testid="tap-header-trace-input"
                    value={tapHeaderTraceId}
                    onChange={(e) => setTapHeaderTraceId(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleTapHeaderTrace(); }}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="tap-header-trace-btn"
                    onClick={handleTapHeaderTrace}
                    disabled={tapTracing}
                  >
                    <Eye className="w-3 h-3" /> {tapTracing ? 'Tracing…' : 'Trace'}
                  </Button>
                </div>
              </Card>

              <TapPayouts />

              {/* Settlement Form */}
              <Card className="p-6" data-testid="tap-settle-form">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-teal-500 rounded-full" />
                  <h3 className="text-xl font-semibold">New Settlement</h3>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input data-testid="tap-amount" type="number" value={tapSettleForm.amount}
                      onChange={(e) => setTapSettleForm(p => ({ ...p, amount: e.target.value }))}
                      placeholder="1.00" />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <select
                      data-testid="tap-currency"
                      value={tapSettleForm.currency}
                      onChange={(e) => setTapSettleForm(p => ({ ...p, currency: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {['USD','SAR','AED','KWD','BHD','QAR','OMR','EGP','JOD','EUR','GBP','INR'].map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Recipient Name</Label>
                    <Input data-testid="tap-recipient" value={tapSettleForm.recipient_name}
                      onChange={(e) => setTapSettleForm(p => ({ ...p, recipient_name: e.target.value }))}
                      placeholder="Recipient name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Recipient Account</Label>
                    <Input data-testid="tap-account" value={tapSettleForm.recipient_account}
                      onChange={(e) => setTapSettleForm(p => ({ ...p, recipient_account: e.target.value }))}
                      placeholder="Account number" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input data-testid="tap-email" value={tapSettleForm.email}
                      onChange={(e) => setTapSettleForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="recipient@email.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone (country + number)</Label>
                    <div className="flex gap-2">
                      <Input data-testid="tap-phone-country" value={tapSettleForm.phone_country}
                        onChange={(e) => setTapSettleForm(p => ({ ...p, phone_country: e.target.value }))}
                        placeholder="966" className="w-20" />
                      <Input data-testid="tap-phone-number" value={tapSettleForm.phone_number}
                        onChange={(e) => setTapSettleForm(p => ({ ...p, phone_number: e.target.value }))}
                        placeholder="500000000" className="flex-1" />
                    </div>
                  </div>
                  <div className="col-span-3 space-y-2">
                    <Label>Description</Label>
                    <Input data-testid="tap-desc" value={tapSettleForm.description}
                      onChange={(e) => setTapSettleForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Settlement description..." />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
                    <input type="checkbox" data-testid="tap-record-only"
                      checked={tapSettleForm.record_only}
                      onChange={(e) => setTapSettleForm(p => ({ ...p, record_only: e.target.checked }))}
                      className="accent-teal-500" />
                    Record locally only (skip Tap API call)
                  </label>
                  <Button data-testid="tap-settle-btn" onClick={handleTapSettle} disabled={tapSettling}
                    className="bg-teal-600 hover:bg-teal-700 text-white">
                    {tapSettling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><DollarSign className="w-4 h-4 mr-2" /> Send Settlement</>}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Tip: "Record locally only" logs the settlement to the ledger without calling Tap — useful for retroactive entries or offline reconciliation.
                </p>
              </Card>

              {/* Trace Result */}
              {tapTraceResult && (
                <Card className="p-6" data-testid="tap-trace-result">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-amber-500 rounded-full" />
                    <h3 className="text-xl font-semibold">Trace Result</h3>
                    <Button size="sm" variant="ghost" onClick={() => setTapTraceResult(null)}>Clear</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {tapTraceResult.local_record && (
                      <div className="bg-slate-50 border rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Local Record</p>
                        <div className="space-y-1 text-sm">
                          <div><span className="text-muted-foreground">Reference:</span> <span className="font-mono font-medium">{tapTraceResult.local_record.reference}</span></div>
                          <div><span className="text-muted-foreground">Amount:</span> <span className="font-medium">${Number(tapTraceResult.local_record.amount || 0).toLocaleString()}</span></div>
                          <div><span className="text-muted-foreground">Status:</span> <span className="font-medium">{tapTraceResult.local_record.status}</span></div>
                          <div><span className="text-muted-foreground">Charge ID:</span> <span className="font-mono text-xs">{tapTraceResult.local_record.charge_id}</span></div>
                          <div><span className="text-muted-foreground">Recipient:</span> <span className="font-medium">{tapTraceResult.local_record.recipient_name}</span></div>
                        </div>
                      </div>
                    )}
                    {tapTraceResult.tap_trace && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wide text-teal-700 mb-2">Tap Payments Trace</p>
                        <div className="space-y-1 text-sm">
                          <div><span className="text-muted-foreground">Tap Status:</span> <span className="font-medium">{tapTraceResult.tap_trace.tap_status}</span></div>
                          <div><span className="text-muted-foreground">Amount:</span> <span className="font-medium">{tapTraceResult.tap_trace.amount} {tapTraceResult.tap_trace.currency}</span></div>
                          <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{tapTraceResult.tap_trace.customer?.name}</span></div>
                          <div><span className="text-muted-foreground">Source:</span> <span className="font-medium">{tapTraceResult.tap_trace.source?.type || 'N/A'}</span></div>
                          {tapTraceResult.tap_trace.trace && (
                            <details className="mt-2">
                              <summary className="text-xs cursor-pointer text-teal-600">Full Trace Details</summary>
                              <pre className="mt-1 bg-white border rounded p-2 text-xs overflow-auto max-h-48 font-mono">
                                {JSON.stringify(tapTraceResult.tap_trace.trace, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Transactions Table */}
              <Card className="p-6" data-testid="tap-transactions">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-teal-400 rounded-full" />
                  <h3 className="text-xl font-semibold">Transactions</h3>
                  <Badge variant="outline">{tapTransactions.length} records</Badge>
                </div>
                {tapTransactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No TAP transactions yet. Create a settlement above.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3">Date</th>
                          <th className="text-left py-2 px-3">Reference</th>
                          <th className="text-left py-2 px-3">Recipient</th>
                          <th className="text-right py-2 px-3">Amount</th>
                          <th className="text-left py-2 px-3">Status</th>
                          <th className="text-right py-2 px-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tapTransactions.map((t) => (
                          <tr key={t.id} className="border-b hover:bg-slate-50">
                            <td className="py-2 px-3 text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : 'N/A'}</td>
                            <td className="py-2 px-3 font-mono text-xs">{t.reference}</td>
                            <td className="py-2 px-3">{t.recipient_name}</td>
                            <td className="py-2 px-3 text-right font-semibold">${Number(t.amount || 0).toLocaleString()} {t.currency}</td>
                            <td className="py-2 px-3">
                              <Badge className={
                                t.status === 'CAPTURED' ? 'bg-emerald-100 text-emerald-700' :
                                t.status === 'INITIATED' ? 'bg-blue-100 text-blue-700' :
                                t.status === 'ABANDONED' ? 'bg-gray-100 text-gray-700' :
                                'bg-amber-100 text-amber-700'
                              }>{t.status}</Badge>
                            </td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex gap-1 justify-end flex-wrap">
                                <Button size="sm" variant="outline" className="gap-1 text-xs"
                                  onClick={() => handleTapStatusUpdate(t.id)} data-testid={`tap-status-${t.id}`}>
                                  <RefreshCw className="w-3 h-3" /> Status
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tap/receipt-page/${t.id}`, '_blank')}
                                  data-testid={`tap-receipt-${t.id}`}>
                                  <FileText className="w-3 h-3" /> Receipt
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tap/dtr/${t.id}`, '_blank')}
                                  data-testid={`tap-dtr-${t.id}`}>
                                  <FileText className="w-3 h-3" /> DTR
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs text-teal-700 border-teal-300 hover:bg-teal-50"
                                  onClick={() => handleTapTrace(t.id)} data-testid={`tap-trace-${t.id}`}>
                                  <Eye className="w-3 h-3" /> Trace
                                </Button>
                                {t.payment_url && (
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                                    onClick={() => window.open(t.payment_url, '_blank')} data-testid={`tap-pay-${t.id}`}>
                                    <ArrowRight className="w-3 h-3" /> Pay
                                  </Button>
                                )}
                                {String(t.status || '').toUpperCase() === 'CAPTURED' && (
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-amber-800 border-amber-300 hover:bg-amber-50"
                                    onClick={() => handleSwiftFromTap(t)} data-testid={`tap-swift-${t.id}`}>
                                    <ArrowRight className="w-3 h-3" /> SWIFT
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ═══════════════ FIN.COM TAB (light theme) ═══════════════ */}
          <TabsContent value="fincom">
            <div className="space-y-6" data-testid="fincom-tab">
              {/* Header + Health */}
              <Card className="p-6 bg-sky-50/40 border-sky-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-sky-400 rounded-full" />
                    <h2 className="text-2xl font-semibold text-sky-900">FIN.COM</h2>
                    <Badge variant="outline" className="text-sky-700 border-sky-300 bg-white">Orchestration API</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    {fincomHealth && (
                      <Badge className={fincomHealth.status === 'connected' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}>
                        {fincomHealth.status === 'connected' ? 'Connected' : (fincomHealth.status || 'Unknown')}
                      </Badge>
                    )}
                    <Button size="sm" variant="outline" onClick={loadFincomData} disabled={fincomLoading} data-testid="fincom-refresh-btn"
                      className="border-sky-200 text-sky-700 hover:bg-sky-100">
                      {fincomLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-sky-800/70">
                  FIN.COM external settlement layer powered by the Fin.com Orchestration API. Send transfer payouts, track transactions, generate receipts and DTRs.
                </p>
                {fincomHealth?.access_token_ttl && (
                  <p className="text-xs text-sky-700/70 mt-2 font-mono">
                    Token TTL: {fincomHealth.access_token_ttl}
                  </p>
                )}
              </Card>

              {/* Settlement Form */}
              <Card className="p-6 bg-white border-sky-100" data-testid="fincom-settle-form">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-sky-300 rounded-full" />
                  <h3 className="text-xl font-semibold text-sky-900">New Settlement</h3>
                  <Badge className="bg-amber-50 text-amber-800 border border-amber-200">MINT source required</Badge>
                </div>

                <MintSourcePicker
                  accounts={mintAccounts}
                  selectedId={fincomSettleForm.mint_account_id}
                  onSelect={(id) => setFincomSettleForm(p => ({ ...p, mint_account_id: id }))}
                  providerLabel="FIN.COM"
                  onRefresh={loadMintAccounts}
                  amount={fincomSettleForm.amount}
                  testIdPrefix="fincom-mint-source"
                />

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700">Amount (USD)</Label>
                    <Input data-testid="fincom-amount" type="number" value={fincomSettleForm.amount}
                      onChange={(e) => setFincomSettleForm(p => ({ ...p, amount: e.target.value }))}
                      placeholder="100" className="border-sky-200 focus-visible:ring-sky-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Recipient Name</Label>
                    <Input data-testid="fincom-recipient" value={fincomSettleForm.recipient_name}
                      onChange={(e) => setFincomSettleForm(p => ({ ...p, recipient_name: e.target.value }))}
                      placeholder="Recipient name" className="border-sky-200 focus-visible:ring-sky-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Recipient Account</Label>
                    <Input data-testid="fincom-account" value={fincomSettleForm.recipient_account}
                      onChange={(e) => setFincomSettleForm(p => ({ ...p, recipient_account: e.target.value }))}
                      placeholder="Account number" className="border-sky-200 focus-visible:ring-sky-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700">Beneficiary ID (optional)</Label>
                    <Input data-testid="fincom-beneficiary" value={fincomSettleForm.beneficiary_id}
                      onChange={(e) => setFincomSettleForm(p => ({ ...p, beneficiary_id: e.target.value }))}
                      placeholder="UUID from Fin.com" className="border-sky-200 focus-visible:ring-sky-300 font-mono text-xs" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-slate-700">Description / Remarks</Label>
                    <Input data-testid="fincom-desc" value={fincomSettleForm.description}
                      onChange={(e) => setFincomSettleForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Settlement description..." className="border-sky-200 focus-visible:ring-sky-300" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
                    <input type="checkbox" data-testid="fincom-record-only"
                      checked={fincomSettleForm.record_only}
                      onChange={(e) => setFincomSettleForm(p => ({ ...p, record_only: e.target.checked }))}
                      className="accent-sky-500" />
                    Record locally only (no API call)
                  </label>
                  <Button data-testid="fincom-settle-btn" onClick={handleFincomSettle} disabled={fincomSettling}
                    className="bg-sky-500 hover:bg-sky-600 text-white">
                    {fincomSettling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><DollarSign className="w-4 h-4 mr-2" /> Send Settlement</>}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Tip: Fin.com requires a valid <span className="font-mono">beneficiary_id</span> created with <span className="font-mono">auto_settlement: true</span>. Without it, use "Record locally only" for ledger-only entries.
                </p>
              </Card>

              {/* Trace Result */}
              {fincomTraceResult && (
                <Card className="p-6 bg-white border-sky-100" data-testid="fincom-trace-result">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-amber-400 rounded-full" />
                    <h3 className="text-xl font-semibold text-slate-800">Trace Result</h3>
                    <Button size="sm" variant="ghost" onClick={() => setFincomTraceResult(null)}>Clear</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {fincomTraceResult.local_record && (
                      <div className="bg-slate-50 border rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Local Record</p>
                        <div className="space-y-1 text-sm">
                          <div><span className="text-muted-foreground">Reference:</span> <span className="font-mono font-medium">{fincomTraceResult.local_record.reference}</span></div>
                          <div><span className="text-muted-foreground">Amount:</span> <span className="font-medium">${Number(fincomTraceResult.local_record.amount || 0).toLocaleString()}</span></div>
                          <div><span className="text-muted-foreground">Status:</span> <span className="font-medium">{fincomTraceResult.local_record.status}</span></div>
                          <div><span className="text-muted-foreground">Transfer ID:</span> <span className="font-mono text-xs">{fincomTraceResult.local_record.transfer_id || 'N/A'}</span></div>
                          <div><span className="text-muted-foreground">Recipient:</span> <span className="font-medium">{fincomTraceResult.local_record.recipient_name}</span></div>
                        </div>
                      </div>
                    )}
                    {fincomTraceResult.fincom_trace && (
                      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                        <p className="text-xs uppercase tracking-wide text-sky-700 mb-2">FIN.COM Trace</p>
                        <pre className="bg-white border rounded p-2 text-xs overflow-auto max-h-64 font-mono">
                          {JSON.stringify(fincomTraceResult.fincom_trace, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Transactions Table */}
              <Card className="p-6 bg-white border-sky-100" data-testid="fincom-transactions">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-sky-200 rounded-full" />
                  <h3 className="text-xl font-semibold text-slate-800">Transactions</h3>
                  <Badge variant="outline" className="border-sky-200 text-sky-700">{fincomTransactions.length} records</Badge>
                </div>
                {fincomTransactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No FIN.COM transactions yet. Create a settlement above.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-sky-100 bg-sky-50/40">
                          <th className="text-left py-2 px-3 text-sky-900">Date</th>
                          <th className="text-left py-2 px-3 text-sky-900">Reference</th>
                          <th className="text-left py-2 px-3 text-sky-900">Recipient</th>
                          <th className="text-right py-2 px-3 text-sky-900">Amount</th>
                          <th className="text-left py-2 px-3 text-sky-900">Status</th>
                          <th className="text-right py-2 px-3 text-sky-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fincomTransactions.map((t) => (
                          <tr key={t.id} className="border-b border-sky-50 hover:bg-sky-50/40">
                            <td className="py-2 px-3 text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : 'N/A'}</td>
                            <td className="py-2 px-3 font-mono text-xs">{t.reference}</td>
                            <td className="py-2 px-3">{t.recipient_name}</td>
                            <td className="py-2 px-3 text-right font-semibold">${Number(t.amount || 0).toLocaleString()} {t.currency}</td>
                            <td className="py-2 px-3">
                              <Badge className={
                                ['success', 'completed', 'settled'].includes(t.status) ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                t.status === 'recorded' ? 'bg-sky-50 text-sky-700 border border-sky-200' :
                                t.status === 'failed' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                'bg-amber-50 text-amber-700 border border-amber-200'
                              }>{t.status}</Badge>
                            </td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex gap-1 justify-end flex-wrap">
                                <Button size="sm" variant="outline" className="gap-1 text-xs border-sky-200 hover:bg-sky-50"
                                  onClick={() => handleFincomStatusUpdate(t.id)} data-testid={`fincom-status-${t.id}`}>
                                  <RefreshCw className="w-3 h-3" /> Status
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs border-sky-200 hover:bg-sky-50"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/fincom/receipt-page/${t.id}`, '_blank')}
                                  data-testid={`fincom-receipt-${t.id}`}>
                                  <FileText className="w-3 h-3" /> Receipt
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs border-sky-200 hover:bg-sky-50"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/fincom/dtr/${t.id}`, '_blank')}
                                  data-testid={`fincom-dtr-${t.id}`}>
                                  <FileText className="w-3 h-3" /> DTR
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs text-sky-700 border-sky-300 hover:bg-sky-50"
                                  onClick={() => handleFincomTrace(t.id)} data-testid={`fincom-trace-${t.id}`}>
                                  <Eye className="w-3 h-3" /> Trace
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ═══════════════ TKM TAB (indigo theme) ═══════════════ */}
          <TabsContent value="tkm">
            <div className="space-y-6" data-testid="tkm-tab">
              <ExternalStpSettlement provider="TKM" />
              {/* Header */}
              <Card className="p-6 bg-indigo-50/40 border-indigo-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-indigo-500 rounded-full" />
                    <h2 className="text-2xl font-semibold text-indigo-900">TKM</h2>
                    <Badge variant="outline" className="text-indigo-700 border-indigo-300 bg-white">TEAM KM Direct API</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    {tkmHealth && (
                      <Badge className={
                        tkmHealth.status === 'connected' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                        tkmHealth.status === 'not_configured' ? 'bg-slate-50 text-slate-700 border border-slate-200' :
                        tkmHealth.status === 'ip_not_whitelisted' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                        'bg-rose-50 text-rose-700 border border-rose-200'
                      }>{tkmHealth.status}</Badge>
                    )}
                    <Button size="sm" variant="outline" onClick={loadTkmData} disabled={tkmLoading}
                      className="border-indigo-200 text-indigo-700 hover:bg-indigo-100" data-testid="tkm-refresh-btn">
                      {tkmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-indigo-800/80">
                  TKM (TEAM KM) Direct API Connectivity — Partner Treasury for SEPA / Wire payouts and inbound payment notifications.
                  Auth: HTTP Basic. Base URL: <span className="font-mono">{tkmHealth?.base_url || 'https://tkm-service.tech'}</span>.
                </p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <Button size="sm" variant="outline" className="gap-1 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="tkm-doc-pdf-btn"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/tkm-integration.pdf`, '_blank')}>
                    <FileText className="w-3 h-3" /> API Notes (PDF)
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1 text-xs border-slate-300 text-slate-700 hover:bg-slate-50"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/docs/tkm-integration.md`, '_blank')}>
                    <FileText className="w-3 h-3" /> API Notes (Markdown)
                  </Button>
                </div>
                {tkmHealth?.status === 'not_configured' && (
                  <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
                    Set <span className="font-mono">TKM_API_USERNAME</span> / <span className="font-mono">TKM_API_PASSWORD</span> / <span className="font-mono">TKM_DEFAULT_ACCOUNT_ID</span> in <span className="font-mono">/app/backend/.env</span> and restart the backend to enable live calls.
                  </div>
                )}
                {tkmHealth?.status === 'ip_not_whitelisted' && (
                  <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
                    TKM reachable but auth/IP rejected ({tkmHealth.http_code}). Ensure your outbound IP is whitelisted with TEAM KM and credentials are correct.
                  </div>
                )}
              </Card>

              {/* Outbound IP for TEAM KM whitelist */}
              <Card className="p-6 bg-white border-indigo-100" data-testid="tkm-outbound-ip">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-indigo-300 rounded-full" />
                    <h3 className="text-xl font-semibold text-slate-800">Outbound IP for Whitelist</h3>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {window.location.hostname.includes('ezytransfer.com') ? 'PRODUCTION' : 'PREVIEW'}
                  </Badge>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Send this IPv4 to TEAM KM (compliance / onboarding) for whitelisting. Production and preview have different IPs — whitelist both if you want to test on preview before going live.
                </p>
                {tkmOutboundIp?.primary_ip ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <code className="px-3 py-2 bg-slate-100 rounded text-lg font-mono font-semibold text-indigo-900" data-testid="tkm-outbound-ip-value">
                      {tkmOutboundIp.primary_ip}
                    </code>
                    <Button size="sm" variant="outline" data-testid="tkm-copy-ip-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(tkmOutboundIp.primary_ip);
                        toast.success('IP copied');
                      }}>Copy IP</Button>
                    <Button size="sm" variant="outline" data-testid="tkm-copy-email-btn"
                      onClick={() => {
                        const env = window.location.hostname.includes('ezytransfer.com') ? 'production' : 'preview';
                        const body = `Hi TEAM KM team,\n\nPlease whitelist the following IP for our EzyTransfer ${env} backend (Direct API Connectivity Package — tkm-service.tech):\n\nIPv4: ${tkmOutboundIp.primary_ip}\nEnvironment: ${env}\nIntended endpoints: /api/partner/treasury/* and /api/payments/inbound/*\nAuth: HTTP Basic\nWebhook receiver (please POST signed events here): https://ezytransfer.com/api/tkm/webhook\n\nOnce whitelisted, please also share:\n  1. API username\n  2. API password (separate secure channel)\n  3. Default settlement account ID\n  4. Webhook HMAC secret (x-Webhook-Signature)\n\nThanks,\nEzyTransfer`;
                        navigator.clipboard.writeText(body);
                        toast.success('Whitelist email body copied');
                      }}>Copy Whitelist Email</Button>
                    <Button size="sm" variant="ghost"
                      onClick={() => loadTkmData()}>Re-detect</Button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Detecting...</p>
                )}
              </Card>

              {/* Webhook & Production Connectivity Readiness */}
              <Card className="p-6 bg-white border-indigo-100" data-testid="tkm-webhook-config">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-indigo-300 rounded-full" />
                    <h3 className="text-xl font-semibold text-slate-800">Webhook & Production Readiness</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="tkm-download-partner-confirmation-btn"
                      onClick={async () => {
                        try {
                          const res = await api.get('/tkm/partner-confirmation.pdf', { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `EzyTransfer_Partner_Confirmation_TEAM_KM_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('Partner Confirmation PDF downloaded');
                        } catch (e) {
                          toast.error('Failed to download PDF');
                        }
                      }}
                    >
                      Partner Confirmation PDF
                    </Button>
                    {tkmWebhookConfig && (
                      <Badge
                        data-testid="tkm-webhook-ready-badge"
                        className={
                          tkmWebhookConfig.ready_for_live_webhooks
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-amber-50 text-amber-800 border border-amber-200'
                        }
                      >
                        {tkmWebhookConfig.ready_for_live_webhooks ? 'READY' : 'AWAITING HMAC SECRET'}
                      </Badge>
                    )}
                  </div>
                </div>
                {tkmWebhookConfig ? (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="border border-indigo-100 rounded p-3 bg-indigo-50/30">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Webhook Algorithm</p>
                      <p className="font-mono text-slate-800 mt-1">{tkmWebhookConfig.hmac_algorithm}</p>
                      <p className="text-xs text-slate-600 mt-1 font-mono">{tkmWebhookConfig.hmac_payload_format}</p>
                    </div>
                    <div className="border border-indigo-100 rounded p-3 bg-indigo-50/30">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">HMAC Secret</p>
                      <p className={`font-semibold mt-1 ${tkmWebhookConfig.hmac_secret_configured ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="tkm-hmac-secret-status">
                        {tkmWebhookConfig.hmac_secret_configured ? 'Configured' : 'Not configured'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Set <code>TKM_WEBHOOK_SECRET</code> in backend .env</p>
                    </div>
                    <div className="border border-indigo-100 rounded p-3 bg-indigo-50/30">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">TLS Cert Renewed Until</p>
                      <p className="font-mono text-slate-800 mt-1">{tkmWebhookConfig.tls_cert_renewed_until}</p>
                      <p className="text-xs text-slate-500 mt-1">verify = {String(tkmWebhookConfig.tls_verify)}</p>
                    </div>
                    <div className="border border-indigo-100 rounded p-3 bg-indigo-50/30">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Whitelisted Production IP</p>
                      <p className="font-mono text-slate-800 mt-1">{tkmWebhookConfig.production_ip_whitelisted}</p>
                      <p className="text-xs text-slate-500 mt-1">Doc ref: {tkmWebhookConfig.doc_reference}</p>
                      {tkmWebhookConfig.current_outbound_ip && (
                        <p
                          className={`text-xs mt-2 font-mono ${tkmWebhookConfig.outbound_ip_matches_whitelist ? 'text-emerald-700' : 'text-rose-700'}`}
                          data-testid="tkm-current-outbound-ip"
                        >
                          {tkmWebhookConfig.outbound_ip_matches_whitelist ? '✓' : '⚠'} Current egress: {tkmWebhookConfig.current_outbound_ip}
                          {!tkmWebhookConfig.outbound_ip_matches_whitelist && ' — send this to TEAM KM'}
                        </p>
                      )}
                    </div>
                    <div className="border border-indigo-100 rounded p-3 bg-indigo-50/30 col-span-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide">mTLS Client Cert Fingerprint (expected)</p>
                      <p className="font-mono text-[10px] text-slate-700 mt-1 break-all">{tkmWebhookConfig.mtls_cert_fingerprint_expected}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Local cert: {tkmWebhookConfig.mtls_cert_present
                          ? `present (${tkmWebhookConfig.mtls_cert_size_bytes} bytes)`
                          : 'not configured (set TKM_CLIENT_CERT_PATH + TKM_CLIENT_KEY_PATH if mTLS required)'}
                      </p>
                    </div>
                    {Array.isArray(tkmWebhookConfig.blocking_items) && tkmWebhookConfig.blocking_items.length > 0 && (
                      <div className="col-span-2 border border-amber-200 bg-amber-50 rounded p-3" data-testid="tkm-blocking-items">
                        <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-1">Blocking Items</p>
                        <ul className="list-disc pl-5 text-xs text-amber-900 space-y-1">
                          {tkmWebhookConfig.blocking_items.map((it, i) => (<li key={i}>{it}</li>))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Loading...</p>
                )}
              </Card>

              {/* Accounts */}
              {tkmAccounts.length > 0 && (
                <Card className="p-6 bg-white border-indigo-100" data-testid="tkm-accounts">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-indigo-300 rounded-full" />
                    <h3 className="text-xl font-semibold text-slate-800">Accounts</h3>
                    <Badge variant="outline" className="text-xs">{tkmAccounts.length}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {tkmAccounts.map((a, idx) => (
                      <div key={a.account_id || idx} className="border border-indigo-100 rounded-lg p-3 bg-indigo-50/30">
                        <p className="text-xs text-slate-600 font-mono">{a.account_id || 'N/A'}</p>
                        <p className="text-lg font-semibold text-indigo-900 mt-1">
                          {(a.currency || 'EUR')} {Number(a.balance || 0).toLocaleString()}
                        </p>
                        {a.status && <p className="text-xs text-slate-500 mt-0.5">{a.status}</p>}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Settlement Form — MINT-first: source MINT account is REQUIRED before anything */}
              <Card className="p-6 bg-white border-indigo-100" data-testid="tkm-settle-form">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-1 h-8 bg-indigo-400 rounded-full" />
                  <h3 className="text-xl font-semibold text-slate-800">New Settlement</h3>
                  <Badge className="bg-amber-50 text-amber-800 border border-amber-200">MINT source required</Badge>
                </div>
                <p className="text-xs text-slate-500 mb-4">All settlements must originate from a chosen MINT account — this is a hard guardrail.</p>

                {/* Step 1: MINT source account picker */}
                <div className="border border-sky-200 bg-sky-50/40 rounded-lg p-3 mb-4">
                  <p className="text-xs uppercase tracking-wide text-sky-800 font-semibold mb-2">Step 1 — Pick MINT source account</p>
                  <div className="grid grid-cols-3 gap-2" data-testid="tkm-mint-source-picker">
                    {mintAccounts.length === 0 && (
                      <button onClick={loadMintAccounts} className="col-span-3 text-xs underline text-sky-700">Load MINT accounts</button>
                    )}
                    {mintAccounts.map((a) => (
                      <button
                        key={a.id}
                        data-testid={`tkm-mint-source-${a.key}`}
                        onClick={() => setTkmSettleForm(p => ({ ...p, mint_account_id: a.id }))}
                        className={`text-left rounded-md p-2 border transition ${tkmSettleForm.mint_account_id === a.id ? 'border-sky-500 bg-white shadow-sm' : 'border-slate-200 bg-white hover:bg-sky-50'}`}
                      >
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">{a.key}</p>
                        <p className="text-xs font-semibold text-slate-800">{a.name}</p>
                        <p className="text-[10px] font-mono text-slate-500">{a.number}</p>
                        <p className="font-mono text-sm text-slate-900 mt-0.5">${Number(a.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </button>
                    ))}
                  </div>
                  {!tkmSettleForm.mint_account_id && <p className="text-[11px] text-rose-700 mt-2">⚠ Cannot proceed without a MINT account selected.</p>}
                </div>

                {/* Step 2: Mode + amount + details */}
                <p className="text-xs uppercase tracking-wide text-slate-700 font-semibold mb-2">Step 2 — Settlement details</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Mode</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="tkm-mode"
                      value={tkmSettleForm.mode}
                      onChange={(e) => setTkmSettleForm(p => ({ ...p, mode: e.target.value }))}>
                      <option value="debit_only">A. Debit MINT + SEPA wire (no TKM API)</option>
                      <option value="debit_and_notify">B. Debit MINT + notify TKM via API</option>
                      <option value="tkm_internal">C. TKM internal transfer (no MINT debit)</option>
                      <option value="payout">D. Legacy TKM payout</option>
                      <option value="credit-inbound">E. Legacy book credit (inbound)</option>
                      <option value="record">F. Record locally only</option>
                    </select>
                    {tkmSettleForm.mode === 'debit_only' && (
                      <p className="text-[11px] text-indigo-700 leading-snug">Debits MINT then logs the outgoing SEPA wire. TKM is not contacted — they will simply see the wire arrive on their banking side.</p>
                    )}
                    {tkmSettleForm.mode === 'debit_and_notify' && (
                      <p className="text-[11px] text-indigo-700 leading-snug">Debits MINT then calls TKM's <code>/api/payments/inbound/{tkmSettleForm.inbound_path || 'M2'}</code> to register the wire on their books.</p>
                    )}
                    {tkmSettleForm.mode === 'tkm_internal' && (
                      <p className="text-[11px] text-indigo-700 leading-snug">No MINT debit (audit-only linkage). Moves funds between two of your TKM treasury accounts via <code>/api/partner/treasury/accounts/transfer</code>.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input data-testid="tkm-amount" type="number" value={tkmSettleForm.amount}
                      onChange={(e) => setTkmSettleForm(p => ({ ...p, amount: e.target.value }))}
                      placeholder="100.00" />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={tkmSettleForm.currency}
                      onChange={(e) => setTkmSettleForm(p => ({ ...p, currency: e.target.value }))}>
                      {['USD','EUR','GBP','SEK','NOK','DKK','CHF','PLN','CZK'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  {tkmSettleForm.mode !== 'tkm_internal' && (
                    <>
                      <div className="space-y-2">
                        <Label>Beneficiary Name</Label>
                        <Input data-testid="tkm-recipient" value={tkmSettleForm.beneficiary_name}
                          onChange={(e) => setTkmSettleForm(p => ({ ...p, beneficiary_name: e.target.value }))}
                          placeholder="Example GmbH" />
                      </div>
                      <div className="space-y-2">
                        <Label>Beneficiary IBAN {tkmSettleForm.mode === 'payout' && <span className="text-rose-500">*</span>}</Label>
                        <Input data-testid="tkm-iban" value={tkmSettleForm.beneficiary_iban}
                          onChange={(e) => setTkmSettleForm(p => ({ ...p, beneficiary_iban: e.target.value.toUpperCase() }))}
                          placeholder="DE89... or EE38..." className="font-mono text-xs" />
                      </div>
                    </>
                  )}
                  {tkmSettleForm.mode === 'debit_and_notify' && (
                    <>
                      <div className="space-y-2">
                        <Label>Inbound Path</Label>
                        <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={tkmSettleForm.inbound_path}
                          onChange={(e) => setTkmSettleForm(p => ({ ...p, inbound_path: e.target.value }))}>
                          <option value="M2">M2 Cash</option>
                          <option value="TKM">TKM standard</option>
                          <option value="">Default (root)</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Source Channel</Label>
                        <Input value={tkmSettleForm.source_channel}
                          onChange={(e) => setTkmSettleForm(p => ({ ...p, source_channel: e.target.value }))}
                          placeholder="partner_api_m2_live" className="font-mono text-xs" />
                      </div>
                    </>
                  )}
                  {tkmSettleForm.mode === 'tkm_internal' && (
                    <>
                      <div className="space-y-2">
                        <Label>TKM From Account ID <span className="text-rose-500">*</span></Label>
                        <Input value={tkmSettleForm.tkm_from_account_id}
                          onChange={(e) => setTkmSettleForm(p => ({ ...p, tkm_from_account_id: e.target.value }))}
                          placeholder="TKM treasury source" className="font-mono text-xs" />
                      </div>
                      <div className="space-y-2">
                        <Label>TKM To Account ID <span className="text-rose-500">*</span></Label>
                        <Input value={tkmSettleForm.tkm_to_account_id}
                          onChange={(e) => setTkmSettleForm(p => ({ ...p, tkm_to_account_id: e.target.value }))}
                          placeholder="TKM treasury destination" className="font-mono text-xs" />
                      </div>
                    </>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label>Description / Purpose</Label>
                    <Input data-testid="tkm-desc" value={tkmSettleForm.description}
                      onChange={(e) => setTkmSettleForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Invoice INV-202..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Purpose Code</Label>
                    <Input value={tkmSettleForm.purpose}
                      onChange={(e) => setTkmSettleForm(p => ({ ...p, purpose: e.target.value }))}
                      placeholder="TKM partner settlement" />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button data-testid="tkm-settle-btn" onClick={handleTkmSettle}
                    disabled={tkmSettling || !tkmSettleForm.mint_account_id}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50">
                    {tkmSettling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <><DollarSign className="w-4 h-4 mr-2" /> Debit MINT &amp; Settle</>}
                  </Button>
                </div>
              </Card>

              {/* Trace Result */}
              {tkmTraceResult && (
                <Card className="p-6 bg-white border-indigo-100" data-testid="tkm-trace-result">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-amber-400 rounded-full" />
                    <h3 className="text-xl font-semibold text-slate-800">Trace Result</h3>
                    <Button size="sm" variant="ghost" onClick={() => setTkmTraceResult(null)}>Clear</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 border rounded-lg p-4">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Local Record</p>
                      <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-64 font-mono">
                        {JSON.stringify(tkmTraceResult.local_record, null, 2)}
                      </pre>
                    </div>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <p className="text-xs uppercase tracking-wide text-indigo-700 mb-2">
                        Webhook Events ({tkmTraceResult.webhook_events?.length || 0})
                      </p>
                      <pre className="text-xs bg-white border rounded p-2 overflow-auto max-h-64 font-mono">
                        {JSON.stringify(tkmTraceResult.webhook_events || [], null, 2)}
                      </pre>
                    </div>
                  </div>
                </Card>
              )}

              {/* Transactions */}
              <Card className="p-6 bg-white border-indigo-100" data-testid="tkm-transactions">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-indigo-200 rounded-full" />
                  <h3 className="text-xl font-semibold text-slate-800">Transactions</h3>
                  <Badge variant="outline" className="border-indigo-200 text-indigo-700">{tkmTransactions.length} records</Badge>
                </div>
                {tkmTransactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No TKM transactions yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-indigo-100 bg-indigo-50/40">
                          <th className="text-left py-2 px-3 text-indigo-900">Date</th>
                          <th className="text-left py-2 px-3 text-indigo-900">Reference</th>
                          <th className="text-left py-2 px-3 text-indigo-900">Mode</th>
                          <th className="text-left py-2 px-3 text-indigo-900">Recipient</th>
                          <th className="text-right py-2 px-3 text-indigo-900">Amount</th>
                          <th className="text-left py-2 px-3 text-indigo-900">Status</th>
                          <th className="text-right py-2 px-3 text-indigo-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tkmTransactions.map((t) => (
                          <tr key={t.id} className="border-b border-indigo-50 hover:bg-indigo-50/40">
                            <td className="py-2 px-3 text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : 'N/A'}</td>
                            <td className="py-2 px-3 font-mono text-xs">
                              {t.reference}
                              {t.wire_id && <div className="text-indigo-700/70">{t.wire_id}</div>}
                            </td>
                            <td className="py-2 px-3 text-xs">{t.mode}</td>
                            <td className="py-2 px-3">{t.recipient_name}</td>
                            <td className="py-2 px-3 text-right font-semibold">{t.currency} {Number(t.amount || 0).toLocaleString()}</td>
                            <td className="py-2 px-3">
                              <Badge className={
                                ['queued', 'credited', 'settled', 'ok'].includes(t.status) ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                t.status === 'recorded' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' :
                                ['error', 'failed'].includes(t.status) ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                'bg-amber-50 text-amber-700 border border-amber-200'
                              }>{t.status}</Badge>
                            </td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex gap-1 justify-end flex-wrap">
                                <Button size="sm" variant="outline" className="gap-1 text-xs border-indigo-200 hover:bg-indigo-50"
                                  onClick={() => handleTkmStatusUpdate(t.id)} data-testid={`tkm-status-${t.id}`}>
                                  <RefreshCw className="w-3 h-3" /> Status
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs border-indigo-200 hover:bg-indigo-50"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tkm/receipt-page/${t.id}`, '_blank')}>
                                  <FileText className="w-3 h-3" /> Receipt
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs border-indigo-200 hover:bg-indigo-50"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tkm/dtr/${t.id}`, '_blank')}>
                                  <FileText className="w-3 h-3" /> DTR
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                                  onClick={() => handleTkmTrace(t.id)} data-testid={`tkm-trace-${t.id}`}>
                                  <Eye className="w-3 h-3" /> Trace
                                </Button>
                                {String(t.mode || '').startsWith('inbound') && (
                                  <Button size="sm" variant="outline" className="gap-1 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                                    onClick={() => handleTkmReplay(t)} data-testid={`tkm-replay-${t.id}`}>
                                    <RefreshCw className="w-3 h-3" /> Replay
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ═══════════════ SWIFT TAB ═══════════════ */}
          <TabsContent value="swift">
            <div className="space-y-6" data-testid="swift-tab">
              {/* Header */}
              <Card className="p-6 bg-amber-50/40 border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-amber-500 rounded-full" />
                    <h2 className="text-2xl font-semibold text-amber-900">SWIFT</h2>
                    <Badge variant="outline" className="text-amber-800 border-amber-300 bg-white">MT103 generator</Badge>
                  </div>
                  <Button size="sm" variant="outline" onClick={loadSwiftData} disabled={swiftLoading}
                    className="border-amber-200 text-amber-800 hover:bg-amber-100" data-testid="swift-refresh">
                    {swiftLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-sm text-amber-900/70">
                  Outbound SWIFT settlements with UETR, MT103 text + PDF generation, saved beneficiaries, default house BIC,
                  and one-click "Send to SWIFT" from any captured TAP charge.
                </p>
              </Card>

              {/* Settings */}
              <Card className="p-6 bg-white border-amber-100" data-testid="swift-settings">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-amber-300 rounded-full" />
                  <h3 className="text-xl font-semibold text-slate-800">Defaults &amp; Provider</h3>
                </div>

                {/* Provider selector */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label>Wire Dispatcher</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="swift-provider"
                      value={swiftSettings.provider}
                      onChange={(e) => setSwiftSettings(p => ({ ...p, provider: e.target.value }))}>
                      <option value="manual">Manual (MT103 only — mark sent yourself)</option>
                      <option value="fincom">Fin.com SWIFT corridor (live wires via /v2/beneficiaries + /v1/transactions/transfer-payout)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Fin.com Customer ID {swiftSettings.provider === 'fincom' && <span className="text-rose-500">*</span>}</Label>
                    <Input
                      data-testid="swift-fincom-customer-id"
                      value={swiftSettings.fincom_customer_id}
                      onChange={(e) => setSwiftSettings(p => ({ ...p, fincom_customer_id: e.target.value }))}
                      placeholder="cust_..."
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-slate-500">First-party APPROVED customer on Fin.com — beneficiaries are created under it.</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ordering Customer Name</Label>
                    <Input value={swiftSettings.ordering_customer_name}
                      onChange={(e) => setSwiftSettings(p => ({ ...p, ordering_customer_name: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Ordering Account</Label>
                    <Input value={swiftSettings.ordering_customer_account}
                      onChange={(e) => setSwiftSettings(p => ({ ...p, ordering_customer_account: e.target.value }))} />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Ordering Address</Label>
                    <Input value={swiftSettings.ordering_customer_address}
                      onChange={(e) => setSwiftSettings(p => ({ ...p, ordering_customer_address: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Sender BIC</Label>
                    <Input value={swiftSettings.sender_bic}
                      onChange={(e) => setSwiftSettings(p => ({ ...p, sender_bic: e.target.value.toUpperCase() }))}
                      placeholder="EZYTRSAR (8 or 11 chars)" />
                  </div>
                  <div className="space-y-2">
                    <Label>Charges Bearer</Label>
                    <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={swiftSettings.charges_bearer}
                      onChange={(e) => setSwiftSettings(p => ({ ...p, charges_bearer: e.target.value }))}>
                      {['OUR', 'SHA', 'BEN'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
                    <input type="checkbox" data-testid="swift-auto-wire-toggle"
                      checked={!!swiftSettings.auto_wire_tap_captures}
                      onChange={(e) => setSwiftSettings(p => ({ ...p, auto_wire_tap_captures: e.target.checked }))}
                      className="accent-amber-500" />
                    Auto-wire every captured TAP charge to default beneficiary
                  </label>
                  <Button size="sm" data-testid="swift-save-settings"
                    onClick={handleSwiftSaveSettings}
                    className="bg-amber-500 hover:bg-amber-600 text-white">
                    Save Settings
                  </Button>
                </div>
              </Card>

              {/* Beneficiaries */}
              <Card className="p-6 bg-white border-amber-100" data-testid="swift-beneficiaries">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-amber-400 rounded-full" />
                  <h3 className="text-xl font-semibold text-slate-800">Saved Beneficiaries</h3>
                  <Badge variant="outline" className="text-xs">{swiftBeneficiaries.length}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <Input placeholder="Label" value={swiftBenForm.label}
                    onChange={(e) => setSwiftBenForm(p => ({ ...p, label: e.target.value }))} />
                  <Input placeholder="Beneficiary Name" value={swiftBenForm.beneficiary_name}
                    onChange={(e) => setSwiftBenForm(p => ({ ...p, beneficiary_name: e.target.value }))} />
                  <Input placeholder="BIC (8 or 11 chars)" value={swiftBenForm.beneficiary_bic}
                    onChange={(e) => setSwiftBenForm(p => ({ ...p, beneficiary_bic: e.target.value.toUpperCase() }))} />
                  <Input placeholder="IBAN" value={swiftBenForm.beneficiary_iban}
                    onChange={(e) => setSwiftBenForm(p => ({ ...p, beneficiary_iban: e.target.value.toUpperCase() }))} />
                  <Input placeholder="Account (if no IBAN)" value={swiftBenForm.beneficiary_account}
                    onChange={(e) => setSwiftBenForm(p => ({ ...p, beneficiary_account: e.target.value }))} />
                  <Input placeholder="Intermediary BIC (optional)" value={swiftBenForm.intermediary_bic}
                    onChange={(e) => setSwiftBenForm(p => ({ ...p, intermediary_bic: e.target.value.toUpperCase() }))} />
                  <Input placeholder="Address" className="col-span-2" value={swiftBenForm.beneficiary_address}
                    onChange={(e) => setSwiftBenForm(p => ({ ...p, beneficiary_address: e.target.value }))} />
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={swiftBenForm.currency}
                    onChange={(e) => setSwiftBenForm(p => ({ ...p, currency: e.target.value }))}>
                    {['USD','SAR','AED','KWD','BHD','QAR','OMR','EGP','JOD','EUR','GBP','INR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-slate-700 select-none">
                    <input type="checkbox" checked={swiftBenForm.is_default}
                      onChange={(e) => setSwiftBenForm(p => ({ ...p, is_default: e.target.checked }))}
                      className="accent-amber-500" />
                    Set as default
                  </label>
                  <Button size="sm" data-testid="swift-add-beneficiary" onClick={handleSwiftAddBeneficiary}
                    className="bg-amber-500 hover:bg-amber-600 text-white">
                    Add Beneficiary
                  </Button>
                </div>

                {swiftBeneficiaries.length > 0 && (
                  <div className="mt-5 space-y-2">
                    {swiftBeneficiaries.map((b) => (
                      <div key={b.id} className="border border-amber-100 rounded-lg p-3 flex items-center justify-between bg-amber-50/30">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-800">{b.label}</span>
                            {b.is_default && <Badge className="bg-amber-100 text-amber-800 border-amber-300">Default</Badge>}
                          </div>
                          <p className="text-xs text-slate-600 font-mono mt-1">
                            {b.beneficiary_name} · {b.beneficiary_bic} · {b.beneficiary_iban || b.beneficiary_account}
                          </p>
                          {b.fincom_beneficiary_id && (
                            <p className="text-xs text-emerald-700 font-mono mt-0.5">
                              Fin.com: {b.fincom_beneficiary_id}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {!b.fincom_beneficiary_id && swiftSettings.provider === 'fincom' && (
                            <Button size="sm" variant="outline" className="text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
                              onClick={() => handleSwiftSyncFincom(b.id)}
                              data-testid={`swift-sync-fincom-${b.id}`}>
                              Sync to Fin.com
                            </Button>
                          )}
                          {!b.is_default && (
                            <Button size="sm" variant="outline" className="text-xs"
                              onClick={() => handleSwiftSetDefault(b.id)}
                              data-testid={`swift-set-default-${b.id}`}>
                              Set Default
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-xs text-rose-600 hover:bg-rose-50"
                            onClick={() => handleSwiftDeleteBeneficiary(b.id)}
                            data-testid={`swift-del-${b.id}`}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* New Transfer */}
              <Card className="p-6 bg-white border-amber-100" data-testid="swift-new-transfer">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-amber-400 rounded-full" />
                  <h3 className="text-xl font-semibold text-slate-800">New SWIFT Transfer</h3>
                  <Badge className="bg-amber-50 text-amber-800 border border-amber-200">MINT source required</Badge>
                </div>

                <MintSourcePicker
                  accounts={mintAccounts}
                  selectedId={swiftTransferForm.mint_account_id}
                  onSelect={(id) => setSwiftTransferForm(p => ({ ...p, mint_account_id: id }))}
                  providerLabel="SWIFT"
                  onRefresh={loadMintAccounts}
                  amount={swiftTransferForm.amount}
                  testIdPrefix="swift-mint-source"
                />

                <div className="grid grid-cols-3 gap-3">
                  <Input placeholder="Amount" type="number" value={swiftTransferForm.amount}
                    data-testid="swift-tx-amount"
                    onChange={(e) => setSwiftTransferForm(p => ({ ...p, amount: e.target.value }))} />
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={swiftTransferForm.currency}
                    onChange={(e) => setSwiftTransferForm(p => ({ ...p, currency: e.target.value }))}>
                    {['USD','SAR','AED','KWD','BHD','QAR','OMR','EGP','JOD','EUR','GBP','INR'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={swiftTransferForm.beneficiary_id || (swiftTransferForm.use_default ? '__default__' : '__adhoc__')}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__default__') setSwiftTransferForm(p => ({ ...p, beneficiary_id: '', use_default: true }));
                      else if (v === '__adhoc__') setSwiftTransferForm(p => ({ ...p, beneficiary_id: '', use_default: false }));
                      else setSwiftTransferForm(p => ({ ...p, beneficiary_id: v, use_default: false }));
                    }}>
                    <option value="__default__">Default beneficiary</option>
                    {swiftBeneficiaries.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
                    <option value="__adhoc__">Ad-hoc (fill below)</option>
                  </select>
                  <Input className="col-span-3" placeholder="Remittance information"
                    value={swiftTransferForm.remittance_info} data-testid="swift-tx-remittance"
                    onChange={(e) => setSwiftTransferForm(p => ({ ...p, remittance_info: e.target.value }))} />
                </div>
                {/* Ad-hoc fields shown only when ad-hoc is selected */}
                {(!swiftTransferForm.use_default && !swiftTransferForm.beneficiary_id) && (
                  <div className="grid grid-cols-3 gap-3 mt-3 p-3 bg-amber-50/40 rounded-lg">
                    <Input placeholder="Beneficiary Name" value={swiftTransferForm.beneficiary_name}
                      onChange={(e) => setSwiftTransferForm(p => ({ ...p, beneficiary_name: e.target.value }))} />
                    <Input placeholder="BIC" value={swiftTransferForm.beneficiary_bic}
                      onChange={(e) => setSwiftTransferForm(p => ({ ...p, beneficiary_bic: e.target.value.toUpperCase() }))} />
                    <Input placeholder="IBAN or account" value={swiftTransferForm.beneficiary_iban}
                      onChange={(e) => setSwiftTransferForm(p => ({ ...p, beneficiary_iban: e.target.value.toUpperCase() }))} />
                    <Input placeholder="Address" className="col-span-2" value={swiftTransferForm.beneficiary_address}
                      onChange={(e) => setSwiftTransferForm(p => ({ ...p, beneficiary_address: e.target.value }))} />
                    <Input placeholder="Intermediary BIC (optional)" value={swiftTransferForm.intermediary_bic}
                      onChange={(e) => setSwiftTransferForm(p => ({ ...p, intermediary_bic: e.target.value.toUpperCase() }))} />
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <Button data-testid="swift-create-tx-btn" onClick={() => handleSwiftCreateTransfer('manual', '')}
                    className="bg-amber-500 hover:bg-amber-600 text-white">
                    <ArrowRight className="w-4 h-4 mr-2" /> Create SWIFT Transfer
                  </Button>
                </div>
              </Card>

              {/* Transfers Table */}
              <Card className="p-6 bg-white border-amber-100" data-testid="swift-transfers-list">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 bg-amber-200 rounded-full" />
                  <h3 className="text-xl font-semibold text-slate-800">Transfers</h3>
                  <Badge variant="outline" className="text-xs">{swiftTransfers.length} records</Badge>
                </div>
                {swiftTransfers.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No SWIFT transfers yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-amber-100 bg-amber-50/40">
                          <th className="text-left py-2 px-3 text-amber-900">Created</th>
                          <th className="text-left py-2 px-3 text-amber-900">Reference / UETR</th>
                          <th className="text-left py-2 px-3 text-amber-900">Beneficiary</th>
                          <th className="text-right py-2 px-3 text-amber-900">Amount</th>
                          <th className="text-left py-2 px-3 text-amber-900">Status</th>
                          <th className="text-left py-2 px-3 text-amber-900">Source</th>
                          <th className="text-right py-2 px-3 text-amber-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {swiftTransfers.map((t) => (
                          <tr key={t.id} className="border-b border-amber-50 hover:bg-amber-50/40">
                            <td className="py-2 px-3 text-xs">{t.created_at?.slice(0, 19)}</td>
                            <td className="py-2 px-3 text-xs font-mono">
                              {t.reference}
                              <div className="text-amber-700/70">{t.uetr}</div>
                            </td>
                            <td className="py-2 px-3 text-xs">
                              {t.beneficiary?.beneficiary_name}
                              <div className="text-slate-500">{t.beneficiary?.beneficiary_bic}</div>
                            </td>
                            <td className="py-2 px-3 text-right font-semibold">{t.currency} {Number(t.amount).toLocaleString()}</td>
                            <td className="py-2 px-3">
                              <Badge className={
                                t.status === 'sent' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                t.status === 'failed' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                'bg-amber-50 text-amber-700 border border-amber-200'
                              }>{t.status}</Badge>
                            </td>
                            <td className="py-2 px-3 text-xs">
                              {t.source_layer !== 'manual' ? (
                                <Badge variant="outline" className="text-xs">{t.source_layer.toUpperCase()}</Badge>
                              ) : <span className="text-slate-400">manual</span>}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <div className="flex gap-1 justify-end flex-wrap">
                                <Button size="sm" variant="outline" className="gap-1 text-xs border-amber-200 hover:bg-amber-50"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/swift/transfers/${t.id}/mt103.pdf`, '_blank')}
                                  data-testid={`swift-pdf-${t.id}`}>
                                  <FileText className="w-3 h-3" /> MT103 PDF
                                </Button>
                                <Button size="sm" variant="outline" className="gap-1 text-xs border-slate-200 hover:bg-slate-50"
                                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/swift/transfers/${t.id}/mt103.txt`, '_blank')}>
                                  TXT
                                </Button>
                                {t.status === 'pending' && (
                                  <Button size="sm" variant="outline" className="gap-1 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                    onClick={() => handleSwiftMarkSent(t.id)}
                                    data-testid={`swift-mark-sent-${t.id}`}>
                                    Mark Sent
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* ═══════════════ DB ADMIN TAB (super_admin only) ═══════════════ */}
          <TabsContent value="db-admin">
            <div className="space-y-6" data-testid="db-admin-tab">
              {dbForbidden ? (
                <Card className="p-10 text-center bg-rose-50/40 border-rose-200">
                  <Shield className="w-12 h-12 mx-auto text-rose-400 mb-3" />
                  <h2 className="text-xl font-semibold text-rose-900">Super Admin only</h2>
                  <p className="text-sm text-rose-700/80 mt-2">Your team role does not allow access to DB Admin.</p>
                </Card>
              ) : (
                <>
                  {/* Header + actions */}
                  <Card className="p-6 bg-rose-50/40 border-rose-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-1 h-8 bg-rose-500 rounded-full" />
                        <h2 className="text-2xl font-semibold text-rose-900">DB Admin</h2>
                        <Badge variant="outline" className="text-rose-700 border-rose-300 bg-white">Super Admin</Badge>
                      </div>
                      <Button size="sm" variant="outline" onClick={loadDbAdmin} disabled={dbLoading}
                        className="border-rose-200 text-rose-700 hover:bg-rose-100" data-testid="db-admin-refresh">
                        {dbLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-sm text-rose-800/70">
                      Browse and edit any whitelisted MongoDB collection, run a one-click production seed (ALT1 $49M + SOVEIN $25M + Bridge reversed), or download a full DB backup.
                    </p>
                    <div className="flex gap-2 mt-4 flex-wrap">
                      <Button size="sm" onClick={handleDbSeed}
                        className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="db-seed-prod-btn">
                        <Server className="w-4 h-4 mr-2" /> Seed Production DB
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleDbBackup}
                        className="border-rose-200" data-testid="db-backup-btn">
                        <FileText className="w-4 h-4 mr-2" /> Download Backup (JSON)
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleDbAuditLog}
                        className="border-slate-200" data-testid="db-audit-log-btn">
                        <Eye className="w-4 h-4 mr-2" /> Load Audit Log
                      </Button>
                    </div>
                  </Card>

                  {/* Collections + browser */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Sidebar: collection list */}
                    <Card className="p-4 bg-white">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="font-semibold text-slate-800">Collections</h3>
                        <Badge variant="outline" className="text-xs">{dbCollections.length}</Badge>
                      </div>
                      <div className="space-y-1 max-h-[480px] overflow-auto pr-1">
                        {dbCollections.map((c) => (
                          <button
                            key={c.name}
                            onClick={() => loadDbCollection(c.name)}
                            data-testid={`db-col-${c.name}`}
                            className={`w-full text-left text-sm px-3 py-2 rounded transition ${
                              dbActiveCol === c.name
                                ? 'bg-rose-100 text-rose-900 font-medium'
                                : 'hover:bg-slate-50 text-slate-700'
                            }`}
                          >
                            <span className="font-mono text-xs">{c.name}</span>
                            <span className="float-right text-xs text-slate-500">{c.count}</span>
                          </button>
                        ))}
                      </div>
                    </Card>

                    {/* Main: documents browser */}
                    <Card className="p-4 bg-white md:col-span-2">
                      {!dbActiveCol ? (
                        <div className="py-12 text-center text-slate-500 text-sm">
                          ← Select a collection to browse its documents
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-3 gap-3">
                            <div>
                              <h3 className="font-semibold text-slate-800 font-mono">{dbActiveCol}</h3>
                              <p className="text-xs text-slate-500">{dbDocs.total || 0} matching · {dbDocs.docs?.length || 0} shown</p>
                            </div>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Search id / reference / email…"
                                value={dbSearch}
                                onChange={(e) => setDbSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') loadDbCollection(dbActiveCol, dbSearch); }}
                                className="text-sm h-8 w-64"
                                data-testid="db-search-input"
                              />
                              <Button size="sm" variant="outline" onClick={() => loadDbCollection(dbActiveCol, dbSearch)} data-testid="db-search-btn">
                                Search
                              </Button>
                            </div>
                          </div>
                          <div className="max-h-[480px] overflow-auto space-y-2">
                            {(dbDocs.docs || []).map((d, idx) => (
                              <details key={d.id || idx} className="border rounded-lg p-3 bg-slate-50/50">
                                <summary className="cursor-pointer flex items-center justify-between text-sm">
                                  <span className="font-mono text-xs text-slate-700">
                                    {d.id || JSON.stringify(d).slice(0, 60) + '…'}
                                  </span>
                                  {d.id && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 text-xs text-rose-600 hover:bg-rose-50"
                                      onClick={(e) => { e.preventDefault(); handleDbDelete(dbActiveCol, d.id); }}
                                      data-testid={`db-del-${d.id}`}
                                    >
                                      Delete
                                    </Button>
                                  )}
                                </summary>
                                <pre className="text-xs mt-2 bg-white p-2 rounded overflow-auto max-h-64 font-mono">
                                  {JSON.stringify(d, null, 2)}
                                </pre>
                              </details>
                            ))}
                          </div>
                        </>
                      )}
                    </Card>
                  </div>

                  {/* Roles catalogue */}
                  <Card className="p-6 bg-white">
                    <h3 className="font-semibold text-slate-800 mb-3">Available Team Roles</h3>
                    <p className="text-xs text-slate-500 mb-3">Only Super Admin can grant these roles to team members. Manage assignments from the Team tab.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {dbRoles.map((r) => (
                        <div key={r.value} className="border rounded-lg p-3 bg-slate-50/50">
                          <p className="font-mono text-sm font-semibold text-slate-800">{r.value}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {(r.default_permissions || []).join(', ')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Audit log */}
                  {dbAuditLog.length > 0 && (
                    <Card className="p-6 bg-white">
                      <h3 className="font-semibold text-slate-800 mb-3">Audit Log (last {dbAuditLog.length})</h3>
                      <div className="max-h-96 overflow-auto space-y-1 text-xs font-mono">
                        {dbAuditLog.map((e) => (
                          <div key={e.id} className="border-b py-1">
                            <span className="text-slate-500">{e.at?.slice(0, 19)}</span>
                            {' '}
                            <span className="text-rose-700">{e.actor}</span>
                            {' '}
                            <span className="text-slate-700">{e.action}</span>
                            {e.collection && <span className="text-sky-700"> · {e.collection}</span>}
                            {e.doc_id && <span className="text-slate-500"> · {e.doc_id}</span>}
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="cyclos">
            <CyclosLayer />
          </TabsContent>

          <TabsContent value="itau">
            <ItauLayer />
          </TabsContent>

          <TabsContent value="paystack">
            <PaystackLayer />
          </TabsContent>

          <TabsContent value="relm">
            <RelmLayer />
          </TabsContent>

          <TabsContent value="equity">
            <EquityLayer />
          </TabsContent>

          <TabsContent value="doronpay">
            <DoronPayLayer />
          </TabsContent>

          <TabsContent value="layer-transfer">
            <LayerTransfer />
          </TabsContent>

        </Tabs>

        {/* Receipt Delivery Dialog */}
        <Dialog open={deliveryOpen} onOpenChange={setDeliveryOpen}>
          <DialogContent className="sm:max-w-md" data-testid="receipt-delivery-dialog">
            <DialogHeader>
              <DialogTitle>Deliver Receipt</DialogTitle>
            </DialogHeader>
            {deliveryTx && (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <p className="font-medium">{deliveryTx.provider?.toUpperCase()} Transaction</p>
                  <p className="text-muted-foreground">Amount: {deliveryTx.provider === 'mpesa' ? 'ETB' : 'USD'} {deliveryTx.amount} | ID: {deliveryTx.id?.slice(0, 8)}</p>
                </div>

                <div className="space-y-2">
                  <Label>Delivery Channel</Label>
                  <Select value={deliveryChannel} onValueChange={(v) => { setDeliveryChannel(v); setDeliveryTo(''); }}>
                    <SelectTrigger data-testid="delivery-channel-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email (SendGrid)</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp (Twilio)</SelectItem>
                      <SelectItem value="sms">SMS (Twilio)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{deliveryChannel === 'email' ? 'Email Address' : 'Phone Number (E.164)'}</Label>
                  <Input
                    data-testid="delivery-to-input"
                    placeholder={deliveryChannel === 'email' ? 'customer@example.com' : '+251911234567'}
                    value={deliveryTo}
                    onChange={(e) => setDeliveryTo(e.target.value)}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    data-testid="delivery-send-btn"
                    onClick={handleDeliverReceipt}
                    disabled={deliverySending || !deliveryTo}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {deliverySending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    {deliverySending ? 'Sending...' : `Send via ${deliveryChannel}`}
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="delivery-wa-share-btn"
                    onClick={() => openWhatsAppShare(deliveryTx.provider, deliveryTx.id)}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Share Link
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// Add Team Member Modal Component
function AddTeamMemberModal({ onAdd, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    permissions: [],
    department: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.role) {
      toast.error('Please enter a name, email and role');
      return;
    }
    onAdd(formData);
  };

  const togglePermission = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Add Team Member</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Full Name *</Label>
            <Input
              data-testid="team-member-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Amina Hassan"
            />
          </div>

          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              data-testid="team-member-email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g., amina@ezytransfer.com"
            />
            <p className="text-xs text-muted-foreground">If no account exists for this email, one is created and you'll get a one-time temporary password to share.</p>
          </div>

          <div className="space-y-2">
            <Label>Role *</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label} - {role.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            <div className="flex flex-wrap gap-2">
              {PERMISSIONS.map((perm) => (
                <div key={perm.value} className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.permissions.includes(perm.value)}
                    onCheckedChange={() => togglePermission(perm.value)}
                  />
                  <span className="text-sm">{perm.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Department</Label>
            <Input
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              placeholder="e.g., Compliance, Finance"
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="team-member-submit-btn">Add Team Member</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// Edit Team Member Modal Component
function EditTeamMemberModal({ member, onUpdate, onClose }) {
  const [formData, setFormData] = useState({
    role: member.role || '',
    permissions: member.permissions || [],
    department: member.department || '',
    is_active: member.is_active !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  const togglePermission = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold">Edit Team Member</h2>
            <p className="text-sm text-muted-foreground">{member.name} ({member.email})</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            <div className="flex flex-wrap gap-2">
              {PERMISSIONS.map((perm) => (
                <div key={perm.value} className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.permissions.includes(perm.value)}
                    onCheckedChange={() => togglePermission(perm.value)}
                  />
                  <span className="text-sm">{perm.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Department</Label>
            <Input
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <span className="text-sm">Active</span>
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">Update</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

